import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Users, Search, Filter, CheckCircle2, Clock, 
  AlertTriangle, ArrowLeft, Eye, EyeOff, Edit, Trash2, RefreshCw,
  FileText, Download, Camera, Phone, MapPin, User,
  Activity, TrendingUp, BarChart3, Save, X, Plus,
  Stethoscope, Fingerprint, Plane, Package, ClipboardCheck,
  Zap, Star, Upload, Award, Target, FileDown, CheckSquare, Printer,
  Barcode, Image as ImageIcon
} from 'lucide-react';

import config from '../config';
import usePermissions from './usePermissions';


const loadHtml2Canvas = async () => {
    if (window.html2canvas) return window.html2canvas;
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = () => resolve(window.html2canvas);
        script.onerror = reject;
        document.head.appendChild(script);
    });
};


const PeriodicFollowUpActivityLogModal = ({
    isOpen,
    onClose,
    logs,
    users,
    filters,
    setFilters,
    onRefresh,
    isLoading,
    cleanupSettings,
    onSaveCleanupSettings,
    onManualCleanup
}) => {
    const [selectedCleanupMode, setSelectedCleanupMode] = useState(cleanupSettings?.cleanupMode || 'off');
    const [manualDeleteMode, setManualDeleteMode] = useState('older_than_month');
    const [isActivityLogMobile, setIsActivityLogMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth <= 768;
    });

    useEffect(() => {
        setSelectedCleanupMode(cleanupSettings?.cleanupMode || 'off');
    }, [cleanupSettings?.cleanupMode]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mediaQuery = window.matchMedia('(max-width: 768px)');
        const handleChange = (event) => setIsActivityLogMobile(event.matches);
        setIsActivityLogMobile(mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }

        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
    }, []);

    if (!isOpen) return null;

    const actionTypes = [
        { value: '', label: 'كل الحركات' },
        { value: 'add_person', label: 'إضافة شخص' },
        { value: 'update_field', label: 'تحديث حقل' },
        { value: 'full_update', label: 'تحديث كامل' },
        { value: 'clear_medical', label: 'مسح الكشف الطبي' },
        { value: 'clear_documents', label: 'مسح تسليم الأوراق' },
        { value: 'remove_person', label: 'حذف شخص' },
        { value: 'list_update', label: 'تعديل قائمة' },
        { value: 'list_hide', label: 'إخفاء قائمة' },
        { value: 'list_show', label: 'إظهار قائمة' },
        { value: 'cleanup_settings', label: 'إعدادات التنظيف' },
        { value: 'cleanup_manual', label: 'حذف يدوي للسجل' }
    ];

    const formatDateTime = (value) => {
        if (!value) return '—';
        try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            return date.toLocaleString('ar-EG', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: true
            });
        } catch {
            return value;
        }
    };

    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        const text = String(value);
        if (/^\d{4}-\d{2}-\d{2}T/.test(text) || /^\d{4}-\d{2}-\d{2}$/.test(text)) {
            try {
                const date = new Date(text);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleString('ar-EG', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: text.includes('T') ? '2-digit' : undefined,
                        minute: text.includes('T') ? '2-digit' : undefined
                    });
                }
            } catch {}
        }
        return text;
    };


    const cleanupModeLabel = (mode) => {
        switch (mode) {
            case 'weekly': return 'أسبوعي - يحتفظ بآخر 7 أيام';
            case 'monthly': return 'شهري - يحتفظ بآخر 30 يوم';
            default: return 'متوقف';
        }
    };

    const manualDeleteModeLabel = (mode) => {
        switch (mode) {
            case 'all': return 'حذف السجل بالكامل';
            case 'older_than_week': return 'حذف الأقدم من أسبوع';
            case 'older_than_month': return 'حذف الأقدم من شهر';
            case 'older_than_3_months': return 'حذف الأقدم من 3 شهور';
            case 'older_than_year': return 'حذف الأقدم من سنة';
            default: return 'حذف الأقدم من شهر';
        }
    };

    const renderDetails = (log) => {
        const details = log.Details;
        const changes = Array.isArray(details?.changes) ? details.changes : [];

        if (changes.length > 0) {
            return (
                <div className="space-y-2">
                    {changes.slice(0, 4).map((change, index) => (
                        <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs">
                            <div className="font-semibold text-gray-700 mb-1">{change.label || change.field}</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div>
                                    <span className="text-gray-400">قبل: </span>
                                    <span className="text-red-700 break-words">{formatValue(change.oldValue)}</span>
                                </div>
                                <div>
                                    <span className="text-gray-400">بعد: </span>
                                    <span className="text-green-700 break-words">{formatValue(change.newValue)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    {changes.length > 4 && (
                        <div className="text-xs text-gray-400">+ {changes.length - 4} تغييرات أخرى</div>
                    )}
                </div>
            );
        }

        if (log.FieldName) {
            return (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs">
                    <div className="font-semibold text-gray-700 mb-1">{log.FieldName}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div><span className="text-gray-400">قبل: </span><span className="text-red-700">{formatValue(log.OldValue)}</span></div>
                        <div><span className="text-gray-400">بعد: </span><span className="text-green-700">{formatValue(log.NewValue)}</span></div>
                    </div>
                </div>
            );
        }

        if (details?.message) return <span className="text-sm text-gray-600">{details.message}</span>;
        return <span className="text-sm text-gray-400">—</span>;
    };

    const getActionStyle = (actionType) => {
        if (['remove_person', 'clear_medical', 'clear_documents', 'cleanup_manual'].includes(actionType)) return 'bg-red-100 text-red-700 border-red-200';
        if (['add_person', 'list_show'].includes(actionType)) return 'bg-green-100 text-green-700 border-green-200';
        if (['list_hide'].includes(actionType)) return 'bg-orange-100 text-orange-700 border-orange-200';
        if (['cleanup_settings'].includes(actionType)) return 'bg-purple-100 text-purple-700 border-purple-200';
        return 'bg-blue-100 text-blue-700 border-blue-200';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4 periodic-activity-log-overlay" dir="rtl">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col overflow-hidden periodic-activity-log-panel">
                <div className="bg-gradient-to-r from-slate-800 to-blue-900 text-white p-5 flex items-center justify-between periodic-log-header">
                    <div>
                        <h2 className="text-2xl font-bold flex items-center gap-2">🕘 سجل المتابعة الدورية</h2>
                        <p className="text-blue-100 text-sm mt-1">كل الحركات الخاصة بالقوائم والعملاء ومراحل المتابعة</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-4 border-b bg-gray-50 grid grid-cols-1 md:grid-cols-5 gap-3 periodic-log-filters">
                    <input
                        type="text"
                        placeholder="بحث باسم العميل / الجواز / القائمة / الحركة..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="border rounded-xl px-3 py-2 text-sm md:col-span-2"
                    />
                    <select
                        value={filters.userId}
                        onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                        className="border rounded-xl px-3 py-2 text-sm"
                    >
                        <option value="">كل المستخدمين</option>
                        {users.map(user => (
                            <option key={user.UserId} value={user.UserId}>{user.FullName || user.Username}</option>
                        ))}
                    </select>
                    <input
                        type="date"
                        value={filters.date}
                        onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                        className="border rounded-xl px-3 py-2 text-sm"
                    />
                    <select
                        value={filters.actionType}
                        onChange={(e) => setFilters(prev => ({ ...prev, actionType: e.target.value }))}
                        className="border rounded-xl px-3 py-2 text-sm"
                    >
                        {actionTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                    <div className="md:col-span-5 flex items-center justify-between gap-2">
                        <div className="text-sm text-gray-500">المعروض: {logs.length} حركة</div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setFilters({ userId: '', date: '', actionType: '', search: '' })}
                                className="px-4 py-2 bg-white border rounded-xl text-sm hover:bg-gray-100"
                            >
                                مسح الفلاتر
                            </button>
                            <button
                                onClick={onRefresh}
                                disabled={isLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                                تحديث
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-b bg-white periodic-log-cleanup-desktop">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="border border-blue-100 bg-blue-50 rounded-xl p-4">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <h3 className="font-semibold text-blue-900">التنظيف التلقائي</h3>
                                    <p className="text-xs text-blue-700 mt-1">
                                        الحالي: {cleanupModeLabel(cleanupSettings?.cleanupMode || 'off')}
                                        {cleanupSettings?.lastCleanupAt ? ` • آخر تنظيف: ${formatDateTime(cleanupSettings.lastCleanupAt)}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <select
                                    value={selectedCleanupMode}
                                    onChange={(e) => setSelectedCleanupMode(e.target.value)}
                                    className="flex-1 border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="off">متوقف</option>
                                    <option value="weekly">أسبوعي - يحتفظ بآخر 7 أيام</option>
                                    <option value="monthly">شهري - يحتفظ بآخر 30 يوم</option>
                                </select>
                                <button
                                    onClick={() => onSaveCleanupSettings(selectedCleanupMode)}
                                    disabled={isLoading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                                >
                                    حفظ الإعداد
                                </button>
                            </div>
                        </div>

                        <div className="border border-red-100 bg-red-50 rounded-xl p-4">
                            <div className="mb-3">
                                <h3 className="font-semibold text-red-900">حذف يدوي للسجل</h3>
                                <p className="text-xs text-red-700 mt-1">استخدمه عند الحاجة لتخفيف حجم قاعدة البيانات.</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <select
                                    value={manualDeleteMode}
                                    onChange={(e) => setManualDeleteMode(e.target.value)}
                                    className="flex-1 border border-red-200 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="older_than_week">حذف الأقدم من أسبوع</option>
                                    <option value="older_than_month">حذف الأقدم من شهر</option>
                                    <option value="older_than_3_months">حذف الأقدم من 3 شهور</option>
                                    <option value="older_than_year">حذف الأقدم من سنة</option>
                                    <option value="all">حذف السجل بالكامل</option>
                                </select>
                                <button
                                    onClick={() => {
                                        const label = manualDeleteModeLabel(manualDeleteMode);
                                        const warning = manualDeleteMode === 'all'
                                            ? `${label}\n\nتحذير: سيتم حذف كل سجل حركات المتابعة الدورية. هل أنت متأكد؟`
                                            : `${label}\n\nهل تريد تنفيذ الحذف الآن؟`;
                                        if (window.confirm(warning)) {
                                            onManualCleanup(manualDeleteMode);
                                        }
                                    }}
                                    disabled={isLoading}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                                >
                                    حذف الآن
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <details className="periodic-log-mobile-cleanup">
                    <summary>
                        <span>إعدادات السجل والتنظيف</span>
                        <small>اختياري</small>
                    </summary>
                    <div className="periodic-log-mobile-cleanup-body">
                        <div className="periodic-log-cleanup-card auto">
                            <div className="periodic-log-cleanup-title">التنظيف التلقائي</div>
                            <div className="periodic-log-cleanup-subtitle">
                                الحالي: {cleanupModeLabel(cleanupSettings?.cleanupMode || 'off')}
                                {cleanupSettings?.lastCleanupAt ? ` • آخر تنظيف: ${formatDateTime(cleanupSettings.lastCleanupAt)}` : ''}
                            </div>
                            <select
                                value={selectedCleanupMode}
                                onChange={(e) => setSelectedCleanupMode(e.target.value)}
                                className="periodic-log-mobile-select"
                            >
                                <option value="off">متوقف</option>
                                <option value="weekly">أسبوعي - يحتفظ بآخر 7 أيام</option>
                                <option value="monthly">شهري - يحتفظ بآخر 30 يوم</option>
                            </select>
                            <button
                                onClick={() => onSaveCleanupSettings(selectedCleanupMode)}
                                disabled={isLoading}
                                className="periodic-log-mobile-primary-btn"
                            >
                                حفظ الإعداد
                            </button>
                        </div>

                        <div className="periodic-log-cleanup-card danger">
                            <div className="periodic-log-cleanup-title">حذف يدوي للسجل</div>
                            <div className="periodic-log-cleanup-subtitle">استخدمه عند الحاجة لتخفيف حجم قاعدة البيانات.</div>
                            <select
                                value={manualDeleteMode}
                                onChange={(e) => setManualDeleteMode(e.target.value)}
                                className="periodic-log-mobile-select"
                            >
                                <option value="older_than_week">حذف الأقدم من أسبوع</option>
                                <option value="older_than_month">حذف الأقدم من شهر</option>
                                <option value="older_than_3_months">حذف الأقدم من 3 شهور</option>
                                <option value="older_than_year">حذف الأقدم من سنة</option>
                                <option value="all">حذف السجل بالكامل</option>
                            </select>
                            <button
                                onClick={() => {
                                    const label = manualDeleteModeLabel(manualDeleteMode);
                                    const warning = manualDeleteMode === 'all'
                                        ? `${label}\n\nتحذير: سيتم حذف كل سجل حركات المتابعة الدورية. هل أنت متأكد؟`
                                        : `${label}\n\nهل تريد تنفيذ الحذف الآن؟`;
                                    if (window.confirm(warning)) {
                                        onManualCleanup(manualDeleteMode);
                                    }
                                }}
                                disabled={isLoading}
                                className="periodic-log-mobile-danger-btn"
                            >
                                حذف الآن
                            </button>
                        </div>
                    </div>
                </details>

                <div className="overflow-auto flex-1 periodic-log-results">
                    {isLoading ? (
                        <div className="p-12 text-center text-gray-500">
                            <RefreshCw className="animate-spin mx-auto mb-3" size={32} />
                            جاري تحميل السجل...
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="p-12 text-center text-gray-400">لا توجد حركات مطابقة للفلاتر الحالية</div>
                    ) : isActivityLogMobile ? (
                        <div className="periodic-log-mobile-list">
                            {logs.map(log => (
                                <div key={log.LogID} className="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm periodic-log-card">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div>
                                            <div className="font-bold text-gray-900">{log.PersonName || log.ListName || '—'}</div>
                                            <div className="text-xs text-gray-500">{formatDateTime(log.CreatedAt)} • {log.UserName || 'النظام'}</div>
                                        </div>
                                        <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold ${getActionStyle(log.ActionType)}`}>
                                            {log.ActionTitle}
                                        </span>
                                    </div>
                                    {log.PassportNumber && <div className="text-xs text-blue-600 mb-2">جواز: {log.PassportNumber}</div>}
                                    <div>{renderDetails(log)}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <table className="w-full text-sm periodic-log-desktop-table">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th className="p-3 text-right border-b">التاريخ</th>
                                    <th className="p-3 text-right border-b">المستخدم</th>
                                    <th className="p-3 text-right border-b">الحركة</th>
                                    <th className="p-3 text-right border-b">العميل / القائمة</th>
                                    <th className="p-3 text-right border-b">التفاصيل</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.LogID} className="hover:bg-blue-50 align-top">
                                        <td className="p-3 border-b whitespace-nowrap text-gray-600">{formatDateTime(log.CreatedAt)}</td>
                                        <td className="p-3 border-b whitespace-nowrap font-medium text-gray-700">{log.UserName || 'النظام'}</td>
                                        <td className="p-3 border-b">
                                            <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold ${getActionStyle(log.ActionType)}`}>
                                                {log.ActionTitle}
                                            </span>
                                        </td>
                                        <td className="p-3 border-b min-w-[220px]">
                                            <div className="font-semibold text-gray-800">{log.PersonName || log.ListName || '—'}</div>
                                            {log.PassportNumber && <div className="text-xs text-gray-500">جواز: {log.PassportNumber}</div>}
                                            {log.ListName && log.PersonName && <div className="text-xs text-blue-600">{log.ListName}</div>}
                                        </td>
                                        <td className="p-3 border-b min-w-[420px]">{renderDetails(log)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};


// =========================
// باركود رقم الطلب - المتابعة الدورية
// =========================
const PFU_APP_BARCODE_STORAGE_KEY = 'masar_periodic_followup_application_barcode_settings_v1';

const PFU_DEFAULT_APP_BARCODE_SETTINGS = {
    printEngine: 'browser',
    outputMode: 'barcode_text', // barcode_text | barcode_only | text_only
    labelWidthMm: 52,
    labelHeightMm: 31,
    dpi: 203,
    printCopies: 1,
    sdkPort: 18080,
    logicalName: 'Printer1',
    printAgentId: '',
    printAgentPrinterName: '',
    density: 15,
    speed: 3,
    gapDots: 16,
    mediaType: 'G',
    orientation: 'T',
    barcodeXmm: 4,
    barcodeYmm: 3,
    barcodeWidthMm: 44,
    barcodeHeightMm: 19,
    barcodeNarrow: 2,
    barcodeWide: 5,
    textXmm: 2,
    textYmm: 23,
    textWidthMm: 48,
    textHeightMm: 6,
    textFontSize: 20,
    textBold: false,
    showText: true,
    lastManualCode: '',
    activeProfileName: 'الإعداد الافتراضي',
    savedProfiles: []
};

const PFU_CODE128_PATTERNS = [
    '212222','222122','222221','121223','121322','131222','122213','122312','132212','221213','221312','231212','112232','122132','122231','113222','123122','123221','223211','221132','221231','213212','223112','312131','311222','321122','321221','312212','322112','322211','212123','212321','232121','111323','131123','131321','112313','132113','132311','211313','231113','231311','112133','112331','132131','113123','113321','133121','313121','211331','231131','213113','213311','213131','311123','311321','331121','312113','312311','332111','314111','221411','431111','111224','111422','121124','121421','141122','141221','112214','112412','122114','122411','142112','142211','241211','221114','413111','241112','134111','111242','121142','121241','114212','124112','124211','411212','421112','421211','212141','214121','412121','111143','111341','131141','114113','114311','411113','411311','113141','114131','311141','411131','211412','211214','211232','2331112'
];

const cleanPeriodicBarcodeValue = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    const cleaned = String(value)
        .replace(/,+/g, '')
        .replace(/\s+/g, '')
        .trim()
        .toUpperCase();
    return cleaned || fallback;
};

const escapePeriodicBarcodeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getPeriodicApplicationBarcodeValue = (person = {}) => {
    const safePerson = person || {};
    return cleanPeriodicBarcodeValue(
        safePerson.CleanApplicationNumber || safePerson.ApplicationNumber || safePerson.applicationNumber || ''
    );
};


const getPeriodicBarcodeUserId = () => {
    try {
        const userData = localStorage.getItem('user');
        if (!userData) return null;
        const parsed = JSON.parse(userData);
        return parsed?.UserId || parsed?.UserID || parsed?.userId || null;
    } catch {
        return null;
    }
};

const stripPeriodicBarcodeProfilesFromSettings = (settings = {}) => {
    const { savedProfiles, activeProfileName, ...rest } = settings || {};
    return rest;
};

const normalizePeriodicBarcodeProfiles = (profiles = []) => {
    if (!Array.isArray(profiles)) return [];
    const seen = new Set();
    return profiles
        .filter(item => item && typeof item === 'object' && String(item.name || '').trim())
        .map(item => {
            const name = String(item.name || '').trim().slice(0, 60);
            return {
                name,
                settings: stripPeriodicBarcodeProfilesFromSettings(item.settings || {}),
                updatedAt: item.updatedAt || new Date().toISOString()
            };
        })
        .filter(item => {
            const key = item.name.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, 30);
};

const mergePeriodicBarcodeSettings = (settings = {}) => {
    const next = { ...PFU_DEFAULT_APP_BARCODE_SETTINGS, ...(settings || {}) };
    next.labelWidthMm = Math.max(20, Number(next.labelWidthMm) || PFU_DEFAULT_APP_BARCODE_SETTINGS.labelWidthMm);
    next.labelHeightMm = Math.max(15, Number(next.labelHeightMm) || PFU_DEFAULT_APP_BARCODE_SETTINGS.labelHeightMm);
    next.dpi = Math.max(100, Number(next.dpi) || PFU_DEFAULT_APP_BARCODE_SETTINGS.dpi);
    next.printCopies = Math.max(1, Math.min(20, Number(next.printCopies) || 1));
    next.sdkPort = Math.max(1, Number(next.sdkPort) || 18080);
    next.density = Math.max(1, Math.min(30, Number(next.density) || 15));
    next.speed = Math.max(1, Math.min(6, Number(next.speed) || 3));
    next.gapDots = Math.max(0, Number(next.gapDots) || 16);
    next.barcodeNarrow = Math.max(1, Math.min(4, Number(next.barcodeNarrow) || 2));
    next.barcodeWide = Math.max(next.barcodeNarrow + 1, Math.min(8, Number(next.barcodeWide) || 5));
    next.barcodeXmm = Math.max(0, Number(next.barcodeXmm) || 0);
    next.barcodeYmm = Math.max(0, Number(next.barcodeYmm) || 0);
    next.barcodeWidthMm = Math.max(10, Number(next.barcodeWidthMm) || 44);
    next.barcodeHeightMm = Math.max(4, Number(next.barcodeHeightMm) || 19);
    next.textXmm = Math.max(0, Number(next.textXmm) || 0);
    next.textYmm = Math.max(0, Number(next.textYmm) || 0);
    next.textWidthMm = Math.max(10, Number(next.textWidthMm) || 48);
    next.textHeightMm = Math.max(3, Number(next.textHeightMm) || 6);
    next.textFontSize = Math.max(8, Math.min(42, Number(next.textFontSize) || 20));
    next.printEngine = ['browser', 'agent', 'server-bridge', 'websdk'].includes(next.printEngine) ? next.printEngine : 'browser';
    next.outputMode = ['barcode_text', 'barcode_only', 'text_only'].includes(next.outputMode) ? next.outputMode : 'barcode_text';
    next.logicalName = cleanPeriodicBarcodeValue(next.logicalName, 'Printer1');
    next.printAgentId = String(next.printAgentId || '').trim();
    next.printAgentPrinterName = String(next.printAgentPrinterName || '').trim();
    next.mediaType = next.mediaType || 'G';
    next.orientation = next.orientation || 'T';
    next.showText = next.outputMode !== 'barcode_only' && next.showText !== false;
    next.textBold = !!next.textBold;
    next.lastManualCode = cleanPeriodicBarcodeValue(next.lastManualCode || '', '');
    next.activeProfileName = String(next.activeProfileName || 'الإعداد الافتراضي').trim() || 'الإعداد الافتراضي';
    next.savedProfiles = normalizePeriodicBarcodeProfiles(next.savedProfiles);
    return next;
};

const getStoredPeriodicBarcodeSettings = () => {
    try {
        const saved = localStorage.getItem(PFU_APP_BARCODE_STORAGE_KEY);
        return mergePeriodicBarcodeSettings(saved ? JSON.parse(saved) : {});
    } catch {
        return mergePeriodicBarcodeSettings();
    }
};

const saveStoredPeriodicBarcodeSettings = (settings = {}) => {
    const next = mergePeriodicBarcodeSettings(settings);
    try {
        localStorage.setItem(PFU_APP_BARCODE_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
        console.warn('تعذر حفظ إعدادات باركود رقم الطلب محليًا:', error);
    }
    return next;
};

const fetchPeriodicBarcodeSettingsFromServer = async () => {
    const response = await fetch(buildPeriodicBarcodeApiUrl('/api/periodic-follow-up/barcode-settings'), { cache: 'no-store' });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) throw new Error(payload?.error || `HTTP ${response.status}`);
    const settings = mergePeriodicBarcodeSettings(payload?.settings || {});
    saveStoredPeriodicBarcodeSettings(settings);
    return settings;
};

const savePeriodicBarcodeSettingsToServer = async (settings = {}) => {
    const finalSettings = saveStoredPeriodicBarcodeSettings(settings);
    const response = await fetch(buildPeriodicBarcodeApiUrl('/api/periodic-follow-up/barcode-settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            settings: finalSettings,
            userId: getPeriodicBarcodeUserId()
        })
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) throw new Error(payload?.error || `HTTP ${response.status}`);
    const serverSettings = mergePeriodicBarcodeSettings(payload?.settings || finalSettings);
    saveStoredPeriodicBarcodeSettings(serverSettings);
    return serverSettings;
};

const getEffectivePeriodicBarcodeSettings = async () => {
    try {
        return await fetchPeriodicBarcodeSettingsFromServer();
    } catch (error) {
        console.warn('تعذر تحميل إعدادات باركود رقم الطلب من قاعدة البيانات، سيتم استخدام آخر ضبط محلي:', error);
        return getStoredPeriodicBarcodeSettings();
    }
};

const buildPeriodicCode128Modules = (value) => {
    const text = cleanPeriodicBarcodeValue(value, 'E817519542').replace(/[^\x20-\x7E]/g, '').slice(0, 48) || 'E817519542';
    const values = [104];
    for (let i = 0; i < text.length; i += 1) {
        values.push(Math.max(0, Math.min(95, text.charCodeAt(i) - 32)));
    }
    let checksum = values[0];
    for (let i = 1; i < values.length; i += 1) checksum += values[i] * i;
    values.push(checksum % 103);
    values.push(106);

    const bars = [];
    let x = 10;
    values.forEach((codeValue) => {
        const pattern = PFU_CODE128_PATTERNS[codeValue] || PFU_CODE128_PATTERNS[0];
        for (let i = 0; i < pattern.length; i += 1) {
            const width = Number(pattern[i]) || 1;
            if (i % 2 === 0) bars.push({ x, width });
            x += width;
        }
    });
    return { text, bars, totalWidth: x + 10 };
};

const PeriodicCode128Preview = React.memo(({ value }) => {
    const data = React.useMemo(() => buildPeriodicCode128Modules(value), [value]);
    return (
        <svg viewBox={`0 0 ${data.totalWidth} 64`} preserveAspectRatio="none" className="w-full h-full block bg-white" role="img" aria-label={`Barcode ${data.text}`}>
            <rect x="0" y="0" width={data.totalWidth} height="64" fill="white" />
            {data.bars.map((bar, index) => <rect key={index} x={bar.x} y="3" width={bar.width} height="58" fill="black" />)}
        </svg>
    );
});

const buildPeriodicBarcodeSvgMarkup = (value) => {
    const data = buildPeriodicCode128Modules(value);
    const rects = data.bars.map(bar => `<rect x="${bar.x}" y="3" width="${bar.width}" height="58" fill="#000"/>`).join('');
    return `<svg viewBox="0 0 ${data.totalWidth} 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges"><rect x="0" y="0" width="${data.totalWidth}" height="64" fill="#fff"/>${rects}</svg>`;
};

const periodicMmToDots = (mm, dpi = 203) => Math.max(1, Math.round((Number(mm) || 0) * (Number(dpi) || 203) / 25.4));
const periodicClampDots = (value, min, max) => Math.max(min, Math.min(Number(value) || 0, max));

const makePeriodicTextBitmap = (text, settings = {}) => {
    if (typeof document === 'undefined') return null;
    const safeSettings = mergePeriodicBarcodeSettings(settings);
    const dpi = Number(safeSettings.dpi) || 203;
    const widthDots = periodicMmToDots(safeSettings.textWidthMm, dpi);
    const heightDots = periodicMmToDots(safeSettings.textHeightMm, dpi);
    const canvas = document.createElement('canvas');
    const scale = 2;
    canvas.width = widthDots * scale;
    canvas.height = heightDots * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(scale, scale);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, widthDots, heightDots);
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let fontPx = Math.max(8, Math.round((Number(safeSettings.textFontSize) || 20) * dpi / 72));
    const clean = cleanPeriodicBarcodeValue(text, 'E817519542');
    const setFont = () => {
        ctx.font = `${safeSettings.textBold ? '700' : '400'} ${fontPx}px Arial, Tahoma, sans-serif`;
    };
    setFont();
    while (ctx.measureText(clean).width > widthDots - 4 && fontPx > 8) {
        fontPx -= 1;
        setFont();
    }
    ctx.fillText(clean, widthDots / 2, heightDots / 2 + 1);
    return {
        dataUrl: canvas.toDataURL('image/png'),
        widthDots,
        heightDots
    };
};

const buildPeriodicBixolonFunctionJson = (functions, id) => {
    const body = { id, functions: {} };
    functions.forEach((item, index) => {
        body.functions[`func${index + 1}`] = { [item.name]: item.args || [] };
    });
    return JSON.stringify(body);
};

const buildPeriodicApplicationBixolonLabelData = ({ code, settings }) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const id = Date.now() % 100000000;
    const dpi = Number(safeSettings.dpi) || 203;
    const widthDots = periodicMmToDots(safeSettings.labelWidthMm, dpi);
    const heightDots = periodicMmToDots(safeSettings.labelHeightMm, dpi);
    const xMax = Math.max(1, widthDots - 2);
    const yMax = Math.max(1, heightDots - 2);
    const xDots = (mm) => periodicClampDots(periodicMmToDots(mm, dpi), 0, xMax);
    const yDots = (mm) => periodicClampDots(periodicMmToDots(mm, dpi), 0, yMax);
    const safeCode = cleanPeriodicBarcodeValue(code, 'E817519542');
    const shouldPrintBarcode = safeSettings.outputMode !== 'text_only';
    const shouldPrintText = safeSettings.outputMode !== 'barcode_only' && safeSettings.showText;

    const functions = [
        { name: 'setLabelId', args: [id] },
        { name: 'checkLabelStatus', args: [] },
        { name: 'clearBuffer', args: [] },
        { name: 'setPrintingType', args: ['d'] },
        { name: 'setWidth', args: [widthDots] },
        { name: 'setLength', args: [heightDots, Number(safeSettings.gapDots) || 16, safeSettings.mediaType || 'G', 0] },
        { name: 'setMargin', args: [0, 0] },
        { name: 'setOrientation', args: [safeSettings.orientation || 'T'] },
        { name: 'setSpeed', args: [Number(safeSettings.speed) || 3] },
        { name: 'setDensity', args: [Number(safeSettings.density) || 15] },
        { name: 'setCharacterset', args: [0, 19] }
    ];

    if (shouldPrintBarcode) {
        functions.push({
            name: 'draw1DBarcode',
            args: [safeCode, xDots(safeSettings.barcodeXmm), yDots(safeSettings.barcodeYmm), 1, Number(safeSettings.barcodeNarrow) || 2, Number(safeSettings.barcodeWide) || 5, periodicMmToDots(safeSettings.barcodeHeightMm, dpi), 0, 0]
        });
    }

    if (shouldPrintText) {
        const bitmap = makePeriodicTextBitmap(safeCode, safeSettings);
        if (bitmap?.dataUrl) {
            functions.push({ name: 'drawBitmap', args: [bitmap.dataUrl, xDots(safeSettings.textXmm), yDots(safeSettings.textYmm), bitmap.widthDots, 0] });
        }
    }

    functions.push({ name: 'printBuffer', args: [] });
    return buildPeriodicBixolonFunctionJson(functions, id);
};

const getPeriodicRuntimeApiBaseUrl = () => {
    const configuredBase = String(config.PASSPORT_API_URL || config.API_BASE_URL || '').replace(/\/+$/, '');
    if (typeof window === 'undefined' || !window.location) return configuredBase;
    const currentHost = window.location.hostname;
    const currentHostApiBase = window.location.origin; // السيرفر على نفس الموقع
    if (!configuredBase) return currentHostApiBase;
    try {
        const configuredUrl = new URL(configuredBase, window.location.origin);
        const localHosts = ['localhost', '127.0.0.1', '0.0.0.0'];
        const isBrowserOnRealNetwork = currentHost && !localHosts.includes(currentHost);
        const isConfiguredLocalhost = localHosts.includes(configuredUrl.hostname);
        if (isBrowserOnRealNetwork && isConfiguredLocalhost) return currentHostApiBase;
        return configuredBase;
    } catch {
        return configuredBase || currentHostApiBase;
    }
};

const buildPeriodicBarcodeApiUrl = (path) => `${getPeriodicRuntimeApiBaseUrl()}${path}`;

const postPeriodicToServerPrintBridge = async (settings, labelJson) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const response = await fetch(buildPeriodicBarcodeApiUrl('/api/passports/barcode/print-server'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            logicalName: safeSettings.logicalName || 'Printer1',
            sdkPort: Number(safeSettings.sdkPort) || 18080,
            labelJson
        })
    });
    const text = await response.text().catch(() => '');
    let payload = null;
    try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || text || `HTTP ${response.status}`);
    }
    return payload || { success: true };
};

const postPeriodicToWebSdk = async (settings, labelJson) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const safeName = encodeURIComponent(safeSettings.logicalName || 'Printer1');
    const base = `http://127.0.0.1:${Number(safeSettings.sdkPort) || 18080}/WebPrintSDK/`;
    const urls = [`${base}${safeName}`, `${base}${safeName}.bxl`];
    let lastError = null;
    for (const url of urls) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                body: labelJson
            });
            const text = await response.text().catch(() => '');
            if (response.ok && !/error/i.test(text)) return { success: true, responseText: text };
            lastError = text || `HTTP ${response.status}`;
        } catch (error) {
            lastError = error.message;
            try {
                await fetch(url, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: labelJson });
                return { success: true, responseText: 'تم إرسال الطلب للطابعة بدون قراءة نتيجة التنفيذ' };
            } catch (silentError) {
                lastError = silentError.message || lastError;
            }
        }
    }
    throw new Error(lastError || 'تعذر الاتصال بخدمة Web Print SDK المحلية');
};

const fetchPeriodicPrintAgents = async () => {
    const response = await fetch(buildPeriodicBarcodeApiUrl('/api/passports/barcode/agents'), { cache: 'no-store' });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) throw new Error(payload?.error || `HTTP ${response.status}`);
    const agents = Array.isArray(payload?.agents) ? payload.agents : [];
    return agents.filter(agent => agent?.agentId).sort((a, b) => {
        if (!!a.isOnline !== !!b.isOnline) return a.isOnline ? -1 : 1;
        return String(a.agentName || a.computerName || a.agentId).localeCompare(String(b.agentName || b.computerName || b.agentId), 'ar');
    });
};

const postPeriodicToMasarPrintAgent = async (settings, payload, copies = 1) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const agentId = String(safeSettings.printAgentId || '').trim();
    if (!agentId) throw new Error('اختار جهاز Masar Print Agent الأول');
    const response = await fetch(buildPeriodicBarcodeApiUrl('/api/passports/barcode/print-agent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            agentId,
            printerName: String(safeSettings.printAgentPrinterName || '').trim(),
            copies: Math.max(1, Number(copies) || 1),
            payload
        })
    });
    const text = await response.text().catch(() => '');
    let result = null;
    try { result = text ? JSON.parse(text) : null; } catch { result = null; }
    if (!response.ok || result?.success === false) throw new Error(result?.error || text || `HTTP ${response.status}`);
    return result || { success: true };
};

const buildPeriodicAgentPayload = ({ person, code, settings, labelJson }) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    return {
        version: 4,
        type: 'passport_barcode_label',
        printMode: 'native-first',
        code,
        content: code,
        symbolData: code,
        fullContent: code,
        barcodeData: code,
        nativeLabelJson: labelJson,
        name: '',
        label: {
            widthMm: Number(safeSettings.labelWidthMm) || 52,
            heightMm: Number(safeSettings.labelHeightMm) || 31,
            dpi: Number(safeSettings.dpi) || 203
        },
        settings: {
            showName: false,
            showId: safeSettings.outputMode !== 'barcode_only' && !!safeSettings.showText,
            showBarcode: safeSettings.outputMode !== 'text_only',
            codeType: 'barcode',
            idXmm: Number(safeSettings.textXmm) || 0,
            idYmm: Number(safeSettings.textYmm) || 0,
            idWidthMm: Number(safeSettings.textWidthMm) || 48,
            idHeightMm: Number(safeSettings.textHeightMm) || 6,
            idFontSize: Number(safeSettings.textFontSize) || 20,
            idBold: !!safeSettings.textBold,
            idAlign: 'center',
            barcodeXmm: Number(safeSettings.barcodeXmm) || 0,
            barcodeYmm: Number(safeSettings.barcodeYmm) || 0,
            barcodeHeightMm: Number(safeSettings.barcodeHeightMm) || 19,
            barcodeNarrow: Number(safeSettings.barcodeNarrow) || 2,
            barcodeWide: Number(safeSettings.barcodeWide) || 5,
            density: Number(safeSettings.density) || 15,
            speed: Number(safeSettings.speed) || 3,
            gapDots: Number(safeSettings.gapDots) || 16,
            mediaType: safeSettings.mediaType || 'G',
            orientation: safeSettings.orientation || 'T'
        },
        passport: person ? {
            passportId: person.PassportID || null,
            officeSerial: person.OfficeFileNumber || person.FileNumber || '',
            passportNumber: person.PassportNumber || '',
            company: person.CompanyName || '',
            delegate: person.DelegateName || ''
        } : null
    };
};

const printPeriodicApplicationBarcodeInBrowser = ({ code, codes, settings }) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const codeList = (Array.isArray(codes) && codes.length > 0 ? codes : [code])
        .map(item => cleanPeriodicBarcodeValue(item, ''))
        .filter(Boolean);
    if (codeList.length === 0) throw new Error('لا توجد أرقام صالحة للطباعة');
    const printWindow = window.open('', '_blank', 'width=520,height=360');
    if (!printWindow) throw new Error('المتصفح منع نافذة الطباعة. اسمح بالنوافذ المنبثقة ثم حاول مرة أخرى.');
    const copies = Math.max(1, Number(safeSettings.printCopies) || 1);
    const shouldPrintBarcode = safeSettings.outputMode !== 'text_only';
    const shouldPrintText = safeSettings.outputMode !== 'barcode_only' && safeSettings.showText;
    const labels = codeList.flatMap((safeCode) => {
        const barcodeSvg = buildPeriodicBarcodeSvgMarkup(safeCode);
        return Array.from({ length: copies }, () => `
            <div class="pfu-label">
                ${shouldPrintBarcode ? `<div class="pfu-barcode">${barcodeSvg}</div>` : ''}
                ${shouldPrintText ? `<div class="pfu-code">${escapePeriodicBarcodeHtml(safeCode)}</div>` : ''}
            </div>
        `);
    }).join('');
    const titleText = codeList.length === 1 ? codeList[0] : `${codeList.length} أرقام`;
    printWindow.document.write(`
        <!doctype html>
        <html>
        <head>
            <meta charset="utf-8" />
            <title>طباعة باركود رقم الطلب ${escapePeriodicBarcodeHtml(titleText)}</title>
            <style>
                @page { size: ${safeSettings.labelWidthMm}mm ${safeSettings.labelHeightMm}mm; margin: 0; }
                html, body { margin: 0; padding: 0; background: #fff; }
                .pfu-label {
                    width: ${safeSettings.labelWidthMm}mm;
                    height: ${safeSettings.labelHeightMm}mm;
                    position: relative;
                    background: #fff;
                    page-break-after: always;
                    overflow: hidden;
                    font-family: Arial, Tahoma, sans-serif;
                }
                .pfu-barcode {
                    position: absolute;
                    left: ${safeSettings.barcodeXmm}mm;
                    top: ${safeSettings.barcodeYmm}mm;
                    width: ${safeSettings.barcodeWidthMm}mm;
                    height: ${safeSettings.barcodeHeightMm}mm;
                }
                .pfu-barcode svg { width: 100%; height: 100%; display: block; }
                .pfu-code {
                    position: absolute;
                    left: ${safeSettings.textXmm}mm;
                    top: ${safeSettings.textYmm}mm;
                    width: ${safeSettings.textWidthMm}mm;
                    height: ${safeSettings.textHeightMm}mm;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #000;
                    font-size: ${safeSettings.textFontSize}pt;
                    font-weight: ${safeSettings.textBold ? 700 : 400};
                    letter-spacing: ${safeSettings.outputMode === 'text_only' ? '0.8mm' : '1.4mm'};
                    line-height: 1;
                    white-space: nowrap;
                }
            </style>
        </head>
        <body>${labels}</body>
        </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => printWindow.close(), 350);
    };
};

const printPeriodicApplicationBarcodeWithSettings = async ({ person, code, settings }) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const safeCode = cleanPeriodicBarcodeValue(code, 'E817519542');
    if (safeSettings.printEngine === 'browser') {
        printPeriodicApplicationBarcodeInBrowser({ code: safeCode, settings: safeSettings });
        return true;
    }
    const labelJson = buildPeriodicApplicationBixolonLabelData({ code: safeCode, settings: safeSettings });
    if (safeSettings.printEngine === 'server-bridge') {
        for (let i = 0; i < safeSettings.printCopies; i += 1) await postPeriodicToServerPrintBridge(safeSettings, labelJson);
        return true;
    }
    if (safeSettings.printEngine === 'websdk') {
        for (let i = 0; i < safeSettings.printCopies; i += 1) await postPeriodicToWebSdk(safeSettings, labelJson);
        return true;
    }
    const payload = buildPeriodicAgentPayload({ person, code: safeCode, settings: safeSettings, labelJson });
    await postPeriodicToMasarPrintAgent(safeSettings, payload, safeSettings.printCopies);
    return true;
};


const printPeriodicApplicationBarcodeBatchWithSettings = async ({ items, settings }) => {
    const safeSettings = mergePeriodicBarcodeSettings(settings || {});
    const jobs = (Array.isArray(items) ? items : [])
        .map(item => ({
            person: item?.person || null,
            code: cleanPeriodicBarcodeValue(item?.code || getPeriodicApplicationBarcodeValue(item?.person), '')
        }))
        .filter(item => item.code);

    if (jobs.length === 0) throw new Error('لا توجد أرقام طلب صالحة للطباعة');

    if (safeSettings.printEngine === 'browser') {
        printPeriodicApplicationBarcodeInBrowser({ codes: jobs.map(item => item.code), settings: safeSettings });
        return true;
    }

    for (const job of jobs) {
        await printPeriodicApplicationBarcodeWithSettings({ person: job.person, code: job.code, settings: safeSettings });
    }
    return true;
};

const PeriodicApplicationBarcodeModal = ({
    isOpen,
    onClose,
    person,
    showAlert,
    canSaveNamedProfile = false,
    canDeleteNamedProfile = false,
    canSaveGlobalSettings = false
}) => {
    const [settings, setSettings] = React.useState(() => getStoredPeriodicBarcodeSettings());
    const [manualCode, setManualCode] = React.useState('');
    const [profileName, setProfileName] = React.useState('');
    const [isPrinting, setIsPrinting] = React.useState(false);
    const [isSavingSettings, setIsSavingSettings] = React.useState(false);
    const [isLoadingSettings, setIsLoadingSettings] = React.useState(false);
    const [agents, setAgents] = React.useState([]);
    const [agentsLoading, setAgentsLoading] = React.useState(false);
    const [dragState, setDragState] = React.useState(null);
    const previewRef = React.useRef(null);
    const previewScale = 6;

    const personCode = React.useMemo(() => getPeriodicApplicationBarcodeValue(person), [person]);
    const code = React.useMemo(() => cleanPeriodicBarcodeValue(manualCode || personCode || settings.lastManualCode || 'E817519542', 'E817519542'), [manualCode, personCode, settings.lastManualCode]);

    React.useEffect(() => {
        if (!isOpen) return;
        let mounted = true;
        setIsLoadingSettings(true);
        getEffectivePeriodicBarcodeSettings()
            .then(next => {
                if (!mounted) return;
                setSettings(next);
                setManualCode(personCode || next.lastManualCode || '');
                setProfileName(next.activeProfileName && next.activeProfileName !== 'الإعداد الافتراضي' ? next.activeProfileName : '');
            })
            .catch(() => {
                if (!mounted) return;
                const fallback = getStoredPeriodicBarcodeSettings();
                setSettings(fallback);
                setManualCode(personCode || fallback.lastManualCode || '');
            })
            .finally(() => { if (mounted) setIsLoadingSettings(false); });
        return () => { mounted = false; };
    }, [isOpen, personCode]);

    React.useEffect(() => {
        if (!isOpen || settings.printEngine !== 'agent') return;
        let mounted = true;
        setAgentsLoading(true);
        fetchPeriodicPrintAgents()
            .then(list => { if (mounted) setAgents(list); })
            .catch(error => {
                if (mounted) {
                    setAgents([]);
                    showAlert?.('تعذر تحميل أجهزة الطباعة: ' + error.message, 'error');
                }
            })
            .finally(() => { if (mounted) setAgentsLoading(false); });
        return () => { mounted = false; };
    }, [isOpen, settings.printEngine, showAlert]);

    React.useEffect(() => {
        if (!dragState) return undefined;
        const handleMove = (event) => {
            event.preventDefault();
            const dxMm = (event.clientX - dragState.startClientX) / previewScale;
            const dyMm = (event.clientY - dragState.startClientY) / previewScale;
            setSettings(prev => {
                const next = { ...prev };
                if (dragState.target === 'barcode') {
                    next.barcodeXmm = Math.max(0, Math.min(Number(next.labelWidthMm || 52), Number(dragState.startXmm) + dxMm)).toFixed(1);
                    next.barcodeYmm = Math.max(0, Math.min(Number(next.labelHeightMm || 31), Number(dragState.startYmm) + dyMm)).toFixed(1);
                } else {
                    next.textXmm = Math.max(0, Math.min(Number(next.labelWidthMm || 52), Number(dragState.startXmm) + dxMm)).toFixed(1);
                    next.textYmm = Math.max(0, Math.min(Number(next.labelHeightMm || 31), Number(dragState.startYmm) + dyMm)).toFixed(1);
                }
                saveStoredPeriodicBarcodeSettings({ ...next, lastManualCode: cleanPeriodicBarcodeValue(manualCode, '') });
                return next;
            });
        };
        const handleUp = () => setDragState(null);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [dragState, manualCode]);

    if (!isOpen) return null;

    const selectedAgent = agents.find(agent => String(agent.agentId) === String(settings.printAgentId));
    const printers = Array.isArray(selectedAgent?.printers) ? selectedAgent.printers : [];
    const shouldShowBarcode = settings.outputMode !== 'text_only';
    const shouldShowText = settings.outputMode !== 'barcode_only' && settings.showText;
    const outputModeLabel = {
        barcode_text: 'باركود + رقم',
        barcode_only: 'باركود فقط',
        text_only: 'رقم فقط'
    }[settings.outputMode] || 'باركود + رقم';
    const outputModeHint = {
        barcode_text: 'سيطبع الباركود وفوق/تحت منه رقم الطلب حسب مكانهما في المعاينة.',
        barcode_only: 'سيطبع الباركود فقط بدون رقم مكتوب.',
        text_only: 'سيطبع الرقم فقط بشكل كبير مثل الملصق الموجود في الصورة.'
    }[settings.outputMode] || '';
    const printEngineLabel = {
        browser: 'طباعة من المتصفح',
        agent: 'Masar Print Agent',
        'server-bridge': 'طباعة من جهاز السيرفر',
        websdk: 'Web Print SDK'
    }[settings.printEngine] || 'طباعة من المتصفح';
    const activeProfileLabel = settings.activeProfileName || 'الإعداد الافتراضي';
    const normalizeNumberInput = (value) => String(value ?? '')
        .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
        .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
        .replace(/,/g, '.')
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*)\./g, '$1');
    const stopFieldKeys = (e) => e.stopPropagation();

    const updateSetting = (key, value) => {
        setSettings(prev => {
            const next = { ...prev, [key]: value };
            if (key === 'outputMode') {
                next.showText = value !== 'barcode_only';
            }
            saveStoredPeriodicBarcodeSettings({ ...next, lastManualCode: cleanPeriodicBarcodeValue(manualCode, '') });
            return next;
        });
    };

    const sanitizeSettings = () => {
        setSettings(prev => saveStoredPeriodicBarcodeSettings({ ...prev, lastManualCode: cleanPeriodicBarcodeValue(manualCode, '') }));
    };

    const saveSettingsGlobally = async (customSettings = settings, permissionAllowed = canSaveGlobalSettings) => {
        if (!permissionAllowed) {
            showAlert?.('ليس لديك صلاحية حفظ إعدادات الباركود لكل المستخدمين', 'warning');
            return null;
        }
        setIsSavingSettings(true);
        try {
            const finalSettings = mergePeriodicBarcodeSettings({
                ...customSettings,
                lastManualCode: cleanPeriodicBarcodeValue(manualCode, ''),
                activeProfileName: customSettings.activeProfileName || settings.activeProfileName || 'الإعداد الافتراضي'
            });
            const saved = await savePeriodicBarcodeSettingsToServer(finalSettings);
            setSettings(saved);
            showAlert?.('تم حفظ إعدادات طباعة الباركود على قاعدة البيانات لكل المستخدمين', 'success');
            return saved;
        } catch (error) {
            console.error('خطأ حفظ إعدادات باركود المتابعة:', error);
            showAlert?.('فشل حفظ إعدادات الباركود: ' + error.message, 'error');
            throw error;
        } finally {
            setIsSavingSettings(false);
        }
    };

    const resetDefaults = () => {
        const next = saveStoredPeriodicBarcodeSettings({
            ...PFU_DEFAULT_APP_BARCODE_SETTINGS,
            savedProfiles: settings.savedProfiles || []
        });
        setSettings(next);
        showAlert?.('تم رجوع ضبط باركود رقم الطلب للوضع الافتراضي', 'success');
    };

    const saveNamedProfile = async () => {
        if (!canSaveNamedProfile) {
            showAlert?.('ليس لديك صلاحية حفظ إعداد باركود باسم وجعله النشط', 'warning');
            return;
        }
        const name = String(profileName || '').trim();
        if (!name) {
            showAlert?.('اكتب اسم الإعداد المراد حفظه قبل الحفظ', 'warning');
            return;
        }
        const currentProfiles = normalizePeriodicBarcodeProfiles(settings.savedProfiles || []);
        const filtered = currentProfiles.filter(item => item.name.toLowerCase() !== name.toLowerCase());
        const next = mergePeriodicBarcodeSettings({
            ...settings,
            activeProfileName: name,
            savedProfiles: [
                ...filtered,
                {
                    name,
                    settings: stripPeriodicBarcodeProfilesFromSettings(settings),
                    updatedAt: new Date().toISOString()
                }
            ]
        });
        setSettings(next);
        await saveSettingsGlobally(next, canSaveNamedProfile);
    };

    const loadNamedProfile = (name) => {
        if (!name || name === 'الإعداد الافتراضي') {
            const next = mergePeriodicBarcodeSettings({
                ...PFU_DEFAULT_APP_BARCODE_SETTINGS,
                savedProfiles: settings.savedProfiles || [],
                activeProfileName: 'الإعداد الافتراضي'
            });
            setSettings(next);
            setProfileName('');
            saveStoredPeriodicBarcodeSettings(next);
            return;
        }
        const profile = (settings.savedProfiles || []).find(item => item.name === name);
        if (!profile) return;
        const next = mergePeriodicBarcodeSettings({
            ...settings,
            ...(profile.settings || {}),
            savedProfiles: settings.savedProfiles || [],
            activeProfileName: profile.name
        });
        setSettings(next);
        setProfileName(profile.name);
        saveStoredPeriodicBarcodeSettings(next);
    };

    const deleteNamedProfile = async () => {
        if (!canDeleteNamedProfile) {
            showAlert?.('ليس لديك صلاحية حذف إعداد باركود محفوظ', 'warning');
            return;
        }
        const name = String(profileName || settings.activeProfileName || '').trim();
        if (!name) return;
        const next = mergePeriodicBarcodeSettings({
            ...settings,
            activeProfileName: 'الإعداد الافتراضي',
            savedProfiles: (settings.savedProfiles || []).filter(item => item.name !== name)
        });
        setSettings(next);
        setProfileName('');
        await saveSettingsGlobally(next, canDeleteNamedProfile);
    };

    const handlePrint = async () => {
        const safeCode = cleanPeriodicBarcodeValue(code);
        if (!safeCode) {
            showAlert?.('لا يوجد رقم أو نص للطباعة', 'error');
            return;
        }
        setIsPrinting(true);
        try {
            const finalSettings = saveStoredPeriodicBarcodeSettings({ ...settings, lastManualCode: safeCode });
            await printPeriodicApplicationBarcodeWithSettings({ person, code: safeCode, settings: finalSettings });
            showAlert?.(`تم إرسال الطباعة: ${safeCode}`, 'success');
        } catch (error) {
            console.error('خطأ طباعة باركود رقم الطلب:', error);
            showAlert?.('فشل طباعة باركود رقم الطلب: ' + error.message, 'error');
            alert(`فشل طباعة باركود رقم الطلب\n\n${error.message}`);
        } finally {
            setIsPrinting(false);
        }
    };

    const startDrag = (event, target) => {
        event.preventDefault();
        setDragState({
            target,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startXmm: target === 'barcode' ? Number(settings.barcodeXmm) || 0 : Number(settings.textXmm) || 0,
            startYmm: target === 'barcode' ? Number(settings.barcodeYmm) || 0 : Number(settings.textYmm) || 0
        });
    };

    const numberInput = (key, label, step = '0.5', min = '0') => (
        <label className="block">
            <span className="block text-xs font-bold text-gray-600 mb-1">{label}</span>
            <input
                type="text"
                inputMode="decimal"
                value={settings[key] ?? ''}
                onChange={(e) => updateSetting(key, normalizeNumberInput(e.target.value))}
                onBlur={sanitizeSettings}
                onKeyDown={stopFieldKeys}
                onKeyDownCapture={stopFieldKeys}
                onKeyUp={stopFieldKeys}
                onKeyUpCapture={stopFieldKeys}
                onInput={stopFieldKeys}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                placeholder={min}
            />
        </label>
    );

    return (
        <div className="fixed inset-0 bg-black/60 z-[10040] flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
                <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white px-5 py-4 flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-black flex items-center gap-2"><Barcode size={22} /> ضبط طباعة رقم الطلب / الباركود</h2>
                        <p className="text-xs text-blue-100 mt-1">اضبط هنا الطريقة التي سيستخدمها زر التنفيذ تلقائيًا داخل جدول المتابعة الدورية. أي تعديل لا يعمل على الشبكة إلا بعد الحفظ.</p>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-white/15"><X size={22} /></button>
                </div>

                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-white border border-blue-200 rounded-2xl p-3">
                        <div className="text-[11px] font-black text-blue-600 mb-1">الإعداد الذي سيعمل تلقائيًا</div>
                        <div className="font-black text-gray-900">{activeProfileLabel}</div>
                        <div className="text-xs text-gray-500 mt-1">زر التنفيذ داخل الجدول يستخدم آخر ضبط محفوظ في قاعدة البيانات.</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-3">
                        <div className="text-[11px] font-black text-gray-500 mb-1">نوع الطباعة المختار</div>
                        <div className="font-black text-gray-900">{outputModeLabel}</div>
                        <div className="text-xs text-gray-500 mt-1">{outputModeHint}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-3">
                        <div className="text-[11px] font-black text-gray-500 mb-1">طريقة الطباعة وعدد النسخ</div>
                        <div className="font-black text-gray-900">{printEngineLabel}</div>
                        <div className="text-xs text-gray-500 mt-1">{Number(settings.printCopies) || 1} نسخة • ملصق {Number(settings.labelWidthMm) || 0}×{Number(settings.labelHeightMm) || 0} مم</div>
                    </div>
                    <div className="md:col-span-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2 text-sm text-amber-900">
                        <b>مهم:</b> التغيير داخل النافذة يكون معاينة وتعديل محلي أولًا. لتطبيقه على كل المستخدمين اضغط زر <b>حفظ الإعداد النشط لكل المستخدمين</b> أسفل النافذة.
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-5 bg-gray-50">
                    <div className="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                            <div className="flex-1 min-w-[260px]">
                                <label className="block text-xs font-bold text-gray-600 mb-1">القيمة المراد طباعتها للتجربة والمعاينة</label>
                                <input
                                    type="text"
                                    value={manualCode}
                                    onChange={(e) => setManualCode(e.target.value)}
                                    onKeyDown={stopFieldKeys}
                                    onKeyDownCapture={stopFieldKeys}
                                    onKeyUp={stopFieldKeys}
                                    onKeyUpCapture={stopFieldKeys}
                                    onInput={stopFieldKeys}
                                    placeholder={personCode || 'اكتب رقم الطلب أو أي رقم يدويًا'}
                                    className="w-full border border-gray-300 rounded-2xl px-4 py-3 text-2xl font-mono tracking-[0.12em] text-gray-900 text-left"
                                    dir="ltr"
                                />
                                <p className="text-xs text-gray-500 mt-1">هذا الحقل للتجربة أو الطباعة اليدوية فقط. زر التنفيذ داخل الصف يطبع رقم الطلب الخاص بالصف تلقائيًا.</p>
                            </div>
                            <button type="button" onClick={resetDefaults} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-bold text-gray-700">افتراضي</button>
                        </div>

                        <div className="bg-white rounded-2xl p-4 border border-gray-200 flex items-center justify-center overflow-auto">
                            <div
                                ref={previewRef}
                                className="relative bg-white border border-dashed border-gray-300 shadow-inner select-none"
                                style={{ width: `${Math.max(240, Number(settings.labelWidthMm) * previewScale)}px`, height: `${Math.max(150, Number(settings.labelHeightMm) * previewScale)}px` }}
                            >
                                {shouldShowBarcode && (
                                    <div
                                        className="absolute bg-white cursor-move ring-1 ring-transparent hover:ring-blue-400"
                                        onMouseDown={(e) => startDrag(e, 'barcode')}
                                        title="اسحب لتحريك الباركود"
                                        style={{
                                            left: `${Number(settings.barcodeXmm) * previewScale}px`,
                                            top: `${Number(settings.barcodeYmm) * previewScale}px`,
                                            width: `${Number(settings.barcodeWidthMm) * previewScale}px`,
                                            height: `${Number(settings.barcodeHeightMm) * previewScale}px`
                                        }}
                                    >
                                        <PeriodicCode128Preview value={code} />
                                    </div>
                                )}
                                {shouldShowText && (
                                    <div
                                        className="absolute flex items-center justify-center text-black whitespace-nowrap cursor-move ring-1 ring-transparent hover:ring-green-400"
                                        onMouseDown={(e) => startDrag(e, 'text')}
                                        title="اسحب لتحريك الرقم"
                                        dir="ltr"
                                        style={{
                                            left: `${Number(settings.textXmm) * previewScale}px`,
                                            top: `${Number(settings.textYmm) * previewScale}px`,
                                            width: `${Number(settings.textWidthMm) * previewScale}px`,
                                            height: `${Number(settings.textHeightMm) * previewScale}px`,
                                            fontSize: `${Math.max(14, Number(settings.textFontSize) * 1.25)}px`,
                                            fontWeight: settings.textBold ? 700 : 400,
                                            letterSpacing: settings.outputMode === 'text_only' ? '2px' : '4px',
                                            fontFamily: 'Arial, Tahoma, sans-serif'
                                        }}
                                    >
                                        {code}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 text-sm text-blue-900 leading-6">
                                السحب بالماوس داخل المعاينة يغير X/Y مباشرة. ثم اضغط حفظ الإعدادات لتظهر لكل المستخدمين.
                            </div>
                            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 text-sm text-amber-900 leading-6">
                                وضع “رقم فقط” مناسب للملصق الموجود في الصورة، بدون أي باركود.
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                            <h3 className="font-black text-gray-900 mb-3">الإعدادات المحفوظة / الإعداد النشط</h3>
                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 mb-3 text-sm text-blue-900 leading-6">اختيار إعداد من القائمة يعرضه في المعاينة. الذي سيشتغل تلقائيًا هو آخر إعداد تضغط عليه حفظ في قاعدة البيانات. استخدم زر حفظ باسم وجعله النشط لو تريد أكثر من شكل محفوظ.</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="block sm:col-span-2">
                                    <span className="block text-xs font-bold text-gray-600 mb-1">اختيار إعداد محفوظ لعرضه</span>
                                    <select
                                        value={settings.activeProfileName || ''}
                                        onChange={(e) => loadNamedProfile(e.target.value)}
                                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="الإعداد الافتراضي">الإعداد الافتراضي</option>
                                        {(settings.savedProfiles || []).map(profile => <option key={profile.name} value={profile.name}>{profile.name}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="block text-xs font-bold text-gray-600 mb-1">اسم الإعداد المراد حفظه</span>
                                    <input value={profileName} onChange={(e) => setProfileName(e.target.value)} onKeyDown={stopFieldKeys} onKeyDownCapture={stopFieldKeys} onKeyUp={stopFieldKeys} onKeyUpCapture={stopFieldKeys} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" placeholder="مثلاً: ملصق 52×31 رقم فقط" />
                                </label>
                                {(canSaveNamedProfile || canDeleteNamedProfile) && (
                                    <div className="flex gap-2 items-end">
                                        {canSaveNamedProfile && (
                                            <button type="button" onClick={saveNamedProfile} disabled={isSavingSettings} className="flex-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold disabled:opacity-60">حفظ باسم وجعله النشط</button>
                                        )}
                                        {canDeleteNamedProfile && (
                                            <button type="button" onClick={deleteNamedProfile} disabled={isSavingSettings || !(settings.savedProfiles || []).length} className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl text-sm font-bold disabled:opacity-50">حذف</button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                            <h3 className="font-black text-gray-900 mb-3">نوع الطباعة</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <button type="button" onClick={() => updateSetting('outputMode', 'barcode_text')} className={`rounded-2xl border p-3 text-sm font-bold text-right ${settings.outputMode === 'barcode_text' ? 'bg-blue-50 border-blue-500 text-blue-800 ring-2 ring-blue-100' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                    <div>{settings.outputMode === 'barcode_text' ? '✓ ' : ''}باركود + رقم</div>
                                    <div className="text-[11px] font-normal mt-1 opacity-75">الباركود والرقم معًا</div>
                                </button>
                                <button type="button" onClick={() => updateSetting('outputMode', 'barcode_only')} className={`rounded-2xl border p-3 text-sm font-bold text-right ${settings.outputMode === 'barcode_only' ? 'bg-blue-50 border-blue-500 text-blue-800 ring-2 ring-blue-100' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                    <div>{settings.outputMode === 'barcode_only' ? '✓ ' : ''}باركود فقط</div>
                                    <div className="text-[11px] font-normal mt-1 opacity-75">بدون الرقم المكتوب</div>
                                </button>
                                <button type="button" onClick={() => updateSetting('outputMode', 'text_only')} className={`rounded-2xl border p-3 text-sm font-bold text-right ${settings.outputMode === 'text_only' ? 'bg-blue-50 border-blue-500 text-blue-800 ring-2 ring-blue-100' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                                    <div>{settings.outputMode === 'text_only' ? '✓ ' : ''}رقم فقط</div>
                                    <div className="text-[11px] font-normal mt-1 opacity-75">مثل ملصق 284 بالصورة</div>
                                </button>
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                            <h3 className="font-black text-gray-900 mb-3">طريقة الطباعة</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="block sm:col-span-2">
                                    <span className="block text-xs font-bold text-gray-600 mb-1">اختيار طريقة الطباعة</span>
                                    <select value={settings.printEngine} onChange={(e) => updateSetting('printEngine', e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                                        <option value="browser">طباعة من المتصفح - أبسط اختيار</option>
                                        <option value="agent">Masar Print Agent - طابعة USB على جهاز آخر</option>
                                        <option value="server-bridge">طباعة من جهاز السيرفر</option>
                                        <option value="websdk">Web Print SDK على نفس الجهاز</option>
                                    </select>
                                </label>

                                {settings.printEngine === 'agent' && (
                                    <>
                                        <label className="block sm:col-span-2">
                                            <span className="block text-xs font-bold text-gray-600 mb-1">جهاز الطباعة</span>
                                            <select
                                                value={settings.printAgentId}
                                                onChange={(e) => {
                                                    const agentId = e.target.value;
                                                    const agent = agents.find(item => String(item.agentId) === String(agentId));
                                                    const defaultPrinter = (agent?.printers || []).find(printer => printer.isDefault)?.name || (agent?.printers || [])[0]?.name || '';
                                                    updateSetting('printAgentId', agentId);
                                                    setSettings(prev => saveStoredPeriodicBarcodeSettings({ ...prev, printAgentId: agentId, printAgentPrinterName: defaultPrinter }));
                                                }}
                                                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="">{agentsLoading ? 'جاري تحميل الأجهزة...' : 'اختر الجهاز...'}</option>
                                                {agents.map(agent => <option key={agent.agentId} value={agent.agentId}>{(agent.agentName || agent.computerName || agent.agentId)} {agent.isOnline ? '— Online' : '— Offline'}</option>)}
                                            </select>
                                        </label>
                                        <label className="block sm:col-span-2">
                                            <span className="block text-xs font-bold text-gray-600 mb-1">الطابعة</span>
                                            <select value={settings.printAgentPrinterName} onChange={(e) => updateSetting('printAgentPrinterName', e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                                                <option value="">اختر الطابعة...</option>
                                                {printers.map(printer => <option key={printer.name} value={printer.name}>{printer.name}{printer.isDefault ? ' — Default' : ''}</option>)}
                                            </select>
                                        </label>
                                        <button type="button" onClick={() => {
                                            setAgentsLoading(true);
                                            fetchPeriodicPrintAgents().then(setAgents).catch(error => showAlert?.('تعذر تحديث أجهزة الطباعة: ' + error.message, 'error')).finally(() => setAgentsLoading(false));
                                        }} className="sm:col-span-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-bold text-slate-700">تحديث أجهزة الطباعة</button>
                                    </>
                                )}

                                {settings.printEngine !== 'browser' && settings.printEngine !== 'agent' && (
                                    <>
                                        <label className="block">
                                            <span className="block text-xs font-bold text-gray-600 mb-1">Logical Name</span>
                                            <input value={settings.logicalName} onChange={(e) => updateSetting('logicalName', e.target.value)} onKeyDown={stopFieldKeys} onKeyDownCapture={stopFieldKeys} onKeyUp={stopFieldKeys} onKeyUpCapture={stopFieldKeys} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
                                        </label>
                                        {numberInput('sdkPort', 'Port', '1', '1')}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                            <h3 className="font-black text-gray-900 mb-3">مقاس الملصق والطباعة</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {numberInput('labelWidthMm', 'عرض الملصق mm')}
                                {numberInput('labelHeightMm', 'ارتفاع الملصق mm')}
                                {numberInput('printCopies', 'عدد النسخ', '1', '1')}
                                {numberInput('dpi', 'DPI', '1', '100')}
                                {settings.printEngine !== 'browser' && numberInput('density', 'الكثافة', '1', '1')}
                                {settings.printEngine !== 'browser' && numberInput('speed', 'السرعة', '1', '1')}
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm">
                            <h3 className="font-black text-gray-900 mb-3">مكان وحجم الباركود والرقم</h3>
                            {shouldShowBarcode && (
                                <div className="grid grid-cols-2 gap-3">
                                    {numberInput('barcodeXmm', 'باركود X')}
                                    {numberInput('barcodeYmm', 'باركود Y')}
                                    {numberInput('barcodeWidthMm', 'عرض الباركود')}
                                    {numberInput('barcodeHeightMm', 'ارتفاع الباركود')}
                                    {settings.printEngine !== 'browser' && numberInput('barcodeNarrow', 'Narrow', '1', '1')}
                                    {settings.printEngine !== 'browser' && numberInput('barcodeWide', 'Wide', '1', '2')}
                                </div>
                            )}
                            {settings.outputMode !== 'barcode_only' && (
                                <>
                                    <label className="mt-4 flex items-center gap-2 text-sm font-bold text-gray-700">
                                        <input type="checkbox" checked={!!settings.showText} onChange={(e) => updateSetting('showText', e.target.checked)} />
                                        إظهار الرقم/النص على الملصق
                                    </label>
                                    {settings.showText && (
                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                            {numberInput('textXmm', 'الرقم X')}
                                            {numberInput('textYmm', 'الرقم Y')}
                                            {numberInput('textWidthMm', 'عرض الرقم')}
                                            {numberInput('textHeightMm', 'ارتفاع الرقم')}
                                            {numberInput('textFontSize', 'حجم الخط', '1', '8')}
                                            <label className="flex items-end gap-2 text-sm font-bold text-gray-700 pb-2">
                                                <input type="checkbox" checked={!!settings.textBold} onChange={(e) => updateSetting('textBold', e.target.checked)} />
                                                خط عريض
                                            </label>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white border-t border-gray-200 px-5 py-4 flex flex-col sm:flex-row gap-3 justify-between">
                    <div className="text-xs text-gray-500 flex items-center leading-6">
                        {isLoadingSettings ? 'جاري تحميل الإعدادات المحفوظة...' : (
                            <span>زر التنفيذ داخل الجدول يستخدم آخر إعداد محفوظ في قاعدة البيانات: <b>{activeProfileLabel}</b> — <b>{outputModeLabel}</b> — <b>{printEngineLabel}</b>.</span>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50">إغلاق</button>
                        {canSaveGlobalSettings && (
                            <button type="button" onClick={() => saveSettingsGlobally()} disabled={isSavingSettings} className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black flex items-center justify-center gap-2 disabled:opacity-60">
                                <Save size={18} />
                                {isSavingSettings ? 'جاري الحفظ...' : 'حفظ الإعداد النشط لكل المستخدمين'}
                            </button>
                        )}
                        <button type="button" onClick={handlePrint} disabled={isPrinting || !code} className="px-6 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-black flex items-center justify-center gap-2 disabled:opacity-60">
                            <Printer size={18} />
                            {isPrinting ? 'جاري الطباعة...' : 'تجربة الطباعة'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PeriodicFollowUp = () => {
    const navigate = useNavigate();

       const { hasPermission, loading: permissionsLoading } = usePermissions();
    
    // تعريف جميع الصلاحيات
    const canAccessPage = hasPermission('التأشيرات', 'المتابعة الدورية');
    const canViewUnifiedList       = hasPermission('المتابعة الدورية', 'عرض القائمة الموحدة');
    const canViewUnifiedListButton  = hasPermission('المتابعة الدورية', 'عرض زر القائمة الموحدة');
    const canViewUnifiedListBanner  = hasPermission('المتابعة الدورية', 'عرض شريط القائمة الموحدة');
    const canViewStats = hasPermission('المتابعة الدورية', 'عرض الإحصائيات');
    const canViewActivityLog = hasPermission('المتابعة الدورية', 'عرض زر سجل المتابعة الدورية');
    const canEnterAppNumber   = hasPermission('المتابعة الدورية', 'إدخال رقم الطلب');
    const canEnterPhone       = hasPermission('المتابعة الدورية', 'تعديل رقم الهاتف');
    const canEnterMedical     = hasPermission('المتابعة الدورية', 'إدخال تاريخ الكشف الطبي');
const canEnterFingerprint = hasPermission('المتابعة الدورية', 'إدخال تاريخ البصمة');
    const canEnterInsurance   = hasPermission('المتابعة الدورية', 'إدخال تاريخ التأمين الطبي');
    const canEnterDocuments   = hasPermission('المتابعة الدورية', 'إدخال تسليم الأوراق');
    const canEnterConsulate   = hasPermission('المتابعة الدورية', 'إدخال دخول القنصلية');
    const canEnterVisa        = hasPermission('المتابعة الدورية', 'إدخال التأشيرة');
    const canEnterTravel      = hasPermission('المتابعة الدورية', 'إدخال السفر');
    const canEnterNotes       = hasPermission('المتابعة الدورية', 'تعديل الملاحظات');
    const canEnterBorderNumber = hasPermission('المتابعة الدورية', 'إدخال رقم الحدود');
    const canDelete = hasPermission('المتابعة الدورية', 'الحذف');
    const canBulkUpdate = hasPermission('المتابعة الدورية', 'التحديث الجماعي');
    const canPrintApplicationBarcodeExecute = hasPermission('باركود المتابعة الدورية', 'طباعة تنفيذ باركود رقم الطلب');
    const canOpenApplicationBarcodeSettings = hasPermission('باركود المتابعة الدورية', 'عرض زر ضبط الباركود');
    const canSaveNamedApplicationBarcodeProfile = hasPermission('باركود المتابعة الدورية', 'حفظ باسم وجعله النشط');
    const canDeleteApplicationBarcodeProfile = hasPermission('باركود المتابعة الدورية', 'حذف إعداد باركود محفوظ');
    const canSaveApplicationBarcodeSettingsGlobally = hasPermission('باركود المتابعة الدورية', 'حفظ إعداد الباركود لكل المستخدمين');
    const canPrintSelectedApplicationBarcodes = hasPermission('باركود المتابعة الدورية', 'طباعة باركود المحدد');
    const canSendToDelegate = hasPermission('المتابعة الدورية', 'إرسال للمناديب');
    const canToggleVisibility = hasPermission('المتابعة الدورية', 'إخفاء وإظهار القوائم');
    const canShowColAppNumber   = hasPermission('المتابعة الدورية', 'عرض عمود رقم الطلب');
    const canShowColPhone       = hasPermission('المتابعة الدورية', 'عرض عمود الهاتف');
    const canShowColMedical     = hasPermission('المتابعة الدورية', 'عرض عمود الكشف الطبي');
const canShowColFingerprint = hasPermission('المتابعة الدورية', 'عرض عمود البصمة');
    const canShowColInsurance   = hasPermission('المتابعة الدورية', 'عرض عمود التأمين الطبي');
    const canShowColDocuments   = hasPermission('المتابعة الدورية', 'عرض عمود تسليم الأوراق');
    const canShowColConsulate   = hasPermission('المتابعة الدورية', 'عرض عمود دخول القنصلية');
    const canShowColVisa        = hasPermission('المتابعة الدورية', 'عرض عمود التأشيرة');
    const canShowColTravel      = hasPermission('المتابعة الدورية', 'عرض عمود السفر');
    const canShowColNotes       = hasPermission('المتابعة الدورية', 'عرض عمود الملاحظات');
    const canShowColBorderNumber = hasPermission('المتابعة الدورية', 'عرض عمود رقم الحدود');
    const canShowColNetDate     = hasPermission('المتابعة الدورية', 'عرض عمود تاريخ النت');
    // ✅ صلاحيات الإحصائيات التفصيلية
    const canViewRequiredCount = hasPermission('إحصائيات المتابعة الدورية', 'عرض العدد المطلوب');
    const canViewTotalCount = hasPermission('إحصائيات المتابعة الدورية', 'عرض إجمالي الحضور');
    const canViewRemainingCount = hasPermission('إحصائيات المتابعة الدورية', 'عرض العدد المتبقي');
    const canViewMedicalPassed = hasPermission('إحصائيات المتابعة الدورية', 'عرض اللائق');
    const canViewFingerprintCount = hasPermission('إحصائيات المتابعة الدورية', 'عرض البصمة');
    const canViewVisaCount = hasPermission('إحصائيات المتابعة الدورية', 'عرض التأشيرة');
    const canViewTraveledCount = hasPermission('إحصائيات المتابعة الدورية', 'عرض سافروا');

    
    const [delegateFilter, setDelegateFilter] = useState('all');


// ✅ إضافة خيار إظهار/إخفاء التجنيد في delegateMessage
const [delegateMessage, setDelegateMessage] = useState({
    customMessage: 'السلام عليكم ورحمة الله وبركاته\nنرجو منكم إبلاغ العمال المذكورين أدناه بموعد الحضور للكشف للإجراءات المطلوبة.\nيرجى التأكد من حضور العمال الموكدين والتواصل مع العمال في الانتظار لتأكيد حضورهم.\nمع تحيات إدارة الشركة',
    appointmentDate: '',
    appointmentTime: '09:00',
    appointmentType: 'medical',
    showMilitary: false // ✅ إضافة خيار إظهار التجنيد
});
// ✅ قوالب الرسائل حسب النوع (5 خيارات فقط)
const messageTemplates = {
    medical: `السلام عليكم ورحمة الله وبركاته
نرجو منكم إبلاغ المذكورين أدناه بموعد الحضور للكشف للإجراءات المطلوبة.
مع التنبيه على التكاليف كاملة
مع تحيات إدارة الشركة`,

    fingerprint: `السلام عليكم ورحمة الله وبركاته
نرجو منكم إبلاغ المذكورين أدناه بموعد الحضور لعمل البصمة في الموعد المحدد.
مع تحيات إدارة الشركة`,

    visa: `السلام عليكم ورحمة الله وبركاته
نرجو منكم إبلاغ المذكورين أدناه بالاستعداد للسفر فى اى وقت من الان .
مع التنبيه عليهم بعمل اذن السفر لمن هم فى سن التجنيد.
مع تحيات إدارة الشركة`,

    travel: `السلام عليكم ورحمة الله وبركاته
نرجو منكم إبلاغ المذكورين أدناه بموعد الحضور للسفر في الموعد المحدد.
يرجى التأكد من إحضار اذن السفر لمن هم فى سن التجنيد .
مع العلم ان الوزن 30 ك + 7 ك
مع تحيات إدارة الشركة`,

    documents: `السلام عليكم ورحمة الله وبركاته
نرجو منكم إبلاغ المذكورين أدناه بضرورة احضار الاوراق المطلوبة .
وهي
- الإقرار ( إثبات تاريخ بالشهر العقارى )
- فيش جنائى موجه للعمل للسعودية موثق من الخارجية المصرية
- صورة البطاقة
- أصل الرخصة سارية وليس تصريح
- أصل المؤهل ( محو أمية او إبتدائى او إعدادى)
- إيصال المعامل و إيصال البصمة
- إذن السفر لمن هم فى سن التجنيد ، أقل من 30 سنة
مع تحيات إدارة الشركة`
};

// ✅ تحديث الأعمدة حسب النوع مع التحكم في التجنيد
const getColumnsForAppointmentType = (type, showMilitary = false) => {
    const baseColumns = ['م', 'الاسم', 'الشركة', 'رقم الهاتف'];
    
    // إضافة التجنيد إذا كان مفعل
    if (showMilitary) {
        baseColumns.push('التجنيد');
    }
    
    // إضافة المحافظة
    baseColumns.push('المحافظة');
    
    switch(type) {
        case 'medical':
            baseColumns.splice(-1, 0, 'حالة الكشف الطبي'); // إدراج قبل المحافظة
            break;
        case 'documents':
            baseColumns.splice(-1, 0, 'حالة تسليم الأوراق');
            break;
        case 'fingerprint':
            baseColumns.splice(-1, 0, 'حالة البصمة');
            break;
        case 'visa':
            // لا نضيف عمود إضافي للتأشيرة
            break;
        case 'travel':
            // لا نضيف عمود إضافي للسفر
            break;
    }
    
    // إضافة الملاحظات في النهاية
    baseColumns.push('ملاحظات');
    
    return baseColumns;
};

const [delegatesData, setDelegatesData] = useState([]);
const [loadingDelegatesData, setLoadingDelegatesData] = useState(false);
const [selectedDelegateFilter, setSelectedDelegateFilter] = useState('');
const [showDateFilters, setShowDateFilters] = useState(false);
const [isMobileView, setIsMobileView] = useState(false);

    // 🎯 State Management
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState(null);
    // 🌟 State للعرض الموحد
const [allPersonsView, setAllPersonsView] = useState(false);
const [allPersons, setAllPersons] = useState([]);
const [allPersonsStats, setAllPersonsStats] = useState({});
const [loadingAllPersons, setLoadingAllPersons] = useState(false);
    const [followUps, setFollowUps] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [listStatistics, setListStatistics] = useState({}); // ✅ إحصائيات خاصة بكل قائمة
    const [isLoading, setIsLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    // 🆕 نظام التحديث المحسن
const [pendingChanges, setPendingChanges] = useState({}); // التغييرات المعلقة
const [selectedPersons, setSelectedPersons] = useState([]); // الأشخاص المحددين
const [bulkUpdateMode, setBulkUpdateMode] = useState(false); // وضع التحديث الجماعي
const [showBulkModal, setShowBulkModal] = useState(false); // نافذة التحديث الجماعي
const [isSaving, setIsSaving] = useState(false); // حالة الحفظ
    
const [dateFilters, setDateFilters] = useState({
    netDate: { enabled: false, startDate: '', endDate: '' },
    medicalDate: { enabled: false, startDate: '', endDate: '' },
    fingerprintDate: { enabled: false, startDate: '', endDate: '' },
    consulateDate: { enabled: false, startDate: '', endDate: '' },
    visaDate: { enabled: false, startDate: '', endDate: '' },
    travelDate: { enabled: false, startDate: '', endDate: '' },
    insuranceDate: { enabled: false, startDate: '', endDate: '' }
});

useEffect(() => {
    const updateMobileView = () => {
        setIsMobileView(window.innerWidth <= 768);
    };
    updateMobileView();
    window.addEventListener('resize', updateMobileView);
    return () => window.removeEventListener('resize', updateMobileView);
}, []);


const [showImagePreview, setShowImagePreview] = useState(false);
const [previewImageData, setPreviewImageData] = useState(null);

// ✈️ رفع ملف التأشيرة من صفحة المتابعة الدورية
const [uploadingVisaFiles, setUploadingVisaFiles] = useState({});

// 🕘 سجل حركات المتابعة الدورية
const [showActivityLog, setShowActivityLog] = useState(false);
const [activityLogEntries, setActivityLogEntries] = useState([]);
const [activityLogUsers, setActivityLogUsers] = useState([]);
const [activityLogLoading, setActivityLogLoading] = useState(false);
const [activityLogCleanupSettings, setActivityLogCleanupSettings] = useState({
    cleanupMode: 'off',
    retentionDays: null,
    lastCleanupAt: null
});
const [activityLogFilters, setActivityLogFilters] = useState({
    userId: '',
    date: '',
    actionType: '',
    search: ''
});

// 🏷️ باركود رقم الطلب في المتابعة الدورية
const [showApplicationBarcodeModal, setShowApplicationBarcodeModal] = useState(false);
const [applicationBarcodePerson, setApplicationBarcodePerson] = useState(null);

const getCurrentUserId = () => {
    try {
        const userData = localStorage.getItem('user');
        if (!userData) return null;
        const parsed = JSON.parse(userData);
        return parsed?.UserId || parsed?.UserID || parsed?.userId || null;
    } catch {
        return null;
    }
};

const getPassportApiBaseUrl = () => config.PASSPORT_API_URL || config.API_BASE_URL;

// نفس طريقة بناء رابط الملفات المستخدمة في صفحة الجوازات
const buildPassportFileUrl = (filePath) => {
    if (!filePath || typeof filePath !== 'string' || filePath.trim() === '') return null;

    try {
        const cleanPath = filePath.replace(/\\/g, '/').trim();

        if (cleanPath.includes('/')) {
            const parts = cleanPath.split('/').filter(Boolean);
            if (parts.length >= 4) {
                const [year, month, folder, ...rest] = parts;
                const filename = rest.join('/');
                const typeMap = {
                    passport_images: 'passport',
                    personal_images: 'personal',
                    license_images: 'license',
                    license_back_images: 'license_back_images',
                    id_images: 'id',
                    id_back_images: 'id_back_images',
                    company_contracts: 'company_contracts',
                    ministry_contracts: 'ministry_contracts',
                    visa_files: 'visa_files',
                    other_attachments: 'other_attachments'
                };
                const type = typeMap[folder] || folder;
                return `${getPassportApiBaseUrl()}/api/passports/images/${year}/${month}/${type}/${filename}`;
            }
        }

        return `${getPassportApiBaseUrl()}/api/passports/images/visa_files/${cleanPath}`;
    } catch (error) {
        console.error('خطأ في بناء رابط ملف التأشيرة:', error);
        return null;
    }
};


const buildCustomVisaDownloadUrl = (filePath, personName) => {
    const fileUrl = buildPassportFileUrl(filePath);

    if (!fileUrl) return null;

    const safePersonName = String(personName || 'بدون اسم')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const downloadName = `${safePersonName || 'بدون اسم'}-تأشيرة`;

    return fileUrl.replace('/api/passports/images/', '/api/passports/download/')
        + `?name=${encodeURIComponent(downloadName)}`;
};

const updateVisaFilePathLocally = (person, visaFilePath) => {
    const matcher = (item) =>
        item.FollowUpID === person.FollowUpID ||
        (person.PassportID && item.PassportID === person.PassportID);

    const updater = (item) => matcher(item) ? { ...item, VisaFilePath: visaFilePath } : item;

    setFollowUps(prev => prev.map(updater));
    setSearchResults(prev => prev.map(updater));
    setAllPersons(prev => prev.map(updater));
};

const handleVisaFileUpload = async (person, file) => {
    if (!file) return;

    if (!person?.PassportID) {
        showAlert('لا يمكن رفع التأشيرة: لا يوجد PassportID لهذا الشخص', 'error');
        return;
    }

    const isValidType = file.type?.startsWith('image/') || file.type === 'application/pdf';
    if (!isValidType) {
        showAlert('صيغة الملف غير مسموحة. المسموح: صورة أو PDF فقط', 'error');
        return;
    }

    if (file.size > 5 * 1024 * 1024) {
        showAlert('حجم الملف أكبر من 5MB', 'error');
        return;
    }

    const uploadKey = person.FollowUpID || person.PassportID;

    try {
        setUploadingVisaFiles(prev => ({ ...prev, [uploadKey]: true }));
        showAlert('جاري رفع ملف التأشيرة...', 'info', 1500);

        const payload = new FormData();
        payload.append('VisaFile', file);
        payload.append('passportId', person.PassportID);
        payload.append('UploadedByUserID', getCurrentUserId() || '');

        const response = await fetch(`${getPassportApiBaseUrl()}/api/passports/upload-images`, {
            method: 'POST',
            body: payload
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
            throw new Error(result.error || `فشل رفع التأشيرة - كود ${response.status}`);
        }

        const visaFilePath = result.imagePaths?.VisaFilePath;
        if (!visaFilePath) {
            throw new Error('تم الرفع لكن السيرفر لم يرجع مسار ملف التأشيرة');
        }

        updateVisaFilePathLocally(person, visaFilePath);
        showAlert(`✅ تم رفع التأشيرة لـ ${person.NameArabic}`, 'success');
    } catch (error) {
        console.error('خطأ في رفع ملف التأشيرة من المتابعة الدورية:', error);
        showAlert('حدث خطأ أثناء رفع التأشيرة: ' + error.message, 'error');
    } finally {
        setUploadingVisaFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
};


// ✅ إضافة state جديد للفلترة والصفحات
const [listsFilter, setListsFilter] = useState({
    page: 1,
    limit: 12,
    search: '',
    showHidden: false, // ✅ خلاص يبدأ بالقوائم الظاهرة
    sortBy: 'recent'
});

const [listsStats, setListsStats] = useState({
    TotalLists: 0,
    VisibleLists: 0,
    HiddenLists: 0,
    VisiblePersons: 0,
    HiddenPersons: 0
});
const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 12
});

    // أضف ده مع باقي الـ states
const [showPrintModal, setShowPrintModal] = useState(false);
// استبدل printOptions بده
const [printOptions, setPrintOptions] = useState({
    includeFields: {
      // البيانات الأساسية
      serialNumber: true,
      name: true,
      company: true,
      profession: true,
      passport: false,
      phone: true,
      delegate: true,
      military: false,
      netDate: false,
            listName: false, // ✅ جديد: اسم القائمة الأصلية
            governorate: true,

      // البيانات الطبية والإجرائية
      medical: false,
      fingerprint: false,
      consulate: false,
      visa: false,
      travel: false,
      documents: false,
      stage: false,
      notes: false
    },
    filters: {
      medicalPending: false,
      medicalPassed: false,
      medicalFailed: false,
      fingerprintPending: false,
      fingerprintDone: false,
      visaPending: false,
      visaDone: false,
      travelReady: false,
      traveled: false,
      documentsNotDelivered: false,
      documentsDelivered: false
    },
    dateFilters: {
      netDate: {
        enabled: false,
        startDate: '',
        endDate: ''
      },
      medicalDate: {
        enabled: false,
        startDate: '',
        endDate: ''
      },
      fingerprintDate: {
        enabled: false,
        startDate: '',
        endDate: ''
      },
      consulateDate: {
        enabled: false,
        startDate: '',
        endDate: ''
      },
      visaDate: {
        enabled: false,
        startDate: '',
        endDate: ''
      },
      travelDate: {
        enabled: false,
        startDate: '',
        endDate: ''
      }
    }
  });



    // 🔍 Search & Filter
    const [searchTerm, setSearchTerm] = useState('');
    const [listSearchTerm, setListSearchTerm] = useState(''); // ✅ بحث داخل القائمة
    const [stageFilter, setStageFilter] = useState('all');
    const [listSortBy, setListSortBy] = useState('addedDate'); // ترتيب البيانات: addedDate | name
    const [listSortDir, setListSortDir] = useState('asc');     // اتجاه الترتيب: asc | desc
    // ⏰ فلتر وقت الرحلة داخل اليوم
const [selectedTravelTime, setSelectedTravelTime] = useState('all'); // 'all' = كل الرحلات

    // 📊 View Mode
    const [activeTab, setActiveTab] = useState('lists');
    const [showImageExportModal, setShowImageExportModal] = useState(false);

    // ⚙️ إعدادات تصدير الصورة المتقدمة
    const [imageExportAdvanced, setImageExportAdvanced] = useState({
        showHeader: true,          // إظهار/إخفاء الجزء العلوي (إجمالي + الصفحة)
        showFooter: true,          // إظهار/إخفاء الجزء السفلي (نظام المتابعة)
        customTitle: '',           // عنوان مخصص يظهر في الصورة
        maxRowsPerImage: 25,       // عدد الصفوف الظاهرة في كل صورة
        colorTheme: 'blue',        // ثيم اللون
    });

    // 🎨 ثيمات الألوان المتاحة (12 لون)
    const colorThemes = {
        blue: {
            label: 'أزرق سماوي',
            dot: '#1976d2',
            bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            headerBg: 'linear-gradient(135deg, #1976d2, #42a5f5)',
            headerBorder: '#0d47a1',
            cardBg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            cardBorder: '#1976d2',
            titleColor: '#0d47a1',
            rowOdd: '#ffffff',
            rowEven: '#e3f2fd',
            cellBorder: '#bbdefb',
            footerBg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            footerBorder: '#1976d2',
        },
        navyBlue: {
            label: 'أزرق داكن',
            dot: '#1a237e',
            bg: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
            headerBg: 'linear-gradient(135deg, #1a237e, #3949ab)',
            headerBorder: '#0d1b6e',
            cardBg: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
            cardBorder: '#1a237e',
            titleColor: '#0d1b6e',
            rowOdd: '#ffffff',
            rowEven: '#e8eaf6',
            cellBorder: '#c5cae9',
            footerBg: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
            footerBorder: '#1a237e',
        },
        green: {
            label: 'أخضر زمردي',
            dot: '#2e7d32',
            bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            headerBg: 'linear-gradient(135deg, #2e7d32, #66bb6a)',
            headerBorder: '#1b5e20',
            cardBg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
            cardBorder: '#2e7d32',
            titleColor: '#1b5e20',
            rowOdd: '#ffffff',
            rowEven: '#e8f5e9',
            cellBorder: '#c8e6c9',
            footerBg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
            footerBorder: '#2e7d32',
        },
        teal: {
            label: 'تيل فيروزي',
            dot: '#00695c',
            bg: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
            headerBg: 'linear-gradient(135deg, #00695c, #26a69a)',
            headerBorder: '#004d40',
            cardBg: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
            cardBorder: '#00695c',
            titleColor: '#004d40',
            rowOdd: '#ffffff',
            rowEven: '#e0f2f1',
            cellBorder: '#b2dfdb',
            footerBg: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
            footerBorder: '#00695c',
        },
        purple: {
            label: 'بنفسجي',
            dot: '#6a1b9a',
            bg: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
            headerBg: 'linear-gradient(135deg, #6a1b9a, #ab47bc)',
            headerBorder: '#4a148c',
            cardBg: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            cardBorder: '#6a1b9a',
            titleColor: '#4a148c',
            rowOdd: '#ffffff',
            rowEven: '#f3e5f5',
            cellBorder: '#e1bee7',
            footerBg: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            footerBorder: '#6a1b9a',
        },
        indigo: {
            label: 'نيلي أنيق',
            dot: '#3730a3',
            bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
            headerBg: 'linear-gradient(135deg, #3730a3, #6366f1)',
            headerBorder: '#312e81',
            cardBg: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
            cardBorder: '#3730a3',
            titleColor: '#312e81',
            rowOdd: '#ffffff',
            rowEven: '#eef2ff',
            cellBorder: '#c7d2fe',
            footerBg: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
            footerBorder: '#3730a3',
        },
        orange: {
            label: 'برتقالي دافئ',
            dot: '#e65100',
            bg: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            headerBg: 'linear-gradient(135deg, #e65100, #fb8c00)',
            headerBorder: '#bf360c',
            cardBg: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            cardBorder: '#e65100',
            titleColor: '#bf360c',
            rowOdd: '#ffffff',
            rowEven: '#fff3e0',
            cellBorder: '#ffe0b2',
            footerBg: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            footerBorder: '#e65100',
        },
        red: {
            label: 'أحمر رسمي',
            dot: '#c62828',
            bg: 'linear-gradient(135deg, #fff5f5 0%, #fee2e2 100%)',
            headerBg: 'linear-gradient(135deg, #c62828, #ef5350)',
            headerBorder: '#b71c1c',
            cardBg: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
            cardBorder: '#c62828',
            titleColor: '#b71c1c',
            rowOdd: '#ffffff',
            rowEven: '#ffebee',
            cellBorder: '#ffcdd2',
            footerBg: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
            footerBorder: '#c62828',
        },
        rose: {
            label: 'وردي ناعم',
            dot: '#be185d',
            bg: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
            headerBg: 'linear-gradient(135deg, #be185d, #f43f5e)',
            headerBorder: '#9d174d',
            cardBg: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
            cardBorder: '#be185d',
            titleColor: '#9d174d',
            rowOdd: '#ffffff',
            rowEven: '#fce7f3',
            cellBorder: '#fbcfe8',
            footerBg: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
            footerBorder: '#be185d',
        },
        brown: {
            label: 'بني كلاسيك',
            dot: '#4e342e',
            bg: 'linear-gradient(135deg, #fdf6f0 0%, #fbe8d8 100%)',
            headerBg: 'linear-gradient(135deg, #4e342e, #8d6e63)',
            headerBorder: '#3e2723',
            cardBg: 'linear-gradient(135deg, #efebe9 0%, #d7ccc8 100%)',
            cardBorder: '#4e342e',
            titleColor: '#3e2723',
            rowOdd: '#ffffff',
            rowEven: '#efebe9',
            cellBorder: '#d7ccc8',
            footerBg: 'linear-gradient(135deg, #efebe9 0%, #d7ccc8 100%)',
            footerBorder: '#4e342e',
        },
        gray: {
            label: 'رمادي رسمي',
            dot: '#374151',
            bg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            headerBg: 'linear-gradient(135deg, #374151, #6b7280)',
            headerBorder: '#1f2937',
            cardBg: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
            cardBorder: '#374151',
            titleColor: '#1f2937',
            rowOdd: '#ffffff',
            rowEven: '#f3f4f6',
            cellBorder: '#e5e7eb',
            footerBg: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
            footerBorder: '#374151',
        },
        darkGreen: {
            label: 'أخضر داكن',
            dot: '#1a3c2a',
            bg: 'linear-gradient(135deg, #f0f7f4 0%, #d4eae0 100%)',
            headerBg: 'linear-gradient(135deg, #1a3c2a, #2d6a4f)',
            headerBorder: '#0f2e1d',
            cardBg: 'linear-gradient(135deg, #d8f3dc 0%, #b7e4c7 100%)',
            cardBorder: '#1a3c2a',
            titleColor: '#0f2e1d',
            rowOdd: '#ffffff',
            rowEven: '#d8f3dc',
            cellBorder: '#b7e4c7',
            footerBg: 'linear-gradient(135deg, #d8f3dc 0%, #b7e4c7 100%)',
            footerBorder: '#1a3c2a',
        },
    };

    // 📋 Export Settings
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportSettings, setExportSettings] = useState({
        format: 'excel', // excel, pdf
        filters: {
            medicalPending: false,
            medicalPassed: false,
            medicalFailed: false,
            fingerprintPending: false,
            fingerprintDone: false,
            visaPending: false,
            visaDone: false,
            travelReady: false,
            traveled: false,
            documentsNotDelivered: false,
            documentsDelivered: false
        },
       includeFields: {
    personalInfo: true,
    applicationNumber: false,
    phoneNumbers: false,
    medicalInfo: true,
    fingerprintInfo: true,
    consulateInfo: true,
    visaInfo: true,
    travelInfo: true,
    documentsInfo: true,
    notes: true,
    listName: true
  }

    });

    // 🚨 Enhanced Alert System
    const showAlert = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now();
        setAlerts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setAlerts(prev => prev.filter(alert => alert.id !== id));
        }, duration);
    }, []);

    const fetchActivityLogUsers = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/users`);
            const data = await response.json();
            setActivityLogUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('خطأ في جلب مستخدمي سجل المتابعة:', error);
        }
    };

    const fetchActivityLog = async (filters = activityLogFilters) => {
        try {
            setActivityLogLoading(true);
            const params = new URLSearchParams();
            params.append('limit', '300');
            if (filters.userId) params.append('userId', filters.userId);
            if (filters.date) params.append('date', filters.date);
            if (filters.actionType) params.append('actionType', filters.actionType);
            if (filters.search) params.append('search', filters.search.trim());

            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/activity-log?${params.toString()}`);
            const data = await response.json();
            if (data.success) {
                setActivityLogEntries(data.logs || []);
            } else {
                throw new Error(data.error || 'فشل في تحميل السجل');
            }
        } catch (error) {
            console.error('خطأ في تحميل سجل المتابعة الدورية:', error);
            showAlert('خطأ في تحميل سجل المتابعة الدورية', 'error');
        } finally {
            setActivityLogLoading(false);
        }
    };


    const fetchActivityLogCleanupSettings = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/activity-log/cleanup-settings`);
            const data = await response.json();
            if (response.ok && data.success) {
                setActivityLogCleanupSettings(data.data || { cleanupMode: 'off', retentionDays: null, lastCleanupAt: null });
            }
        } catch (error) {
            console.error('خطأ في جلب إعدادات تنظيف سجل المتابعة:', error);
        }
    };

    const saveActivityLogCleanupSettings = async (cleanupMode) => {
        try {
            setActivityLogLoading(true);
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/activity-log/cleanup-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cleanupMode, UpdatedByUserID: getCurrentUserId() })
            });
            const data = await response.json();
            if (!response.ok || !data.success) throw new Error(data.error || 'فشل في حفظ إعدادات تنظيف السجل');
            showAlert('تم حفظ إعدادات تنظيف سجل المتابعة بنجاح', 'success');
            await fetchActivityLogCleanupSettings();
            await fetchActivityLog(activityLogFilters);
        } catch (error) {
            console.error('خطأ في حفظ إعدادات تنظيف سجل المتابعة:', error);
            showAlert('حدث خطأ في حفظ إعدادات تنظيف السجل: ' + error.message, 'error');
        } finally {
            setActivityLogLoading(false);
        }
    };

    const manualCleanupActivityLog = async (deleteMode) => {
        try {
            setActivityLogLoading(true);
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/activity-log/cleanup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deleteMode, DeletedByUserID: getCurrentUserId() })
            });
            const data = await response.json();
            if (!response.ok || !data.success) throw new Error(data.error || 'فشل في حذف السجل');
            showAlert(`تم حذف ${data.deletedCount || 0} حركة من سجل المتابعة`, 'success');
            await fetchActivityLogCleanupSettings();
            await fetchActivityLog(activityLogFilters);
        } catch (error) {
            console.error('خطأ في حذف سجل المتابعة الدورية:', error);
            showAlert('حدث خطأ أثناء حذف السجل: ' + error.message, 'error');
        } finally {
            setActivityLogLoading(false);
        }
    };

    useEffect(() => {
        if (showActivityLog) {
            fetchActivityLogUsers();
            fetchActivityLog(activityLogFilters);
            fetchActivityLogCleanupSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showActivityLog]);

    useEffect(() => {
        if (!showActivityLog) return;
        const timer = setTimeout(() => {
            fetchActivityLog(activityLogFilters);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityLogFilters, showActivityLog]);


    // 🆕 نظام إدارة التغييرات المعلقة
const addPendingChange = (followUpId, field, value) => {
    setPendingChanges(prev => ({
        ...prev,
        [followUpId]: {
            ...prev[followUpId],
            [field]: value
        }
    }));
    
    // تحديث البيانات محلياً للعرض المباشر
    if (selectedList) {
        setFollowUps(prevFollowUps => 
            prevFollowUps.map(person => 
                person.FollowUpID === followUpId 
                    ? { ...person, [field]: value }
                    : person
            )
        );
    }
    
    if (searchResults.length > 0) {
        setSearchResults(prevResults => 
            prevResults.map(person => 
                person.FollowUpID === followUpId 
                    ? { ...person, [field]: value }
                    : person
            )
        );
    }
};

// 🔄 حفظ جميع التغييرات المعلقة
const saveAllPendingChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
        showAlert('لا توجد تغييرات للحفظ', 'info');
        return;
    }

    setIsSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
        for (const [followUpId, changes] of Object.entries(pendingChanges)) {
            for (const [field, value] of Object.entries(changes)) {
                try {
                    const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/update-field/${followUpId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ field, value, UpdatedByUserID: getCurrentUserId() })
                    });

                    if (response.ok) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch {
                    errorCount++;
                }
            }
        }

        if (successCount > 0) {
            showAlert(`✅ تم حفظ ${successCount} تحديث بنجاح`, 'success');
            setPendingChanges({});
            await fetchStatistics();
            if (selectedList) {
                await fetchListStatistics(selectedList.ListID);
            }
        }

        if (errorCount > 0) {
            showAlert(`❌ فشل في حفظ ${errorCount} تحديث`, 'error');
        }

    } catch (error) {
        console.error('خطأ في حفظ التغييرات:', error);
        showAlert('خطأ في عملية الحفظ', 'error');
    } finally {
        setIsSaving(false);
    }
};

// 🆕 نظام التحديد المتعدد
const togglePersonSelection = (followUpId) => {
    setSelectedPersons(prev => 
        prev.includes(followUpId)
            ? prev.filter(id => id !== followUpId)
            : [...prev, followUpId]
    );
};

const selectAllPersons = () => {
    const allIds = getFilteredFollowUps().map(person => person.FollowUpID);
    setSelectedPersons(allIds);
};

const clearSelection = () => {
    setSelectedPersons([]);
};

// 🆕 التحديث الجماعي
const applyBulkUpdate = async (field, value) => {
    if (selectedPersons.length === 0) {
        showAlert('يجب تحديد أشخاص أولاً', 'error');
        return;
    }

    // تحويل __CLEAR__ إلى قيمة فارغة لمسح التاريخ
    const actualValue = value === '__CLEAR__' ? '' : value;

    selectedPersons.forEach(followUpId => {
        addPendingChange(followUpId, field, actualValue);
    });

    const clearMsg = value === '__CLEAR__' ? '(مسح التاريخ)' : '';
    showAlert(`✅ تم إضافة التحديث ${clearMsg} لـ ${selectedPersons.length} شخص للحفظ`, 'success');
    setShowBulkModal(false);
};


const fetchListsStats = async () => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/lists/stats`);
        const data = await response.json();
        
        if (data.success) {
            setListsStats(data.stats || {
                TotalLists: 0,
                VisibleLists: 0,
                HiddenLists: 0,
                VisiblePersons: 0,
                HiddenPersons: 0
            });
        }
    } catch (error) {
        console.error('خطأ في جلب إحصائيات القوائم:', error);
        // حط قيم افتراضية في حالة الخطأ
        setListsStats({
            TotalLists: 0,
            VisibleLists: 0,
            HiddenLists: 0,
            VisiblePersons: 0,
            HiddenPersons: 0
        });
    }
};

// ✅ دالة إخفاء/إظهار القائمة
const toggleListVisibility = async (listId, isHidden, listName) => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/lists/${listId}/visibility`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isHidden: !isHidden, ChangedByUserID: getCurrentUserId() })
        });
        
        const data = await response.json();
        
        if (data.success) {
            showAlert(`✅ تم ${!isHidden ? 'إخفاء' : 'إظهار'} قائمة "${listName}" بنجاح`, 'success');
            await fetchLists();
            await fetchListsStats();
        } else {
            throw new Error(data.error || 'فشل في تحديث حالة العرض');
        }
    } catch (error) {
        console.error('خطأ في تحديث حالة العرض:', error);
        showAlert('خطأ في تحديث حالة عرض القائمة', 'error');
    }
};

// ✅ دالة تغيير الصفحة
const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
        const newFilters = { ...listsFilter, page: newPage };
        fetchLists(newFilters);
    }
};

// ✅ دالة تطبيق الفلاتر
const applyListsFilter = (newFilters) => {
    const updatedFilters = { ...listsFilter, ...newFilters, page: 1 };
    fetchLists(updatedFilters);
};

// 📊 تجميع إحصائيات القوائم الظاهرة فقط (اللي في الصفحة الحالية)
const visibleUnifiedStats = lists.reduce(
  (acc, list) => {
    return {
      TotalPersons:     (acc.TotalPersons     || 0) + (list.TotalCount           || 0),
      MedicalPassed:    (acc.MedicalPassed    || 0) + (list.MedicalPassedCount   || 0),
      FingerprintDone:  (acc.FingerprintDone  || 0) + (list.FingerprintDoneCount || 0),
      VisaIssued:       (acc.VisaIssued       || 0) + (list.VisaIssuedCount      || 0),
      Traveled:         (acc.Traveled         || 0) + (list.TraveledCount        || 0),
    };
  },
  {
    TotalPersons:    0,
    MedicalPassed:   0,
    FingerprintDone: 0,
    VisaIssued:      0,
    Traveled:        0,
  }
);




 useEffect(() => {
    fetchLists();
    fetchListsStats();
    fetchStatistics();
}, []);

const fetchLists = async (filters = listsFilter) => {
    try {
        setIsLoading(true);
        console.log('🔍 جاري جلب القوائم مع الفلاتر:', filters);
        
        const params = new URLSearchParams({
            page: filters.page.toString(),
            limit: filters.limit.toString(),
            search: filters.search || '',
            showHidden: filters.showHidden === 'all' ? 'all' : filters.showHidden.toString(),
            sortBy: filters.sortBy || 'recent'
        });
        
        const url = `${config.API_BASE_URL}/api/periodic-follow-up/lists?${params}`;
        console.log('📡 الرابط:', url);
        
        const response = await fetch(url);
        const data = await response.json();
        
        console.log('📦 البيانات المستلمة:', data);
        
        if (data.success) {
            console.log('✅ القوائم:', data.lists?.length || 0);
            console.log('📊 Pagination:', data.pagination);
            
            setLists(data.lists || []);
            setPagination(data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalCount: 0,
                hasNextPage: false,
                hasPrevPage: false,
                limit: filters.limit
            });
            
            if (JSON.stringify(filters) !== JSON.stringify(listsFilter)) {
                setListsFilter(filters);
            }
        } else {
            console.error('❌ خطأ في البيانات:', data.error);
            setLists([]);
            setPagination({
                currentPage: 1,
                totalPages: 1,
                totalCount: 0,
                hasNextPage: false,
                hasPrevPage: false,
                limit: filters.limit
            });
        }
    } catch (error) {
        console.error('💥 خطأ في الشبكة:', error);
        showAlert('خطأ في جلب قوائم المتابعة', 'error');
        setLists([]);
        setPagination({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            hasNextPage: false,
            hasPrevPage: false,
            limit: filters.limit
        });
    } finally {
        setIsLoading(false);
    }
};

    const fetchListDetails = async (listId) => {
        try {
            setIsLoading(true);
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/lists/${listId}`);
            const data = await response.json();
            
            if (data.success) {
                setFollowUps(data.followUps);
                const listInfo = lists.find(l => l.ListID === parseInt(listId));
               setSelectedList(listInfo);
setPendingChanges({}); // مسح التغييرات المعلقة
setSelectedPersons([]); // مسح الأشخاص المحددين
                
                // ✅ جلب إحصائيات القائمة المحددة
                await fetchListStatistics(listId);
            }
        } catch (error) {
            console.error('خطأ في جلب تفاصيل القائمة:', error);
            showAlert('خطأ في جلب تفاصيل القائمة', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ جلب إحصائيات قائمة محددة
    const fetchListStatistics = async (listId) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/lists/${listId}/statistics`);
            const data = await response.json();
            
            if (data.success) {
                setListStatistics(data.statistics);
            }
        } catch (error) {
            console.error('خطأ في جلب إحصائيات القائمة:', error);
        }
    };


// 🌟 جلب القائمة الموحدة (تعامل معاها كقائمة عادية)
const fetchAllPersons = async () => {
    try {
        setIsLoading(true);
        console.log('📊 جاري جلب القائمة الموحدة...');
        
        const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/all-persons`);
        const data = await response.json();
        
        if (data.success) {
            // ✅ خزنها في followUps بدل allPersons عشان تشتغل مع كل الدوال الموجودة
            setFollowUps(data.persons);
            
            // ✅ اعمل قائمة وهمية للعرض
            setSelectedList({
                ListID: 'unified',
                ListName: '📊 القائمة الموحدة لكل القوائم',
                TotalCount: data.persons.length,
                IsUnified: true // علامة مميزة
            });
            
            console.log(`✅ تم جلب ${data.persons.length} شخص من كل القوائم`);
            
            // جلب الإحصائيات
            await fetchAllPersonsStatistics();
        }
    } catch (error) {
        console.error('خطأ في جلب القائمة الموحدة:', error);
        showAlert('خطأ في جلب البيانات الموحدة', 'error');
    } finally {
        setIsLoading(false);
    }
};

// 🌟 جلب إحصائيات القائمة الموحدة
const fetchAllPersonsStatistics = async () => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/all-persons/statistics`);
        const data = await response.json();
        
        if (data.success) {
            setListStatistics(data.statistics); // ✅ استخدم نفس الـ state العادي
        }
    } catch (error) {
        console.error('خطأ في جلب إحصائيات القائمة الموحدة:', error);
    }
};


const fetchStatistics = async () => {
    try {
        // ✅ إضافة معامل showHidden=false للحصول على القوائم الظاهرة فقط
        const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/statistics?showHidden=false`);
        const data = await response.json();
        
        if (data.success) {
            setStatistics(data.statistics);
        }
    } catch (error) {
        console.error('خطأ في جلب الإحصائيات:', error);
    }
};


const fetchDelegatesData = async () => {
    if (!selectedList) return;
    
    setLoadingDelegatesData(true);
    try {
        // ✅ تعديل API call ليشمل المحافظة
        const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/lists/${selectedList.ListID}/delegates?includeGovernorate=true`);
        const data = await response.json();
        
        if (data.success) {
            setDelegatesData(data.delegates || []);
        }
    } catch (error) {
        console.error('خطأ في جلب بيانات المناديب:', error);
        showAlert('خطأ في جلب بيانات المناديب', 'error');
    } finally {
        setLoadingDelegatesData(false);
    }
};


const fetchDelegatesDataForFiltered = async (filteredData) => {
    if (!selectedList || !filteredData || filteredData.length === 0) {
        setDelegatesData([]);
        return;
    }
    
    setLoadingDelegatesData(true);
    try {
        // تجميع البيانات المفلترة حسب المندوب مع إضافة المحافظة
        const delegatesMap = {};
        
        filteredData.forEach(person => {
            const delegateName = person.DelegateName || 'مندوب غير معروف';
            
            if (!delegatesMap[delegateName]) {
                delegatesMap[delegateName] = {
                    delegateInfo: {
                        DelegateName: delegateName
                    },
                    clients: []
                };
            }
            
            // ✅ إضافة المحافظة للعميل
            delegatesMap[delegateName].clients.push({
                ...person,
                Governorate: person.Governorate || 'غير محدد' // من جدول Passports
            });
        });
        
        const delegatesArray = Object.values(delegatesMap);
        setDelegatesData(delegatesArray);
        
        console.log(`✅ تم تجميع ${delegatesArray.length} مندوب للبيانات المفلترة مع المحافظات`);
        
    } catch (error) {
        console.error('خطأ في تجميع بيانات المناديب المفلترة:', error);
        showAlert('خطأ في تجميع بيانات المناديب', 'error');
        setDelegatesData([]);
    } finally {
        setLoadingDelegatesData(false);
    }
};


const getFilteredClientsByDelegate = (clients) => {
    if (!clients) return [];
    
    switch (delegateFilter) {
        case 'traveled':
            return clients.filter(client => client.TravelDate);
        case 'not_traveled':
            return clients.filter(client => !client.TravelDate && client.VisaDate);
        case 'documents_delivered':
            return clients.filter(client => client.DocumentsDeliveryStatus === 'سلم');
        case 'documents_not_delivered':
            return clients.filter(client => !client.DocumentsDeliveryStatus || client.DocumentsDeliveryStatus === 'لم يسلم');
        case 'documents_partial':
            return clients.filter(client => client.DocumentsDeliveryStatus === 'سلم البعض وينقصه شيء');
        case 'medical_passed':
            return clients.filter(client => client.MedicalExamStatus === 'لائق');
        case 'medical_failed':
            return clients.filter(client => client.MedicalExamStatus === 'غير لائق');
        case 'visa_ready':
            return clients.filter(client => client.VisaDate);
        case 'visa_pending':
            return clients.filter(client => client.FingerprintDate && !client.VisaDate);
        case 'fingerprint_done':
            return clients.filter(client => client.FingerprintDate);
        case 'fingerprint_pending':
            return clients.filter(client => client.MedicalExamStatus === 'لائق' && !client.FingerprintDate);
        
        // ✅ الفلاتر العكسية الجديدة للمندوبين
        case 'exclude_medical_passed':
            return clients.filter(client => client.MedicalExamStatus !== 'لائق');
        case 'exclude_documents_delivered':
            return clients.filter(client => client.DocumentsDeliveryStatus !== 'سلم');
        case 'exclude_visa_done':
            return clients.filter(client => !client.VisaDate);
        case 'exclude_traveled':
            return clients.filter(client => !client.TravelDate);
        
        default:
            return clients;
    }
};



const filterTitles = {
    'all': 'جميع العملاء',
    'traveled': 'العملاء المسافرين',
    'not_traveled': 'العملاء الجاهزين للسفر',
    'documents_delivered': 'العملاء الذين سلموا الأوراق',
    'documents_not_delivered': 'العملاء الذين لم يسلموا الأوراق',
    'documents_partial': 'العملاء الذين سلموا البعض',
    'medical_passed': 'العملاء ذوي الكشف الطبي اللائق',
    'medical_failed': 'العملاء ذوي الكشف الطبي غير اللائق',
    'visa_ready': 'العملاء الذين صدرت تأشيرتهم',
    'visa_pending': 'العملاء في انتظار التأشيرة',
    'fingerprint_done': 'العملاء الذين تم بصمتهم',
    'fingerprint_pending': 'العملاء في انتظار البصمة',
    
    // ✅ العناوين الجديدة للفلاتر العكسية
    'exclude_medical_passed': 'الكل ماعدا اللائق طبياً',
    'exclude_documents_delivered': 'الكل ماعدا من سلم أوراقه',
    'exclude_visa_done': 'الكل ماعدا من صدرت تأشيرته', 
    'exclude_traveled': 'الكل ماعدا المسافرين'
};



// دالة مساعدة لعناوين أنواع المواعيد (5 خيارات فقط)
const getAppointmentTypeTitle = (type) => {
    const titles = {
        'medical': 'الكشف الطبي',
        'fingerprint': 'البصمة',
        'visa': 'التأشيرة',
        'travel': 'السفر',
        'documents': 'تسليم الأوراق'
    };
    return titles[type] || 'الكشف الطبي';
};

// ✅ دالة طباعة محدثة مع الأعمدة المخصصة
const printDelegateReport = (delegateInfo, filteredClients) => {
    const appointmentType = delegateMessage.appointmentType;
    const showMilitary = delegateMessage.showMilitary;
    const columns = getColumnsForAppointmentType(appointmentType, showMilitary);
    const appointmentDate = delegateMessage.appointmentDate;
    const appointmentTime = delegateMessage.appointmentTime;
    const customMessage = delegateMessage.customMessage;
    
    const printWindow = window.open('', '_blank');
    
    // بناء headers الجدول
    const tableHeaders = columns.map(col => `<th>${col}</th>`).join('');
    
    // ✅ بناء صفوف الجدول حسب النوع المختار
    const tableRows = filteredClients.map((client, index) => {
        let rowData = `<td class="row-number">${index + 1}</td>`;
        rowData += `<td class="name-cell">${client.NameArabic}</td>`;
        rowData += `<td class="company-cell">${client.CompanyName}</td>`;
        rowData += `<td style="text-align: right; vertical-align: top; padding: 8px;">
    ${(() => {
        const phones = getAllPhoneNumbers(client.PhoneNumbers);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px 4px; background: #f0f0f0; border-radius: 3px; font-size: 12px; border: 1px solid #ddd;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic; font-size: 12px;">غير محدد</div>';
    })()}
</td>`;
        
        // إضافة التجنيد إذا كان مفعل
        if (showMilitary) {
            rowData += `<td class="military-cell">${client.FinalMilitaryStatus || client.MilitaryStatus || 'غير محدد'}</td>`;
        }
        
        // إضافة العمود الخاص بالنوع
        switch(appointmentType) {
            case 'medical':
                rowData += `<td class="status-cell">${client.MedicalExamStatus || 'في الانتظار'}</td>`;
                break;
            case 'documents':
                rowData += `<td class="status-cell">${client.DocumentsDeliveryStatus || 'لم يسلم'}</td>`;
                break;
            case 'fingerprint':
                const fingerprintStatus = client.FingerprintDate ? 'تم عمل البصمة' : 
                                        (client.MedicalExamStatus === 'لائق' ? 'جاهز للبصمة' : 'في انتظار الطبي');
                rowData += `<td class="status-cell">${fingerprintStatus}</td>`;
                break;
            case 'visa':
            case 'travel':
                // لا نضيف عمود إضافي للتأشيرة والسفر
                break;
        }
        
        // إضافة المحافظة
        rowData += `<td class="governorate-cell">${client.Governorate || 'غير محدد'}</td>`;
        
        // إضافة الملاحظات
        rowData += `<td class="notes-cell">${client.Notes || '-'}</td>`;
        
        return `<tr>${rowData}</tr>`;
    }).join('');
    
// في دالة printDelegateReport، استبدل الـ CSS بهذا:

const htmlContent = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
    <meta charset="UTF-8">
    <title>قائمة العملاء - ${getAppointmentTypeTitle(appointmentType)}</title>
    <style>
        @page { size: A4 portrait; margin: 15mm; }
        
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body { 
            font-family: 'Arial', sans-serif;
            direction: rtl;
            background: white;
            color: black;
            font-size: 16px;
            line-height: 1.4;
        }

        .main-container { max-width: 100%; margin: 0 auto; }

        .header {
            text-align: center;
            margin-bottom: 15px;
            padding: 10px;
            border: 2px solid #333;
            border-radius: 8px;
            background: #f8f9fa;
        }

        .header-title {
            font-size: 22px;
            font-weight: bold;
            margin-bottom: 5px;
        }

        .info-section {
            margin: 10px 0;
            padding: 8px;
            border: 1px solid #333;
            border-radius: 5px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .delegate-info, .company-info {
            background: #f8f9fa;
            padding: 5px 10px;
            border-radius: 3px;
            font-weight: bold;
            font-size: 16px;
            flex: 1;
            text-align: center;
            margin: 0 5px;
        }

        .appointment-section {
            background: #e3f2fd;
            border: 2px solid #1976d2;
            border-radius: 8px;
            padding: 12px;
            margin: 12px 0;
            text-align: center;
        }

        .message-section {
            border: 1px solid #666;
            border-radius: 8px;
            padding: 15px;
            margin: 15px 0;
            background: #f9f9f9;
            text-align: center;
        }

        table { 
            width: 100%; 
            border-collapse: collapse;
            border: 2px solid #333;
            margin-top: 15px;
            background: white;
            font-size: 14px;
        }

        th, td { 
            padding: 8px 6px;
            text-align: center;
            border: 1px solid #333;
            vertical-align: middle;
        }

        th { 
            background: #e0e0e0;
            font-weight: bold;
            font-size: 15px;
        }

        .row-number { 
            font-weight: bold; 
            background: #f0f0f0; 
            width: 40px;
        }

        .name-cell { 
            font-weight: bold; 
            text-align: center;
            min-width: 120px;
        }

        .company-cell {
             font-weight: bold; 
            text-align: center;
            min-width: 120px;
        }


       .governorate-cell {
    font-weight: bold; 
            text-align: center;
    min-width: 80px;
   
}

        .military-cell {
            font-weight: bold; 
            text-align: center;
            min-width: 80px;
            font-size: 13px;
        }

        .status-cell {
            font-weight: bold; 
            text-align: center;
            text-align: center;
            min-width: 100px;
        }

        .notes-cell {
             font-weight: bold; 
            text-align: center;
            max-width: 140px;
            font-size: 13px;
            word-wrap: break-word;
        }

        .footer {
            margin-top: 20px;
            text-align: center;
            border-top: 1px solid #333;
            padding-top: 10px;
            font-size: 14px;
            color: #666;
        }

        @media print {
            body { margin: 0; font-size: 14px; }
            table { font-size: 12px; }
            th, td { padding: 6px 4px; }
        }
    </style>
</head>
<body>
    <div class="main-container">
        <div class="header">
            <div class="header-title">
                📋 قائمة العملاء - ${getAppointmentTypeTitle(appointmentType)}
            </div>
        </div>
        
        <div class="info-section">
            <div class="delegate-info">
                المندوب: ${delegateInfo.DelegateName}
            </div>
            <div class="company-info">
                الشركة: ${selectedList?.ListName || '-'}
            </div>
        </div>
        
        ${appointmentDate ? `
        <div class="appointment-section">
            <div class="appointment-title">
                📅 موعد الحضور المطلوب
            </div>
            <div class="appointment-date">
                ${appointmentDate === '2025/8/15' ? 'الجمعة، 15 أغسطس 2025' : 
                  new Date(appointmentDate).toLocaleDateString('ar-EG', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })} الساعة ${appointmentTime || '09:00'}
            </div>
        </div>
        ` : ''}
        
${customMessage ? `
<div style="text-align:center;">
  <div style="display:inline-block; direction:rtl; background:#fff; border:2px solid #333; border-radius:10px; padding:10px 16px; font-size:16px; font-weight:600; color:#111; line-height:1.6;">
    📝 <span style="font-weight:700;">رسالة هامة للمندوب:</span>
    ${customMessage.replace(/\n/g, '<br>')}
  </div>
</div>
` : ''}


        
        <table>
            <thead>
                <tr>${tableHeaders}</tr>
            </thead>
            <tbody>
                ${tableRows}
            </tbody>
        </table>
        
        <div class="footer">
            نظام إدارة السفر | أطبع بتاريخ: ${new Date().toLocaleString('ar-EG')}
            ${showMilitary ? ' | يتضمن بيانات التجنيد' : ''}
        </div>
    </div>
</body>
</html>
`;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 500);
    };
};

// استبدل دالة createWhatsAppImageElement بهذه النسخة المحدثة
const createWhatsAppImageElement = (delegateInfo, filteredClients, appointmentInfo) => {
    const container = document.createElement('div');
    container.style.cssText = `
        width: 800px;
        padding: 20px;
        background: white;
        font-family: 'Arial', sans-serif;
        direction: rtl;
        color: black;
        font-size: 14px;
        line-height: 1.4;
        position: absolute;
        left: -9999px;
        top: -9999px;
    `;

    const appointmentType = appointmentInfo.appointmentType;
    const showMilitary = appointmentInfo.showMilitary;
    const appointmentDate = appointmentInfo.appointmentDate;
    const appointmentTime = appointmentInfo.appointmentTime;
    const customMessage = appointmentInfo.customMessage; // ✅ إضافة الرسالة المخصصة
    
    const typeTitle = appointmentType === 'medical' ? '🏥 الكشف الطبي' : 
                     appointmentType === 'fingerprint' ? '👆 البصمة' :
                     appointmentType === 'visa' ? '📄 التأشيرة' :
                     appointmentType === 'travel' ? '✈️ السفر' :
                     appointmentType === 'documents' ? '📦 تسليم الأوراق' : '📋 عام';

    container.innerHTML = `
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 3px solid black; padding-bottom: 15px;">
            <div style="font-size: 28px; font-weight: bold; color: black; margin-bottom: 10px;">
                📋 قائمة العملاء
            </div>
            <div style="font-size: 22px; color: black; margin-bottom: 8px; background: #f0f0f0; padding: 12px; border: 2px solid black; border-radius: 8px; font-weight: bold;">
                ${typeTitle}
            </div>
            <div style="font-size: 18px; color: black; margin-bottom: 5px;">
                👤 المندوب: ${delegateInfo.DelegateName}
            </div>
            ${appointmentDate ? `
            <div style="font-size: 16px; color: black; margin-bottom: 5px;">
                📅 الموعد: ${appointmentDate === '2025/8/15' ? 'الجمعة، 15 أغسطس 2025' : 
                    new Date(appointmentDate).toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} الساعة ${appointmentTime}
            </div>
            ` : ''}
            <div style="font-size: 14px; color: black;">
                💥 العدد: ${filteredClients.length} شخص${showMilitary ? ' | يتضمن التجنيد' : ''}
            </div>
        </div>

   ${customMessage ? `
    <div style="background: #f9f9f9; border: 2px solid #333; border-radius: 8px; padding: 15px; margin-bottom: 20px; text-align: center;">
        <div style="font-weight: bold; margin-bottom: 8px; font-size: 18px; color: black; text-align: center;">📝 رسالة هامة للمندوب</div>
        <div style="font-size: 16px; font-weight: bold; color: black; line-height: 1.8; text-align: center; white-space: pre-line;">${customMessage}</div>
    </div>
` : ''}


        <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black; font-size: 12px;">
            <thead>
                <tr style="background: #f0f0f0;">
                    <th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold; width: 40px;">م</th>
                    <th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">الاسم</th>
                    <th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">الشركة</th>
                    <th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">الهاتف</th>
                    ${showMilitary ? `<th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">التجنيد</th>` : ''}
                    ${appointmentType === 'medical' ? `<th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">الطبي</th>` : ''}
                    ${appointmentType === 'documents' ? `<th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">الأوراق</th>` : ''}
                    ${appointmentType === 'fingerprint' ? `<th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">البصمة</th>` : ''}
                    <th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">المحافظة</th>
                    <th style="padding: 8px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">ملاحظات</th>
                </tr>
            </thead>
            <tbody>
                ${filteredClients.map((client, index) => {
                    const rowBg = index % 2 === 0 ? '#f8f8f8' : 'white';
                    
                    let statusCell = '';
                    if (appointmentType === 'medical') {
                        statusCell = `<td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 11px; color: black; background: #d4edda;">${client.MedicalExamStatus || 'في الانتظار'}</td>`;
                    } else if (appointmentType === 'documents') {
                        statusCell = `<td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 11px; color: black; background: #d4edda;">${client.DocumentsDeliveryStatus || 'لم يسلم'}</td>`;
                    } else if (appointmentType === 'fingerprint') {
                        const fingerprintStatus = client.FingerprintDate ? 'تم' : (client.MedicalExamStatus === 'لائق' ? 'جاهز' : 'انتظار');
                        statusCell = `<td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 11px; color: black; background: #d4edda;">${fingerprintStatus}</td>`;
                    }
                    
                    return `
                    <tr style="background: ${rowBg};">
                        <td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 11px; color: black; font-weight: bold; background: #e0e0e0;">${index + 1}</td>
                        <td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 11px; color: black; font-weight: bold;">${client.NameArabic}</td>
                        <td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 10px; color: black;">${client.CompanyName}</td>
                      <td style="padding: 6px 4px; text-align: right; border: 1px solid black; font-size: 10px; color: black; vertical-align: top;">
    ${(() => {
        const phones = getAllPhoneNumbers(client.PhoneNumbers);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 1px 3px; background: #e8f4fd; border-radius: 2px; font-size: 9px; border: 1px solid #b3d9ff; font-family: monospace;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic; font-size: 9px;">غير محدد</div>';
    })()}
</td>
                        ${showMilitary ? `<td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 10px; color: black; background: #fff3cd;">${client.FinalMilitaryStatus || client.MilitaryStatus || 'غير محدد'}</td>` : ''}
                        ${statusCell}
                        <td style="padding: 6px 4px; text-align: center; border: 1px solid black; font-size: 10px; color: black; background: white; border: 2px solid #000;">${client.Governorate || 'غير محدد'}</td>
                        <td style="padding: 6px 4px; text-align: right; border: 1px solid black; font-size: 9px; color: black; background: #f8f9fa; max-width: 120px; word-wrap: break-word;">${client.Notes || '-'}</td>
                    </tr>
                    `;
                }).join('')}
            </tbody>
        </table>

        <div style="margin-top: 20px; text-align: center; color: black; font-size: 12px; border-top: 1px solid black; padding-top: 10px;">
            نظام إدارة السفر | أطبع بتاريخ: ${new Date().toLocaleString('ar-EG')}
            ${showMilitary ? ' | يتضمن بيانات التجنيد' : ''}
        </div>
    `;

    return container;
};

// استبدل دالة generateDelegateImageForWhatsApp بهذه النسخة المحدثة
const generateDelegateImageForWhatsApp = async (delegateInfo, filteredClients) => {
    try {
        showAlert('جاري إنشاء الصورة للواتساب...', 'info');
        
        const html2canvas = await loadHtml2Canvas();
        const appointmentInfo = {
            appointmentType: delegateMessage.appointmentType,
            showMilitary: delegateMessage.showMilitary,
            appointmentDate: delegateMessage.appointmentDate,
            appointmentTime: delegateMessage.appointmentTime,
            customMessage: delegateMessage.customMessage // ✅ إضافة الرسالة المخصصة
        };
        
        const container = createWhatsAppImageElement(delegateInfo, filteredClients, appointmentInfo);
        
        document.body.appendChild(container);
        
        const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 800,
            height: container.scrollHeight
        });
        
        document.body.removeChild(container);
        
        // تحويل إلى blob وعرض في نافذة معاينة
        canvas.toBlob((blob) => {
            const imageUrl = URL.createObjectURL(blob);
            setPreviewImageData({
                url: imageUrl,
                delegateName: delegateInfo.DelegateName || 'مندوب غير معروف',
                clientCount: filteredClients.length,
                blob: blob
            });
            setShowImagePreview(true);
        }, 'image/png', 0.95);
        
    } catch (error) {
        console.error('خطأ في إنشاء صورة الواتساب:', error);
        showAlert('حدث خطأ أثناء إنشاء الصورة: ' + error.message, 'error');
    }
};


    const searchAllLists = async (query) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        
        try {
            setIsLoading(true);
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/search?query=${encodeURIComponent(query)}`);
            const data = await response.json();
            
            if (data.success) {
                setSearchResults(data.results);
                showAlert(`🔍 تم العثور على ${data.count} نتيجة`, 'success');
            }
        } catch (error) {
            console.error('خطأ في البحث:', error);
            showAlert('خطأ في البحث', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ البحث داخل القائمة المحددة
    const searchWithinList = (query, followUpsList) => {
        if (!query || query.trim().length < 2) {
            return followUpsList;
        }
        
        const searchLower = query.toLowerCase();
        return followUpsList.filter(person =>
            followUpMatchesFileNumber(person, query) ||
            person.NameArabic?.toLowerCase().includes(searchLower) ||
            person.PassportNumber?.toLowerCase().includes(searchLower) ||
            person.CompanyName?.toLowerCase().includes(searchLower) ||
            person.DelegateName?.toLowerCase().includes(searchLower) ||
            person.PhoneNumbers?.toLowerCase().includes(searchLower) ||
            person.BorderNumber?.toLowerCase().includes(searchLower)
        );
    };

    // ⚡ تحديث ذكي بدون إعادة تحميل الجدول
    const ultraFastUpdateWithoutRefresh = async (followUpId, field, value, personName) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/periodic-follow-up/update-field/${followUpId}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ field, value, UpdatedByUserID: getCurrentUserId() })
            });
            
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('استجابة غير صحيحة من السيرفر');
            }
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                showAlert(`⚡ تم تحديث ${getFieldDisplayName(field)} لـ ${personName}`, 'success', 1500);
                
                // ✅ تحديث الإحصائيات فقط (بدون إعادة تحميل الجدول)
                await fetchStatistics();
                if (selectedList) {
                    await fetchListStatistics(selectedList.ListID);
                }
            } else {
                throw new Error(data.error || 'فشل في التحديث');
            }
        } catch (error) {
            console.error('خطأ في التحديث:', error);
            showAlert(`❌ خطأ في التحديث: ${error.message}`, 'error');
            throw error;
        }
    };



    // 🧹 دالة تنظيف رقم الطلب
const cleanApplicationNumber = (appNumber) => {
    if (!appNumber) return '';
    
    let cleaned = appNumber
        .replace(/,+/g, '') // إزالة الفواصل
        .replace(/\s+/g, ' ') // توحيد المسافات
        .trim();
    
    // إزالة التكرار إذا كان نفس الرقم مكتوب مرتين
    const parts = cleaned.split(' ');
    if (parts.length === 2 && parts[0] === parts[1]) {
        cleaned = parts[0];
    }
    
    return cleaned;
};

const getApplicationBarcodePersonPayload = (person = {}) => {
    const applicationNumber = cleanPeriodicBarcodeValue(cleanApplicationNumber(person?.CleanApplicationNumber || person?.ApplicationNumber || ''));
    if (!applicationNumber) return null;
    return {
        ...person,
        CleanApplicationNumber: applicationNumber,
        ApplicationNumber: applicationNumber
    };
};

const openApplicationBarcodeSettings = (person = null) => {
    if (!canOpenApplicationBarcodeSettings) {
        showAlert('ليس لديك صلاحية فتح ضبط الباركود', 'warning');
        return;
    }
    const payload = person ? getApplicationBarcodePersonPayload(person) : null;
    if (person && !payload) {
        showAlert('لا يوجد رقم طلب لهذا الشخص حتى يتم طباعة الباركود', 'warning');
        return;
    }
    setApplicationBarcodePerson(payload);
    setShowApplicationBarcodeModal(true);
};

const openGeneralApplicationBarcodeSettings = () => {
    if (!canOpenApplicationBarcodeSettings) {
        showAlert('ليس لديك صلاحية فتح ضبط الباركود', 'warning');
        return;
    }
    setApplicationBarcodePerson(null);
    setShowApplicationBarcodeModal(true);
};

const printApplicationBarcodeDirect = async (person) => {
    if (!canPrintApplicationBarcodeExecute) {
        showAlert('ليس لديك صلاحية تنفيذ طباعة باركود رقم الطلب', 'warning');
        return;
    }
    const payload = getApplicationBarcodePersonPayload(person);
    if (!payload) {
        showAlert('لا يوجد رقم طلب لهذا الشخص حتى يتم الطباعة', 'warning');
        return;
    }
    try {
        const savedSettings = await getEffectivePeriodicBarcodeSettings();
        await printPeriodicApplicationBarcodeWithSettings({
            person: payload,
            code: payload.ApplicationNumber,
            settings: savedSettings
        });
        showAlert(`تم إرسال رقم الطلب للطباعة: ${payload.ApplicationNumber}`, 'success');
    } catch (error) {
        console.error('خطأ تنفيذ طباعة رقم الطلب:', error);
        showAlert('فشل تنفيذ طباعة رقم الطلب: ' + error.message, 'error');
        alert(`فشل تنفيذ طباعة رقم الطلب\n\n${error.message}`);
    }
};

const printSelectedApplicationBarcodes = async () => {
    if (!canPrintSelectedApplicationBarcodes) {
        showAlert('ليس لديك صلاحية طباعة باركود المحدد', 'warning');
        return;
    }
    if (selectedPersons.length === 0) {
        showAlert('حدد أشخاص أولاً للطباعة', 'warning');
        return;
    }
    const currentRows = getFilteredFollowUps();
    const selectedRows = currentRows.filter(person => selectedPersons.includes(person.FollowUpID));
    const items = selectedRows
        .map(person => {
            const payload = getApplicationBarcodePersonPayload(person);
            return payload ? { person: payload, code: payload.ApplicationNumber } : null;
        })
        .filter(Boolean);

    if (items.length === 0) {
        showAlert('الأشخاص المحددون لا يوجد لديهم أرقام طلب صالحة للطباعة', 'warning');
        return;
    }

    try {
        const savedSettings = await getEffectivePeriodicBarcodeSettings();
        await printPeriodicApplicationBarcodeBatchWithSettings({ items, settings: savedSettings });
        showAlert(`تم إرسال ${items.length} رقم طلب للطباعة`, 'success');
    } catch (error) {
        console.error('خطأ طباعة الباركود للمحدد:', error);
        showAlert('فشل طباعة الباركود للمحدد: ' + error.message, 'error');
        alert(`فشل طباعة الباركود للمحدد\n\n${error.message}`);
    }
};

// 📝 تحديث البيانات محلياً (بدون استدعاء السيرفر)
const updateLocalData = (followUpId, field, value) => {
    // ✅ تنظيف خاص لرقم الطلب
    if (field === 'ApplicationNumber' && value) {
        value = value.replace(/,+/g, '').trim();
        // إزالة التكرار إذا كان نفس الرقم مكتوب مرتين
        const parts = value.split(/\s+/);
        if (parts.length === 2 && parts[0] === parts[1]) {
            value = parts[0];
        }
    }
    
    // تحديث البيانات في المتابعة الحالية
    if (selectedList) {
        setFollowUps(prevFollowUps => 
            prevFollowUps.map(person => 
                person.FollowUpID === followUpId 
                    ? { ...person, [field]: value }
                    : person
            )
        );
    }
    
    // تحديث البيانات في نتائج البحث
    if (searchResults.length > 0) {
        setSearchResults(prevResults => 
            prevResults.map(person => 
                person.FollowUpID === followUpId 
                    ? { ...person, [field]: value }
                    : person
            )
        );
    }
};
    const removePerson = async (personOrFollowUpId, personNameFallback = '') => {
        // ✅ حذف المتابعة يستخدم نفس مسار إلغاء الحضور في صفحة الاختيار
        if (!canDelete) {
            alert('⛔ ليس لديك صلاحية للحذف');
            return;
        }

        const person = typeof personOrFollowUpId === 'object' && personOrFollowUpId !== null
            ? personOrFollowUpId
            : { FollowUpID: personOrFollowUpId, NameArabic: personNameFallback };

        const personName = person.NameArabic || personNameFallback || 'هذا الشخص';
        const listId = person.ListID || selectedList?.ListID;
        const passportId = person.PassportID;

        if (!listId || !passportId) {
            showAlert('لا يمكن تنفيذ إلغاء الحضور لأن بيانات القائمة أو الجواز غير مكتملة', 'error');
            return;
        }
        
        if (!window.confirm(`🗑️ هل تريد إلغاء حضور "${personName}"؟

سيتم استخدام نفس وظيفة زر إلغاء الحضور في صفحة الاختيار:
- مسح تاريخ النت/رقم الطلب/القنصلية/التأشيرة/التأشيرة المحملة/السفر فقط
- إلغاء التأكيد في القائمة
- إخفاء الشخص من المتابعة عند وجود كشف/بصمة/تأمين/تسليم أوراق
- عدم مسح الكشف الطبي أو البصمة أو التأمين أو تسليم الأوراق`)) {
            return;
        }
        
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/update-confirmation`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listId,
                    passportId,
                    isConfirmed: false,
                    requestedFrom: 'periodic_follow_up_delete_button'
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                showAlert(data.message || `✅ تم إلغاء حضور ${personName} بنفس زر الاختيار`, 'success');
                
                if (selectedList) {
                    await fetchListDetails(selectedList.ListID);
                }
                await fetchLists();
                await fetchStatistics();
            } else {
                showAlert(data.error || 'فشل في إلغاء الحضور', 'error');
            }
        } catch (error) {
            console.error('خطأ في إلغاء حضور الشخص من المتابعة:', error);
            showAlert('خطأ في إلغاء حضور الشخص من المتابعة', 'error');
        }
    };

const getFieldDisplayName = (field) => {
    const fieldNames = {
        'ApplicationNumber': 'رقم الطلب',
        'PhoneNumbers': 'رقم الهاتف', // ✅ جديد
        'MedicalExamDate': 'تاريخ الكشف الطبي',
        'MedicalExamStatus': 'نتيجة الكشف',
        'FingerprintDate': 'تاريخ البصمة',
        'VisaDate': 'تاريخ التأشيرة',
        'TravelDate': 'تاريخ السفر',
        'BorderNumber': 'رقم الحدود',
        'DocumentsDeliveryStatus': 'حالة تسليم الأوراق',
        'Notes': 'الملاحظات'
    };
    return fieldNames[field] || field;
};

    const getStageIcon = (stage) => {
        if (stage.includes('مسافر')) return <Plane className="text-green-600" size={18} />;
        if (stage.includes('جاهز للسفر')) return <Award className="text-blue-600" size={18} />;
        if (stage.includes('دخل القنصلية')) return <MapPin className="text-teal-600" size={18} />;
        if (stage.includes('التأشيرة')) return <FileText className="text-purple-600" size={18} />;
        if (stage.includes('البصمة')) return <Fingerprint className="text-orange-600" size={18} />;
        if (stage.includes('الكشف الطبي')) return <Stethoscope className="text-pink-600" size={18} />;
        return <Clock className="text-gray-600" size={18} />;
    };

 const getPhoneNumber = (phoneNumbers) => {
    if (!phoneNumbers) return '';
    try {
        if (typeof phoneNumbers === 'string') {
            if (phoneNumbers.startsWith('[')) {
                const parsed = JSON.parse(phoneNumbers);
                return Array.isArray(parsed) ? (parsed[0] || '') : phoneNumbers;
            }
            return phoneNumbers.split(',')[0].trim(); // أول رقم فقط
        }
        return Array.isArray(phoneNumbers) ? (phoneNumbers[0] || '') : phoneNumbers.toString();
    } catch {
        return phoneNumbers || '';
    }
};

// دالة جديدة للحصول على جميع الأرقام
const getAllPhoneNumbers = (phoneNumbers) => {
    if (!phoneNumbers) return [];
    try {
        if (typeof phoneNumbers === 'string') {
            if (phoneNumbers.startsWith('[')) {
                const parsed = JSON.parse(phoneNumbers);
                return Array.isArray(parsed) ? parsed : [phoneNumbers];
            }
            return phoneNumbers.split(',').map(phone => phone.trim()).filter(phone => phone !== '');
        }
        return Array.isArray(phoneNumbers) ? phoneNumbers : [phoneNumbers.toString()];
    } catch {
        return [phoneNumbers || ''];
    }
};

// رقم الملف الظاهر للمستخدم فقط: OfficeSerial هو البديل الظاهري لـ PassportID
const getFollowUpFileNumber = (person) => {
    const value = person?.OfficeSerial ?? person?.FileNumber ?? person?.SerialNumber;
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    return String(value).trim();
};

const followUpMatchesFileNumber = (person, searchValue) => {
    const value = String(searchValue || '').trim();
    if (!value) return false;
    const officeSerial = getFollowUpFileNumber(person);
    return officeSerial !== '—' && officeSerial.includes(value);
};

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ar-EG');
    };

    const getMilitaryStatusColor = (status) => {
        const safeStatus = status ? String(status).trim() : '';
        
        switch (safeStatus) {
            case 'غير مطلوب':
            case 'معاف':
                return 'bg-emerald-100 text-emerald-800';
            case 'في سن التجنيد':
            case 'مؤجل':
                return 'bg-amber-100 text-amber-800';
            case 'مؤدى':
            case 'أدى الخدمة':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

const getFilteredFollowUps = () => {
    let filtered = allPersonsView ? allPersons : followUps;
    
        // تطبيق فلتر المرحلة
        if (stageFilter !== 'all') {
            filtered = filtered.filter(person => {
                switch (stageFilter) {
                    // الفلاتر العادية الموجودة
                    case 'medical_pending':
                        return !person.MedicalExamDate;
                    case 'medical_passed':
                        return person.MedicalExamStatus === 'لائق';
                    case 'medical_failed':
                        return person.MedicalExamStatus === 'غير لائق';
                    case 'fingerprint_pending':
                        return person.MedicalExamStatus === 'لائق' && !person.FingerprintDate;
                    case 'fingerprint_done':
                        return person.FingerprintDate;
                    case 'consulate_done':
                        return person.ConsulateEntryDate;
                    case 'visa_pending':
                        return person.FingerprintDate && !person.VisaDate;
                    case 'visa_done':
                        return person.VisaDate;
                    case 'travel_ready':
                        return person.VisaDate && !person.TravelDate;
                    case 'traveled':
                        return person.TravelDate;
                    case 'documents_not_delivered':
                        return !person.DocumentsDeliveryStatus || person.DocumentsDeliveryStatus === 'لم يسلم';
                    case 'documents_partial':
                        return person.DocumentsDeliveryStatus === 'سلم البعض وينقصه شيء';
                case 'documents_delivered':
                        return person.DocumentsDeliveryStatus === 'سلم';
                    case 'insurance_done':
                        return !!person.MedicalInsuranceDate;
                    case 'insurance_pending':
                        return !person.MedicalInsuranceDate;
                    
                    // ✅ الفلاتر العكسية الجديدة
                    case 'exclude_medical_passed':
                        // الكل ماعدا اللائق (يعني: غير لائق + قيد التحميل + فارغ)
                        return person.MedicalExamStatus !== 'لائق';
                    
                    case 'exclude_fingerprint_done':
                        return !person.FingerprintDate;
                    
                    case 'exclude_consulate_done':
                        return !person.ConsulateEntryDate;
                    
                    case 'exclude_documents_delivered':
                        // الكل ماعدا من سلم (يعني: لم يسلم + سلم البعض + فارغ)
                        return person.DocumentsDeliveryStatus !== 'سلم';
                    
                    case 'exclude_visa_done':
                        // الكل ماعدا من صدرت تأشيرته (يعني: لم تصدر + فارغ)
                        return !person.VisaDate;
                    
                    case 'exclude_traveled':
                        // الكل ماعدا المسافرين (يعني: لم يسافر + فارغ)
                        return !person.TravelDate;
                    
                    default:
                        return true;
                }
            });
        }
    
        // باقي الفلاتر (التواريخ والبحث الداخلي) تبقى كما هي
        Object.entries(dateFilters).forEach(([filterKey, filterValue]) => {
            if (filterValue.enabled && (filterValue.startDate || filterValue.endDate)) {
                filtered = filtered.filter(person => {
                    let personDate = null;
                    
                    switch (filterKey) {
                        case 'netDate':
                            personDate = person.NetWorkDate;
                            break;
                        case 'medicalDate':
                            personDate = person.MedicalExamDate;
                            break;
                        case 'fingerprintDate':
                            personDate = person.FingerprintDate;
                            break;
                        case 'consulateDate':
                            personDate = person.ConsulateEntryDate;
                            break;
                        case 'visaDate':
                            personDate = person.VisaDate;
                            break;
                    case 'travelDate':
                            personDate = person.TravelDate;
                            break;
                        case 'insuranceDate':
                            personDate = person.MedicalInsuranceDate;
                            break;
                        default:
                            return true;
                    }
                    
                    if (!personDate) return false;
                    
                    const date = new Date(personDate);
                    
                    if (filterValue.startDate && !filterValue.endDate) {
                        const startDate = new Date(filterValue.startDate);
                        return date >= startDate;
                    }
                    
                    if (!filterValue.startDate && filterValue.endDate) {
                        const endDate = new Date(filterValue.endDate);
                        return date <= endDate;
                    }
                    
                    if (filterValue.startDate && filterValue.endDate) {
                        const startDate = new Date(filterValue.startDate);
                        const endDate = new Date(filterValue.endDate);
                        return date >= startDate && date <= endDate;
                    }
                    
                    return true;
                });
            }
        });
    
        // تطبيق البحث الداخلي
        if (listSearchTerm && listSearchTerm.trim().length >= 2) {
            filtered = searchWithinList(listSearchTerm, filtered);
        }
    
// ⏰ فلتر وقت الرحلة داخل اليوم (لو تم اختيار وقت معين)
if (selectedTravelTime !== 'all') {
    filtered = filtered.filter(person => {
        if (!person.TravelDate) return false;

        const timeStr = formatTravelTime(person.TravelDate);
        return timeStr === selectedTravelTime;
    });
}

// 🔃 تطبيق الترتيب
filtered = [...filtered].sort((a, b) => {
    let valA, valB;
    if (listSortBy === 'name') {
        valA = (a.NameArabic || '').trim();
        valB = (b.NameArabic || '').trim();
        const cmp = valA.localeCompare(valB, 'ar');
        return listSortDir === 'asc' ? cmp : -cmp;
    } else {
        // addedDate - sort by AddedAt or FollowUpID
        valA = a.AddedAt ? new Date(a.AddedAt).getTime() : (a.FollowUpID || 0);
        valB = b.AddedAt ? new Date(b.AddedAt).getTime() : (b.FollowUpID || 0);
        return listSortDir === 'asc' ? valA - valB : valB - valA;
    }
});

        return filtered;
    };


    // ⏰ دالة موحدة لتنسيق وقت السفر بنفس شكل الكروت (مثلاً: ٠٤:٤٨ م)
const formatTravelTime = (value) => {
    if (!value) return null;
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;

        // نفس طريقة عرض التاريخ/الوقت اللى مستخدمة فى EnhancedEditCell
        const full = d.toLocaleString('ar-EG', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });

        // بيطلع عادة بالشكل: "٢٠٢٥/١٢/٠٣ ٠٤:٤٨ م"
        const parts = full.split(' ');
        if (parts.length >= 2) {
            const timePart = parts[1];       // ٠٤:٤٨
            const ampmPart = parts[2] || ''; // ص / م
            return `${timePart} ${ampmPart}`.trim();
        }

        return full;
    } catch {
        return null;
    }
};



    // دالة مساعدة لتنسيق التاريخ بالعربي
const formatDateForImage = (value) => {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('ar-EG');
  } catch {
    return value;
  }
};

// بناء صف لكل عميل بناءً على الأعمدة المختارة فى exportSettings.includeFields
// بناء صف لكل عميل بناءً على الأعمدة المختارة فى printOptions.includeFields
const buildFollowUpExportRowForImage = (person, index, includeFields) => {
  const row = {};

  // 🔹 رقم مسلسل
  if (includeFields.serialNumber) {
    row['م'] = index + 1;
  }

  // 🔹 الأعمدة الأساسية
  if (includeFields.name) {
    row['الاسم'] = person.NameArabic || '-';
  }

  if (includeFields.company) {
    const fileNumber = getFollowUpFileNumber(person);
    row['الشركة / رقم الملف'] = fileNumber !== '—'
      ? `${person.CompanyName || '-'} - ID: ${fileNumber}`
      : (person.CompanyName || '-');
  }

  if (includeFields.profession) {
    row['المهنة'] = person.ProfessionName || '-';
  }

  if (includeFields.passport) {
    row['رقم الجواز'] = person.PassportNumber || '-';
  }

  if (includeFields.phone) {
    const phones = getAllPhoneNumbers(person.PhoneNumbers);
    row['الهاتف'] = phones && phones.length > 0 ? phones.join(' - ') : '-';
  }

  if (includeFields.delegate) {
    row['المندوب'] = person.DelegateName || '-';
  }

  if (includeFields.military) {
    row['التجنيد'] =
      person.FinalMilitaryStatus ||
      person.MilitaryStatus ||
      '-';
  }

  if (includeFields.netDate) {
    row['تاريخ النت'] = formatDateForImage(person.NetWorkDate);
  }

  if (includeFields.listName) {
    row['اسم القائمة الأصلية'] = person.ListName || '-';
  }

   if (includeFields.governorate) {
    row['المحافظة'] = person.Governorate || '-';
  }

  // 🔹 الأعمدة الإجرائية
  if (includeFields.medical) {
    const datePart = person.MedicalExamDate
      ? formatDateForImage(person.MedicalExamDate)
      : '-';
    const statusPart = person.MedicalExamStatus || '-';
    // نفس فكرة الطباعة: تاريخ + نتيجة فى عمود واحد
    row['الكشف الطبي'] = `${datePart} - ${statusPart}`;
  }

  if (includeFields.fingerprint) {
    row['البصمة'] = person.FingerprintDate
      ? formatDateForImage(person.FingerprintDate)
      : '-';
  }

  if (includeFields.consulate) {
    row['دخول القنصلية'] = person.ConsulateEntryDate
      ? formatDateForImage(person.ConsulateEntryDate)
      : '-';
  }

  if (includeFields.visa) {
    row['التأشيرة'] = person.VisaDate
      ? formatDateForImage(person.VisaDate)
      : '-';
  }

  if (includeFields.travel) {
    row['السفر'] = person.TravelDate
      ? formatDateForImage(person.TravelDate)
      : '-';
  }

  if (includeFields.documents) {
    row['الأوراق'] = person.DocumentsDeliveryStatus || 'لم يسلم';
  }

  if (includeFields.stage) {
    row['المرحلة'] = person.CurrentStage || '-';
  }

  if (includeFields.notes) {
    row['ملاحظات'] = person.Notes || '-';
  }

  return row;
};



const savePeriodicReportAsImages = async () => {
  // 1) تجهيز البيانات
  let dataToExport = getFilteredFollowUps();

  if (!Array.isArray(dataToExport) || dataToExport.length === 0) {
    showAlert('لا توجد بيانات لتصديرها كصورة', 'error');
    return;
  }

  try {
    showAlert('جاري تجهيز صور كشف المتابعة الدورية.', 'info');

    const html2canvas = await loadHtml2Canvas();

    // قراءة الإعدادات المتقدمة
    const adv = imageExportAdvanced;
    const theme = colorThemes[adv.colorTheme] || colorThemes.blue;
    const maxRows = Math.max(5, parseInt(adv.maxRowsPerImage) || 25);

    // استخدام نفس إعدادات الأعمدة من خيارات الطباعة
    const includeFields = printOptions.includeFields || {};

    // بناء بيانات ديناميكية من الأشخاص
    const mainData = dataToExport
      .map((person, index) =>
        buildFollowUpExportRowForImage(person, index, includeFields)
      )
      .filter(row => Object.keys(row).length > 0);

    if (mainData.length === 0) {
      showAlert('لم يتم اختيار أى أعمدة للتصدير', 'warning');
      return;
    }

    const headers = Object.keys(mainData[0]);
    const visibleColumnsCount = headers.length;

    let headerFontSize, cellFontSize, titleFontSize, containerWidth;

    if (visibleColumnsCount <= 5) {
      headerFontSize = '16px'; cellFontSize = '15px'; titleFontSize = '26px'; containerWidth = '900px';
    } else if (visibleColumnsCount <= 10) {
      headerFontSize = '14px'; cellFontSize = '13px'; titleFontSize = '24px'; containerWidth = '1100px';
    } else if (visibleColumnsCount <= 15) {
      headerFontSize = '12px'; cellFontSize = '11px'; titleFontSize = '22px'; containerWidth = '1300px';
    } else if (visibleColumnsCount <= 20) {
      headerFontSize = '11px'; cellFontSize = '10px'; titleFontSize = '20px'; containerWidth = '1500px';
    } else {
      headerFontSize = '10px'; cellFontSize = '9px'; titleFontSize = '18px'; containerWidth = '1700px';
    }

    // تقسيم الصفوف حسب العدد المحدد يدوياً
    const MAX_ROWS_PER_IMAGE = maxRows;
    const totalRows = mainData.length;

    let pages;
    if (totalRows <= MAX_ROWS_PER_IMAGE + 4) {
      pages = [mainData];
    } else {
      pages = [];
      for (let i = 0; i < totalRows; i += MAX_ROWS_PER_IMAGE) {
        pages.push(mainData.slice(i, i + MAX_ROWS_PER_IMAGE));
      }
    }

    const totalPages = pages.length;
    let pageNumber = 1;

    for (const pageRows of pages) {
      const container = document.createElement('div');
      container.style.cssText = `
        width: ${containerWidth};
        padding: 30px;
        background: ${theme.bg};
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        direction: rtl;
        color: #1a1a1a;
        font-size: ${cellFontSize};
        line-height: 1.6;
        position: absolute;
        left: -9999px;
        top: -9999px;
      `;

      const headerCells = headers.map(h => `
        <th style="
          padding: 14px 10px;
          background: ${theme.headerBg};
          color: white;
          font-size: ${headerFontSize};
          font-weight: bold;
          text-align: center;
          white-space: nowrap;
          border: 1px solid ${theme.headerBorder};
          text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
        ">
          ${h}
        </th>
      `).join('');

      const rowsHtml = pageRows.map((row, rowIndex) => {
        const cells = headers.map(h => `
          <td style="
            padding: 10px 8px;
            border: 1px solid ${theme.cellBorder};
            font-size: ${cellFontSize};
            font-weight: bold;
            text-align: center;
            color: #1a1a1a;
            background: ${rowIndex % 2 === 0 ? theme.rowOdd : theme.rowEven};
          ">
            ${String(row[h] ?? '-')}
          </td>
        `).join('');

        return `<tr>${cells}</tr>`;
      }).join('');

      // بناء الجزء العلوي (اختياري)
      const headerSection = adv.showHeader ? `
        <div style="
          margin-bottom: 25px;
          padding: 30px;
          background: ${theme.cardBg};
          border-radius: 15px;
          border: 3px solid ${theme.cardBorder};
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        ">
          <div style="
            font-size: ${titleFontSize};
            font-weight: bold;
            text-align: center;
            color: ${theme.titleColor};
            margin-bottom: 20px;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
          ">
            ${adv.customTitle ? adv.customTitle : '📋 كشف المتابعة الدورية'}
          </div>

          <div style="display: flex; justify-content: space-around; align-items: center; gap: 20px;">
            <div style="
              text-align: center;
              padding: 20px 35px;
              background: white;
              border-radius: 12px;
              border: 3px solid ${theme.cardBorder};
              min-width: 160px;
              box-shadow: 0 3px 10px rgba(0,0,0,0.1);
            ">
              <div style="font-size: 14px; color: ${theme.titleColor}; font-weight: 700; margin-bottom: 10px;">إجمالي العملاء</div>
              <div style="font-size: 32px; color: ${theme.titleColor}; font-weight: bold;">${mainData.length}</div>
            </div>
            <div style="
              text-align: center;
              padding: 20px 35px;
              background: white;
              border-radius: 12px;
              border: 3px solid ${theme.cardBorder};
              min-width: 160px;
              box-shadow: 0 3px 10px rgba(0,0,0,0.1);
            ">
              <div style="font-size: 14px; color: ${theme.titleColor}; font-weight: 700; margin-bottom: 10px;">الصفحة</div>
              <div style="font-size: 32px; color: ${theme.titleColor}; font-weight: bold;">${pageNumber} / ${totalPages}</div>
            </div>
          </div>

          <div style="font-size: 13px; color: ${theme.titleColor}; text-align: center; margin-top: 18px; font-weight: 600;">
            📅 تاريخ التصدير: ${new Date().toLocaleString('ar-EG')}
          </div>
          <div style="font-size: 13px; color: ${theme.titleColor}; text-align: center; margin-top: 6px; font-weight: 600;">
            الشركة: ${selectedList?.ListName || '-'}
          </div>
        </div>
      ` : adv.customTitle ? `
        <div style="
          margin-bottom: 20px;
          padding: 20px 30px;
          background: ${theme.cardBg};
          border-radius: 12px;
          border: 3px solid ${theme.cardBorder};
        ">
          <div style="
            font-size: ${titleFontSize};
            font-weight: bold;
            text-align: center;
            color: ${theme.titleColor};
            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
          ">
            ${adv.customTitle}
          </div>
        </div>
      ` : '';

      container.innerHTML = `
        ${headerSection}

        <div style="
          background: white;
          border-radius: 15px;
          overflow: hidden;
          border: 3px solid ${theme.cardBorder};
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        ">
          <table style="width: 100%; border-collapse: collapse; background: white;">
            <thead><tr>${headerCells}</tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>

        ${adv.showFooter ? `
        <div style="
          margin-top: 25px;
          padding: 20px;
          background: ${theme.footerBg};
          border-radius: 12px;
          text-align: center;
          border: 3px solid ${theme.footerBorder};
        ">
          <div style="font-size: 15px; color: ${theme.titleColor}; font-weight: bold; margin-bottom: 6px;">
            💼 نظام المتابعة الدورية
          </div>
          <div style="font-size: 13px; color: ${theme.titleColor}; font-weight: 600;">
            صفحة ${pageNumber} من ${totalPages}
          </div>
        </div>
        ` : ''}
      `;

      document.body.appendChild(container);

      const canvas = await html2canvas(container, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: container.offsetWidth,
        height: container.scrollHeight
      });

      document.body.removeChild(container);

      const blob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/png', 0.95)
      );

      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const datePart = new Date().toISOString().split('T')[0];

        link.href = url;
        link.download = `كشف-المتابعة-الدورية-صفحة-${pageNumber}-${datePart}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      pageNumber += 1;
    }

    showAlert('✅ تم إنشاء صور كشف المتابعة الدورية بنجاح!', 'success');
  } catch (error) {
    console.error('خطأ في تصدير صور المتابعة الدورية:', error);
    showAlert('حدث خطأ أثناء إنشاء الصور: ' + error.message, 'error');
  }
};





const DateFiltersComponent = () => (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-4 mb-4 border border-green-200">
        <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center gap-2">
            📅 فلاتر التواريخ
            <button
             onClick={() => setDateFilters({
                    netDate: { enabled: false, startDate: '', endDate: '' },
                    medicalDate: { enabled: false, startDate: '', endDate: '' },
                    fingerprintDate: { enabled: false, startDate: '', endDate: '' },
                    consulateDate: { enabled: false, startDate: '', endDate: '' },
                    visaDate: { enabled: false, startDate: '', endDate: '' },
                    travelDate: { enabled: false, startDate: '', endDate: '' },
                    insuranceDate: { enabled: false, startDate: '', endDate: '' }
                })}
                className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
            >
                مسح الكل
            </button>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
                { key: 'netDate', label: '📅 تاريخ النت', color: 'blue' },
                { key: 'medicalDate', label: '🏥 تاريخ الكشف الطبي', color: 'green' },
                { key: 'fingerprintDate', label: '👆 تاريخ البصمة', color: 'purple' },
                { key: 'consulateDate', label: '🏛️ تاريخ دخول القنصلية', color: 'teal' },
                { key: 'visaDate', label: '📄 تاريخ التأشيرة', color: 'indigo' },
             { key: 'travelDate', label: '✈️ تاريخ السفر', color: 'emerald' },
                { key: 'insuranceDate', label: '🏥 تاريخ التأمين الطبي', color: 'green' }
            ].map(dateFilter => (
                <div key={dateFilter.key} className={`bg-white p-3 rounded-lg border border-${dateFilter.color}-200`}>
                    <label className="flex items-center gap-2 mb-2">
                        <input
                            type="checkbox"
                            checked={dateFilters[dateFilter.key].enabled}
                            onChange={(e) => setDateFilters(prev => ({
                                ...prev,
                                [dateFilter.key]: {
                                    ...prev[dateFilter.key],
                                    enabled: e.target.checked
                                }
                            }))}
                            className="w-4 h-4"
                        />
                        <span className="text-sm font-medium">{dateFilter.label}</span>
                    </label>
                    
                    {dateFilters[dateFilter.key].enabled && (
                        <div className="space-y-2">
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">من تاريخ:</label>
                                <input
                                    type="date"
                                    value={dateFilters[dateFilter.key].startDate}
                                    onChange={(e) => setDateFilters(prev => ({
                                        ...prev,
                                        [dateFilter.key]: {
                                            ...prev[dateFilter.key],
                                            startDate: e.target.value
                                        }
                                    }))}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">إلى تاريخ:</label>
                                <input
                                    type="date"
                                    value={dateFilters[dateFilter.key].endDate}
                                    onChange={(e) => setDateFilters(prev => ({
                                        ...prev,
                                        [dateFilter.key]: {
                                            ...prev[dateFilter.key],
                                            endDate: e.target.value
                                        }
                                    }))}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                />
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
        


        {/* عداد النتائج */}
        <div className="mt-3 text-center">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                📊 النتائج المفلترة: {getFilteredFollowUps().length} شخص
            </span>
        </div>
    </div>
);




// ⏰ إرجاع مواعيد الرحلات المتاحة لليوم المختار فى فلتر تاريخ السفر
// ⏰ إرجاع مواعيد الرحلات المتاحة لليوم المختار فى فلتر تاريخ السفر
const getAvailableTravelTimesForSelectedDate = () => {
    const travelFilter = dateFilters.travelDate;

    // لو الفلتر مش مفعّل أو مفيش تاريخ مختار → مفيش أوقات
    if (!travelFilter.enabled || !travelFilter.startDate) return [];

    // حدود اليوم من/إلى
    const start = new Date(travelFilter.startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(travelFilter.endDate || travelFilter.startDate);
    end.setHours(23, 59, 59, 999);

    const timesSet = new Set();

    followUps.forEach(person => {
        if (!person.TravelDate) return;

        const d = new Date(person.TravelDate);
        if (d >= start && d <= end) {
            const timeStr = formatTravelTime(person.TravelDate);
            if (timeStr) timesSet.add(timeStr);
        }
    });

    // ترتيب أبجدي بسيط كافى هنا
    return Array.from(timesSet).sort();
};




const exportData = async () => {
  try {
    const filteredData = getFilteredFollowUps();

    if (!filteredData || filteredData.length === 0) {
      showAlert('لا توجد بيانات للتصدير', 'warning');
      return;
    }

    // ✅ حالة التصدير كصورة
    if (exportSettings.format === 'image') {
      await savePeriodicReportAsImages(); // الدالة أصلاً بتستخدم نفس الفلاتر والأعمدة المختارة
      setShowExportModal(false);
      return;
    }

    // ✅ باقي الحالات (Excel أو غيره)
    const response = await fetch(
      `${config.API_BASE_URL}/api/periodic-follow-up/export`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: exportSettings.format,      // غالبًا 'excel'
          filters: exportSettings.filters,
          includeFields: exportSettings.includeFields,
          data: filteredData
        })
      }
    );

    if (!response.ok) {
      throw new Error('فشل في تنفيذ التصدير');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      exportSettings.format === 'excel'
        ? 'periodic-follow-up.xlsx'
        : 'export-data';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    showAlert('✅ تم تنفيذ التصدير بنجاح', 'success');
    setShowExportModal(false);
  } catch (error) {
    console.error('خطأ في التصدير:', error);
    showAlert('حدث خطأ أثناء التصدير', 'error');
  }
};




    // مكون خاص بإدارة أرقام الهواتف المتعددة
const PhoneNumbersManager = ({ person, onUpdate, readOnly = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [phones, setPhones] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);

    // تحويل أرقام الهواتف من النص إلى مصفوفة
    const parsePhoneNumbers = (phoneData) => {
        if (!phoneData) return [''];
        
        try {
            // إذا كان JSON array
            if (phoneData.startsWith('[')) {
                return JSON.parse(phoneData).filter(phone => phone && phone.trim() !== '');
            }
            // إذا كان نص عادي مفصول بفواصل
            return phoneData.split(',').map(phone => phone.trim()).filter(phone => phone !== '');
        } catch {
            // إذا فشل التحليل، اعتبره نص واحد
            return [phoneData.trim()];
        }
    };

    // عند بدء التعديل
    const startEdit = () => {
        const currentPhones = parsePhoneNumbers(person.PhoneNumbers);
        setPhones(currentPhones.length > 0 ? currentPhones : ['']);
        setIsEditing(true);
    };

    // إضافة رقم جديد
    const addPhone = () => {
        setPhones(prev => [...prev, '']);
    };

    // حذف رقم معين
    const removePhone = (index) => {
        setPhones(prev => prev.filter((_, i) => i !== index));
    };

    // تحديث رقم معين
    const updatePhone = (index, value) => {
        setPhones(prev => prev.map((phone, i) => i === index ? value : phone));
    };

    // حفظ التغييرات
    const saveChanges = async () => {
        setIsUpdating(true);
        try {
            // تنظيف الأرقام وإزالة الفارغة
            const cleanPhones = phones
                .map(phone => phone.trim())
                .filter(phone => phone !== '')
                .map(phone => phone.replace(/[^\d+\-\s]/g, ''));

            // تحويل إلى JSON للحفظ
            const phoneData = JSON.stringify(cleanPhones);
            
            await onUpdate(person.FollowUpID, 'PhoneNumbers', phoneData, person.NameArabic);
            setIsEditing(false);
        } catch (error) {
            console.error('خطأ في حفظ أرقام الهواتف:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    // إلغاء التعديل
    const cancelEdit = () => {
        setIsEditing(false);
        setPhones([]);
    };

    if (isEditing) {
        return (
            <div className="phone-manager-container bg-blue-50 p-3 rounded-lg border-2 border-blue-200" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-2">
                    {phones.map((phone, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => updatePhone(index, e.target.value)}
                                placeholder={`رقم الهاتف ${index + 1}`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                disabled={isUpdating}
                            />
                            {phones.length > 1 && (
                                <button
                                    onClick={() => removePhone(index)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    disabled={isUpdating}
                                    title="حذف الرقم"
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <div className="flex justify-between items-center mt-3 pt-2 border-t border-blue-200">
                    <button
                        onClick={addPhone}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isUpdating}
                    >
                        <Plus size={14} />
                        إضافة رقم
                    </button>

                    <div className="flex gap-2">
                        <button
                            onClick={cancelEdit}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                            disabled={isUpdating}
                        >
                            <X size={12} />
                        </button>
                        <button
                            onClick={saveChanges}
                            className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                            disabled={isUpdating}
                        >
                            {isUpdating ? <RefreshCw className="animate-spin" size={12} /> : <Save size={12} />}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // عرض أرقام الهواتف
    const displayPhones = parsePhoneNumbers(person.PhoneNumbers);


    
    
    return (
        <div 
            className={`phone-display p-2 rounded-lg transition-colors ${readOnly ? 'cursor-default' : 'cursor-pointer hover:bg-blue-50'}`}
            onClick={readOnly ? undefined : startEdit}
            title={readOnly ? undefined : '🖱️ اضغط لتعديل أرقام الهواتف'}
        >
            {displayPhones.length > 0 ? (
                <div className="space-y-1">
                    {displayPhones.map((phone, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                            <Phone size={12} className="text-blue-600" />
                            <span className="text-blue-600 font-medium select-all cursor-text">
                                {phone}
                            </span>
                            {index === 0 && displayPhones.length > 1 && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-1 rounded">
                                    +{displayPhones.length - 1} أخرى
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <span className="empty-value text-gray-500 text-sm italic">
                    {readOnly ? '—' : '💫 اضغط لإضافة أرقام هواتف'}
                </span>
            )}
        </div>
    );
};


const VisaFileUploadCell = ({ person }) => {
    const uploadKey = person.FollowUpID || person.PassportID;
    const isUploading = !!uploadingVisaFiles[uploadKey];
    const fileUrl = buildPassportFileUrl(person.VisaFilePath);
    const hasVisaFile = !!fileUrl;
    const canUploadVisaFile = canEnterVisa && !!person.PassportID;

    return (
        <div className="flex flex-col items-center gap-1 min-w-[120px]">
            {hasVisaFile ? (
            <a
    href={buildCustomVisaDownloadUrl(person.VisaFilePath, person.NameArabic)}
    className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-lg hover:bg-blue-200 transition"
    title="تحميل التأشيرة باسم العميل"
>
    <Download size={13} />
    عرض التأشيرة
</a>
            ) : (
                <span className="text-xs text-gray-400">لا يوجد ملف</span>
            )}

            {canUploadVisaFile ? (
                <label className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition ${
                    isUploading
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : hasVisaFile
                            ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 cursor-pointer'
                            : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 cursor-pointer'
                }`}>
                    <input
                        type="file"
                        accept="image/*,application/pdf"
                        disabled={isUploading}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (file) handleVisaFileUpload(person, file);
                        }}
                        className="hidden"
                    />
                    {isUploading ? (
                        <>
                            <RefreshCw size={13} className="animate-spin" />
                            جاري الرفع
                        </>
                    ) : (
                        <>
                            <Upload size={13} />
                            {hasVisaFile ? 'تغيير الملف' : 'رفع التأشيرة'}
                        </>
                    )}
                </label>
            ) : (
                <span className="text-[11px] text-gray-300">بدون صلاحية رفع</span>
            )}
        </div>
    );
};

const EnhancedEditCell = ({ person, field, currentValue, type = 'text' }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState('');
    
    const pendingValue = pendingChanges[person.FollowUpID]?.[field];
    const displayValue = pendingValue !== undefined ? pendingValue : currentValue;
    const hasChanges = pendingValue !== undefined;

    // ✅ التحقق من الصلاحيات حسب نوع الحقل
    // يرجع: true=مسموح بالتعديل | 'readonly'=يشوف بس | false=مخفي
    const canEdit = () => {
        switch(field) {
            case 'ApplicationNumber':
                return canEnterAppNumber ? true : 'readonly';
            case 'PhoneNumbers':
                return canEnterPhone ? true : 'readonly';
            case 'MedicalExamDate':
            case 'MedicalExamStatus':
                return canEnterMedical ? true : 'readonly';
            case 'FingerprintDate':
                return canEnterFingerprint ? true : 'readonly';
            case 'DocumentsDeliveryDate':
            case 'DocumentsDeliveryStatus':
                return canEnterDocuments ? true : 'readonly';
            case 'ConsulateEntryDate':
                return canEnterConsulate ? true : 'readonly';
            case 'VisaDate':
                return canEnterVisa ? true : 'readonly';
            case 'TravelDate':
                return canEnterTravel ? true : 'readonly';
            case 'BorderNumber':
                return canEnterBorderNumber ? true : 'readonly';
            case 'Notes':
                return canEnterNotes ? true : 'readonly';
            default:
                return true;
        }
    };

    const editPermission = canEdit();

    // ✅ عرض البيانات بدون إمكانية التعديل لو readonly
    if (editPermission === 'readonly') {
        const roValue = (() => {
            if (!displayValue) return null;
            if (type === 'date') {
                try {
                    const d = new Date(displayValue);
                    if (!isNaN(d.getTime())) {
                        const localDate = new Date(d.getTime() + d.getTimezoneOffset() * -60000);
                        return localDate.toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' });
                    }
                } catch(e) {}
                return displayValue;
            }
            if (type === 'datetime-local') {
                try {
                    const d = new Date(displayValue);
                    if (!isNaN(d.getTime())) {
                        const datePart = d.toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' });
                        const timePart = d.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit', hour12: true });
                        return (
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="font-semibold text-gray-800">{datePart}</span>
                                <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">{timePart}</span>
                            </div>
                        );
                    }
                } catch(e) {}
                return displayValue;
            }
            if (field === 'MedicalExamStatus') {
                const statusMap = { 'لائق': '✅ لائق', 'غير لائق': '❌ غير لائق', 'قيد التحميل': '⏳ قيد التحميل' };
                return statusMap[displayValue] || displayValue;
            }
            return displayValue;
        })();
        return (
            <div className="text-gray-700 text-xs text-center px-1 py-1">
                {roValue
                    ? <span className="font-medium">{roValue}</span>
                    : <span className="text-gray-300">—</span>
                }
            </div>
        );
    }

    const startEdit = () => {
        setTempValue(displayValue || '');
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setTempValue('');
    };

    const saveEdit = () => {
        if (tempValue !== currentValue) {
            addPendingChange(person.FollowUpID, field, tempValue);

            // ✅ لو تم تحديد لائق أو غير لائق، ضع تاريخ اليوم تلقائياً لو مفيش تاريخ
            if (field === 'MedicalExamStatus' && (tempValue === 'لائق' || tempValue === 'غير لائق')) {
                const today = new Date().toISOString().split('T')[0];
                const currentMedDate = person.MedicalExamDate;
                const pendingMedDate = pendingChanges[person.FollowUpID]?.['MedicalExamDate'];
                if (!currentMedDate && !pendingMedDate) {
                    addPendingChange(person.FollowUpID, 'MedicalExamDate', today);
                }
            }
        }
        setIsEditing(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && type !== 'textarea') {
            e.preventDefault();
            saveEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
        }
    };

    if (isEditing) {
        return (
            <div className="enhanced-edit-container" onClick={(e) => e.stopPropagation()}>
            {(type === 'date' || type === 'datetime-local') ? (
    <input
        type={type}
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onKeyDown={handleKeyPress}
        onBlur={saveEdit}
        className="enhanced-edit-input"
        autoFocus
    />
) : type === 'select' ? (
                    <select
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onBlur={saveEdit}
                        className="enhanced-edit-select"
                        autoFocus
                    >
                        {field === 'MedicalExamStatus' ? (
                            <>
                                <option value="">⭐ اختر الحالة</option>
                                <option value="قيد التحميل">⏳ قيد التحميل</option>
                                <option value="لائق">✅ لائق</option>
                                <option value="غير لائق">❌ غير لائق</option>
                            </>
                        ) : field === 'DocumentsDeliveryStatus' ? (
                            <>
                                <option value="">⭐ اختر الحالة</option>
                                <option value="لم يسلم">❌ لم يسلم</option>
                                <option value="سلم البعض وينقصه شيء">⚠️ سلم البعض</option>
                                <option value="سلم">✅ سلم كاملاً</option>
                            </>
                        ) : null}
                    </select>
                ) : type === 'textarea' ? (
                    <textarea
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onBlur={saveEdit}
                        className="enhanced-edit-input min-h-[72px]"
                        autoFocus
                    />
                ) : (
                    <input
                        type="text"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onBlur={saveEdit}
                        className="enhanced-edit-input"
                        autoFocus
                    />
                )}
            </div>
        );
    }

const getDisplayValue = () => {
    if (!displayValue) return null;
    
    if (type === 'date') {
        try {
            const d = new Date(displayValue);
            if (!isNaN(d.getTime())) {
                // تصحيح مشكلة UTC
                const localDate = new Date(d.getTime() + d.getTimezoneOffset() * -60000);
                return localDate.toLocaleDateString('ar-EG', {
                    year: 'numeric', month: '2-digit', day: '2-digit'
                });
            }
        } catch (e) {}
        return displayValue;
    }

    if (type === 'datetime-local') {
        try {
            const d = new Date(displayValue);
            if (!isNaN(d.getTime())) {
                const datePart = d.toLocaleDateString('ar-EG', {
                    year: 'numeric', month: '2-digit', day: '2-digit'
                });
                const timePart = d.toLocaleTimeString('ar-EG', {
                    hour: '2-digit', minute: '2-digit', hour12: true
                });
                return (
                    <div className="flex flex-col items-center gap-0.5">
                        <span className="font-semibold text-gray-800">{datePart}</span>
                        <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">{timePart}</span>
                    </div>
                );
            }
        } catch (e) {}
        return displayValue;
    }
    
    if (field === 'MedicalExamStatus') {
        const statusMap = {
            'لائق': '✅ لائق',
            'غير لائق': '❌ غير لائق',
            'قيد التحميل': '⏳ قيد التحميل'
        };
        return statusMap[displayValue] || displayValue;
    }
    
    return displayValue;
};

    return (
        <div 
            className={`enhanced-edit-display ${hasChanges ? 'has-pending-changes' : ''}`}
            onClick={startEdit}
            title="🖱️ اضغط للتعديل"
        >
            {getDisplayValue() || (
                <span className="empty-value">
                    💫 اضغط للإضافة
                </span>
            )}
            {hasChanges && (
                <div className="pending-indicator" title="تغييرات معلقة - اضغط حفظ">
                    <Clock size={12} className="text-orange-500" />
                </div>
            )}
        </div>
    );
};

// 🆕 نافذة التحديث الجماعي

const MobileFollowUpField = ({ label, children, className = '' }) => (
    <div className={`pfu-mobile-field ${className}`}>
        <div className="pfu-mobile-field-label">{label}</div>
        <div className="pfu-mobile-field-content">{children}</div>
    </div>
);

const MobileStageRow = ({ title, children }) => (
    <div className="pfu-stage-row">
        <div className="pfu-stage-row-title">{title}</div>
        {children}
    </div>
);

const FollowUpMobileCard = ({ person, index }) => {
    const hasPendingForPerson = !!pendingChanges[person.FollowUpID];
    return (
        <div className={`pfu-follow-card ${hasPendingForPerson ? 'has-mobile-pending' : ''}`}>
            <div className="pfu-follow-card-header">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h3 className="text-base font-bold leading-7 truncate">{person.NameArabic || 'بدون اسم'}</h3>
                        </div>
                        <div className="text-xs text-blue-100 flex flex-wrap gap-2">
                            <span>جواز: {person.PassportNumber || 'غير محدد'}</span>
                            {selectedList?.IsUnified && person.ListName && <span>• {person.ListName}</span>}
                        </div>
                    </div>
                    {bulkUpdateMode && (
                        <label className="bg-white bg-opacity-15 rounded-xl px-3 py-2 flex items-center gap-2 text-sm font-bold">
                            <input
                                type="checkbox"
                                checked={selectedPersons.includes(person.FollowUpID)}
                                onChange={() => togglePersonSelection(person.FollowUpID)}
                                className="w-5 h-5"
                            />
                            تحديد
                        </label>
                    )}
                </div>
                <div className="mt-3 bg-white bg-opacity-15 rounded-2xl p-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-bold">
                        {getStageIcon(person.CurrentStage || '')}
                        <span>{person.CurrentStage || 'غير محدد'}</span>
                    </div>
                    {hasPendingForPerson && <span className="text-xs bg-amber-300 text-amber-900 px-2 py-1 rounded-full font-bold">تغييرات معلقة</span>}
                </div>
            </div>

            <div className="pfu-follow-card-body space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <MobileFollowUpField label="الشركة">
                        {person.CompanyName || 'غير محدد'}
                        <div className="text-[11px] text-blue-600 mt-1">ID: {getFollowUpFileNumber(person)}</div>
                    </MobileFollowUpField>
                    <MobileFollowUpField label="المهنة">{person.ProfessionName || 'غير محدد'}</MobileFollowUpField>
                    <MobileFollowUpField label="المندوب">{person.DelegateName || 'غير محدد'}</MobileFollowUpField>
                    <MobileFollowUpField label="التجنيد">
                        <span className={`military-status-badge ${getMilitaryStatusColor(person.FinalMilitaryStatus || person.MilitaryStatus)}`}>
                            {person.FinalMilitaryStatus || person.MilitaryStatus || 'غير محدد'}
                        </span>
                    </MobileFollowUpField>
                </div>

                {canShowColPhone && (
                    <MobileStageRow title="📱 أرقام الهاتف">
                        <PhoneNumbersManager
                            person={person}
                            onUpdate={ultraFastUpdateWithoutRefresh}
                            readOnly={!canEnterPhone}
                        />
                    </MobileStageRow>
                )}

                <div className="grid grid-cols-1 gap-2">
                    {canShowColAppNumber && (
                        <MobileStageRow title="⚡ رقم الطلب">
                            <div className="space-y-2">
                                <EnhancedEditCell
                                    person={person}
                                    field="ApplicationNumber"
                                    currentValue={cleanApplicationNumber(person.CleanApplicationNumber || person.ApplicationNumber) || ''}
                                    type="text"
                                />

                            </div>
                        </MobileStageRow>
                    )}

                    {canShowColNetDate && (
                        <MobileStageRow title="📅 تاريخ النت">
                            <div className="text-center font-bold text-emerald-700 bg-emerald-50 rounded-xl py-3">
                                {formatDate(person.NetWorkDate)}
                            </div>
                        </MobileStageRow>
                    )}

                    {canShowColMedical && (
                        <MobileStageRow title="🏥 الكشف الطبي">
                            <div className="grid grid-cols-2 gap-2">
                                <EnhancedEditCell
                                    person={person}
                                    field="MedicalExamDate"
                                    currentValue={person.MedicalExamDate ? person.MedicalExamDate.split('T')[0] : ''}
                                    type="date"
                                />
                                <EnhancedEditCell
                                    person={person}
                                    field="MedicalExamStatus"
                                    currentValue={person.MedicalExamStatus || 'قيد التحميل'}
                                    type="select"
                                />
                            </div>
                        </MobileStageRow>
                    )}

                    {canShowColFingerprint && (
                        <MobileStageRow title="👆 البصمة">
                            <EnhancedEditCell
                                person={person}
                                field="FingerprintDate"
                                currentValue={person.FingerprintDate ? person.FingerprintDate.split('T')[0] : ''}
                                type="date"
                            />
                        </MobileStageRow>
                    )}

                    {canShowColInsurance && (
                        <MobileStageRow title="🏥 التأمين الطبي">
                            <EnhancedEditCell
                                person={person}
                                field="MedicalInsuranceDate"
                                currentValue={person.MedicalInsuranceDate ? person.MedicalInsuranceDate.split('T')[0] : ''}
                                type="date"
                            />
                        </MobileStageRow>
                    )}

                    {canShowColDocuments && (
                        <MobileStageRow title="📦 تسليم الأوراق">
                            <EnhancedEditCell
                                person={person}
                                field="DocumentsDeliveryStatus"
                                currentValue={person.DocumentsDeliveryStatus || 'لم يسلم'}
                                type="select"
                            />
                        </MobileStageRow>
                    )}

                    {canShowColConsulate && (
                        <MobileStageRow title="🏛️ دخول القنصلية">
                            <EnhancedEditCell
                                person={person}
                                field="ConsulateEntryDate"
                                currentValue={person.ConsulateEntryDate ? person.ConsulateEntryDate.split('T')[0] : ''}
                                type="date"
                            />
                        </MobileStageRow>
                    )}

                    {canShowColVisa && (
                        <MobileStageRow title="📄 التأشيرة">
                            <div className="grid grid-cols-1 gap-2">
                                <EnhancedEditCell
                                    person={person}
                                    field="VisaDate"
                                    currentValue={person.VisaDate ? person.VisaDate.split('T')[0] : ''}
                                    type="date"
                                />
                                <VisaFileUploadCell person={person} />
                            </div>
                        </MobileStageRow>
                    )}

                    {canShowColTravel && (
                        <MobileStageRow title="✈️ السفر">
                            <EnhancedEditCell
                                person={person}
                                field="TravelDate"
                                currentValue={person.TravelDate ? person.TravelDate.slice(0, 16) : ''}
                                type="datetime-local"
                            />
                        </MobileStageRow>
                    )}

                    {canShowColBorderNumber && (
                        <MobileStageRow title="🧾 رقم الحدود">
                            <EnhancedEditCell
                                person={person}
                                field="BorderNumber"
                                currentValue={person.BorderNumber || ''}
                                type="text"
                            />
                        </MobileStageRow>
                    )}

                    {canShowColNotes && (
                        <MobileStageRow title="📝 الملاحظات">
                            <EnhancedEditCell
                                person={person}
                                field="Notes"
                                currentValue={person.Notes || ''}
                                type="textarea"
                            />
                        </MobileStageRow>
                    )}
                </div>

                {canShowColAppNumber && canPrintApplicationBarcodeExecute && (
                    <button
                        type="button"
                        onClick={() => printApplicationBarcodeDirect(person)}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-2xl py-3 font-bold flex items-center justify-center gap-2"
                    >
                        <Printer size={16} />
                        تنفيذ طباعة رقم الطلب
                    </button>
                )}
            </div>
        </div>
    );
};

const FollowUpMobileCards = ({ persons }) => (
    <div className="pfu-mobile-only pfu-mobile-list" data-mobile-view={isMobileView ? 'true' : 'false'}>
        {persons.map((person, index) => (
            <FollowUpMobileCard key={person.FollowUpID || `${person.PassportID}-${index}`} person={person} index={index} />
        ))}
    </div>
);

const BulkUpdateModal = () => {
    const [bulkField, setBulkField] = useState('');
    const [bulkValue, setBulkValue] = useState('');

    const bulkFields = [
        { value: 'MedicalExamDate', label: '🏥 تاريخ الكشف الطبي', type: 'date' },
        { value: 'MedicalExamStatus', label: '🏥 نتيجة الكشف', type: 'select' },
        { value: 'FingerprintDate', label: '👆 تاريخ البصمة', type: 'date' },
        { value: 'ConsulateEntryDate', label: '🏛️ تاريخ دخول القنصلية', type: 'date' },
        { value: 'VisaDate', label: '📄 تاريخ التأشيرة', type: 'date' },
        { value: 'TravelDate', label: '✈️ تاريخ السفر', type: 'datetime-local' },
        { value: 'BorderNumber', label: '🧾 رقم الحدود', type: 'text' },
        { value: 'DocumentsDeliveryStatus', label: '📦 حالة الأوراق', type: 'select' }
    ];

    const selectedField = bulkFields.find(f => f.value === bulkField);

    return (
        <div className="bulk-modal-overlay">
            <div className="bulk-modal-content">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                        📝 تحديث جماعي لـ {selectedPersons.length} شخص
                    </h2>
                    <button onClick={() => setShowBulkModal(false)}>
                        <X size={24} />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            اختر الحقل المراد تحديثه:
                        </label>
                        <select
                            value={bulkField}
                            onChange={(e) => {
                                setBulkField(e.target.value);
                                setBulkValue('');
                            }}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                        >
                            <option value="">-- اختر الحقل --</option>
                            {bulkFields.map(field => (
                                <option key={field.value} value={field.value}>
                                    {field.label}
                                </option>
                            ))}
                        </select>
                    </div>

                 {bulkField && (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
            القيمة الجديدة:
        </label>
        {(selectedField?.type === 'date' || selectedField?.type === 'datetime-local') ? (
            <div className="space-y-2">
                <input
                    type={selectedField.type}
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                />
                <button
                    type="button"
                    onClick={() => setBulkValue('__CLEAR__')}
                    className={`w-full p-2 border rounded-lg text-sm transition-colors ${bulkValue === '__CLEAR__' ? 'bg-red-600 text-white border-red-600' : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'}`}
                >
                    🗑️ مسح التاريخ (جعله فارغاً)
                </button>
            </div>
        ) : selectedField?.type === 'select' ? (

                                <select
                                    value={bulkValue}
                                    onChange={(e) => setBulkValue(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                >
                                    <option value="">-- اختر القيمة --</option>
                                    {bulkField === 'MedicalExamStatus' ? (
                                        <>
                                            <option value="قيد التحميل">⏳ قيد التحميل</option>
                                            <option value="لائق">✅ لائق</option>
                                            <option value="غير لائق">❌ غير لائق</option>
                                        </>
                                    ) : bulkField === 'DocumentsDeliveryStatus' ? (
                                        <>
                                            <option value="لم يسلم">❌ لم يسلم</option>
                                            <option value="سلم البعض وينقصه شيء">⚠️ سلم البعض</option>
                                            <option value="سلم">✅ سلم كاملاً</option>
                                        </>
                                    ) : null}
                                </select>
                            ) : selectedField?.type === 'textarea' ? (
                                <textarea
                                    value={bulkValue}
                                    onChange={(e) => setBulkValue(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg min-h-[90px]"
                                    placeholder="اكتب القيمة أو الملاحظات"
                                />
                            ) : (
                                <input
                                    type="text"
                                    value={bulkValue}
                                    onChange={(e) => setBulkValue(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                    placeholder="أدخل القيمة الجديدة"
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 justify-end mt-6">
                    <button
                        onClick={() => setShowBulkModal(false)}
                        className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                    >
                        إلغاء
                    </button>
                    <button
                        onClick={() => applyBulkUpdate(bulkField, bulkValue)}
                        disabled={!bulkField || !bulkValue}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
                    >
                        تطبيق على {selectedPersons.length} شخص
                    </button>
                </div>
            </div>
        </div>
    );
};


// إضافة الدوال الجديدة قبل return في component
// دالة طباعة جميع المناديب المحدثة
const printAllDelegates = () => {
    if (delegatesData.length === 0) {
        showAlert('لا توجد بيانات مناديب للطباعة', 'error');
        return;
    }

    const printWindow = window.open('', '_blank');
    
    let allTablesHTML = '';
    
    delegatesData.forEach((delegateGroup, index) => {
        const allClients = delegateGroup.clients || [];
        if (allClients.length === 0) return;
        
        const appointmentType = delegateMessage.appointmentType;
        const showMilitary = delegateMessage.showMilitary;
        const columns = getColumnsForAppointmentType(appointmentType, showMilitary);
        
        // بناء headers الجدول
        const tableHeaders = columns.map(col => `<th>${col}</th>`).join('');
        
        // بناء صفوف الجدول
        const tableRows = allClients.map((client, clientIndex) => {
            let rowData = `<td class="row-number">${clientIndex + 1}</td>`;
            rowData += `<td class="name-cell">${client.NameArabic}</td>`;
            rowData += `<td class="company-cell">${client.CompanyName}</td>`;
           rowData += `<td style="text-align: right; vertical-align: top; padding: 8px;">
    ${(() => {
        const phones = getAllPhoneNumbers(client.PhoneNumbers);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px 4px; background: #f0f0f0; border-radius: 3px; font-size: 12px; border: 1px solid #ddd;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic; font-size: 12px;">غير محدد</div>';
    })()}
</td>`;
            
            if (showMilitary) {
                rowData += `<td class="military-cell">${client.FinalMilitaryStatus || client.MilitaryStatus || 'غير محدد'}</td>`;
            }
            
            switch(appointmentType) {
                case 'medical':
                    rowData += `<td class="status-cell">${client.MedicalExamStatus || 'في الانتظار'}</td>`;
                    break;
                case 'documents':
                    rowData += `<td class="status-cell">${client.DocumentsDeliveryStatus || 'لم يسلم'}</td>`;
                    break;
                case 'fingerprint':
                    const fingerprintStatus = client.FingerprintDate ? 'تم عمل البصمة' : 
                                            (client.MedicalExamStatus === 'لائق' ? 'جاهز للبصمة' : 'في انتظار الطبي');
                    rowData += `<td class="status-cell">${fingerprintStatus}</td>`;
                    break;
            }
            
            rowData += `<td class="governorate-cell">${client.Governorate || 'غير محدد'}</td>`;
            rowData += `<td class="notes-cell">${client.Notes || '-'}</td>`;
            
            return `<tr>${rowData}</tr>`;
        }).join('');
        
        // إضافة جدول المندوب الحالي
        allTablesHTML += `
            <div class="delegate-section">
                <div class="delegate-header">
                    <h3 style="margin: 0; font-size: 18px; font-weight: bold;">🧑‍💼 المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'مندوب غير معروف'}</h3>
                    <p style="margin: 5px 0; font-size: 14px;">👥 العدد: ${allClients.length} شخص | 🏢 ${selectedList?.ListName || '-'}</p>
                </div>
                
                <table class="delegate-table">
                    <thead>
                        <tr style="background: #e0e0e0;">${tableHeaders}</tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
                
                <div class="delegate-footer">
                    نظام إدارة السفر | طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')}
                    ${showMilitary ? ' | يتضمن بيانات التجنيد' : ''}
                </div>
            </div>
        `;
    });

    // إعداد الموعد والرسالة الموحدة في الأعلى
    const appointmentInfo = delegateMessage.appointmentDate ? `
        <div class="unified-appointment-section">
            <div class="appointment-title">📅 موعد الحضور المطلوب</div>
            <div class="appointment-date">
                ${delegateMessage.appointmentDate === '2025/8/15' ? 'الجمعة، 15 أغسطس 2025' : 
                  new Date(delegateMessage.appointmentDate).toLocaleDateString('ar-EG', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })} الساعة ${delegateMessage.appointmentTime || '09:00'}
            </div>
        </div>
    ` : '';

    const unifiedMessage = delegateMessage.customMessage ? `
        <div class="unified-message-section">
            <div class="message-title">📝 رسالة هامة للمناديب</div>
            <div class="message-content">${delegateMessage.customMessage.replace(/\n/g, '<br>')}</div>
        </div>
    ` : '';

    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>كشوف جميع المناديب</title>
            <style>
                @page { size: A4 portrait; margin: 8mm; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                
                body { 
                    font-family: 'Arial', sans-serif;
                    direction: rtl;
                    font-size: 14px;
                    line-height: 1.4;
                }
                
                .main-header {
                    text-align: center;
                    margin-bottom: 15px;
                    border-bottom: 3px solid #333;
                    padding-bottom: 8px;
                }
                
                .main-title {
                    font-size: 24px;
                    font-weight: bold;
                    margin-bottom: 6px;
                }
                
                .main-subtitle {
                    font-size: 16px;
                    margin-bottom: 4px;
                }
                
                .main-info {
                    font-size: 14px;
                    color: #666;
                }
                
                .unified-appointment-section {
                    background: #e3f2fd;
                    border: 2px solid #1976d2;
                    border-radius: 8px;
                    padding: 10px;
                    margin-bottom: 12px;
                    text-align: center;
                }
                
                .appointment-title {
                    font-weight: bold;
                    font-size: 16px;
                    color: #1976d2;
                    margin-bottom: 6px;
                }
                
                .appointment-date {
                    font-size: 15px;
                    font-weight: 600;
                    color: #333;
                }
                
                .unified-message-section {
                    border: 2px solid #333;
                    border-radius: 8px;
                    padding: 10px;
                    margin-bottom: 15px;
                    background: #f9f9f9;
                    text-align: center;
                }
                
                .message-title {
                    font-weight: bold;
                    margin-bottom: 6px;
                    font-size: 15px;
                    color: #333;
                }
                
                .message-content {
                    font-size: 14px;
                    font-weight: 600;
                    color: #000;
                    line-height: 1.6;
                }
                
                .delegate-section {
                    margin-bottom: 15px;
                    /* إزالة page-break-inside: avoid للسماح بالتقسيم */
                }
                
                .delegate-header {
                    background: #f0f0f0;
                    padding: 8px;
                    border: 2px solid #333;
                    border-radius: 8px;
                    margin-bottom: 6px;
                    text-align: center;
                }
                
                .delegate-table { 
                    width: 100%; 
                    border-collapse: collapse;
                    border: 2px solid #333;
                    margin-bottom: 6px;
                    font-size: 13px;
                }
                
                .delegate-table th, 
                .delegate-table td { 
                    padding: 6px 4px;
                    text-align: center;
                    border: 1px solid #333;
                    vertical-align: middle;
                }
                
                .delegate-table th { 
                    background: #e0e0e0;
                    font-weight: bold;
                    font-size: 14px;
                }
                
                .delegate-table tbody tr:nth-child(even) {
                    background: #f8f8f8;
                }
                
                .row-number { 
                    font-weight: bold; 
                    background: #f0f0f0 !important; 
                    width: 35px;
                }
                
                .name-cell, .company-cell { 
                    font-weight: bold; 
                    min-width: 110px;
                }
                
                .governorate-cell { min-width: 70px; }
                .military-cell { min-width: 75px; font-size: 12px; }
                .status-cell { min-width: 90px; }
                .notes-cell { 
                    max-width: 130px; 
                    font-size: 11px; 
                    word-wrap: break-word; 
                }
                
                .delegate-footer {
                    text-align: center;
                    color: #666;
                    font-size: 11px;
                    border-top: 1px solid #333;
                    padding-top: 4px;
                }
                
                @media print {
                    body { font-size: 12px; }
                    .delegate-table { font-size: 11px; }
                    .delegate-table th, .delegate-table td { padding: 4px 3px; }
                    .main-title { font-size: 20px; }
                    .appointment-title { font-size: 14px; }
                    .message-title { font-size: 13px; }
                }
            </style>
        </head>
        <body>
            <div class="main-header">
                <div class="main-title">📋 كشوف جميع المناديب</div>
                <div class="main-subtitle">📋 ${getAppointmentTypeTitle(delegateMessage.appointmentType)}</div>
                <div class="main-subtitle">الشركة: ${selectedList?.ListName || '-'}</div>
                <div class="main-info">تاريخ الطباعة: ${new Date().toLocaleString('ar-EG')} | إجمالي المناديب: ${delegatesData.length}</div>
            </div>
            
            ${appointmentInfo}
            ${unifiedMessage}
            
            ${allTablesHTML}
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 500);
    };
    
    showAlert(`✅ تم طباعة كشوف ${delegatesData.length} مندوب`, 'success');
};

// دالة تنزيل جميع صور المناديب
const downloadAllDelegateImages = async () => {
    if (delegatesData.length === 0) {
        showAlert('لا توجد بيانات مناديب لتنزيل الصور', 'error');
        return;
    }

    showAlert('🔄 جاري إنشاء جميع الصور... هذا قد يستغرق بعض الوقت', 'info');
    
    try {
        const html2canvas = await loadHtml2Canvas();
        const appointmentInfo = {
            appointmentType: delegateMessage.appointmentType,
            showMilitary: delegateMessage.showMilitary,
            appointmentDate: delegateMessage.appointmentDate,
            appointmentTime: delegateMessage.appointmentTime,
            customMessage: delegateMessage.customMessage
        };

        for (let i = 0; i < delegatesData.length; i++) {
            const delegateGroup = delegatesData[i];
            const allClients = delegateGroup.clients || [];
            
            if (allClients.length === 0) continue;
            
            showAlert(`🔄 إنشاء صورة المندوب ${i + 1}/${delegatesData.length}: ${delegateGroup.delegateInfo?.DelegateName}`, 'info');
            
            const container = createWhatsAppImageElement(delegateGroup.delegateInfo, allClients, appointmentInfo);
            document.body.appendChild(container);
            
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                useCORS: true,
                allowTaint: true,
                width: 800,
                height: container.scrollHeight
            });
            
            document.body.removeChild(container);
            
            // تنزيل الصورة
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const delegateName = (delegateGroup.delegateInfo?.DelegateName || 'مندوب_غير_معروف').replace(/[^a-zA-Z0-9\u0600-\u06FF]/g, '_');
                link.download = `${delegateName}_${new Date().toISOString().split('T')[0]}.png`;
                link.click();
                URL.revokeObjectURL(url);
            }, 'image/png', 0.95);
            
            // تأخير بسيط لتجنب التحميل الزائد
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        showAlert(`✅ تم تنزيل ${delegatesData.length} صورة بنجاح!`, 'success', 5000);
        
    } catch (error) {
        console.error('خطأ في تنزيل الصور:', error);
        showAlert('حدث خطأ أثناء تنزيل الصور: ' + error.message, 'error');
    }
};



    const printReport = async () => {
        let dataToExport = getFilteredFollowUps();
        
        // تطبيق فلاتر الحالات
        const hasStatusFilters = Object.values(printOptions.filters).some(v => v);
        if (hasStatusFilters) {
          dataToExport = dataToExport.filter(person => {
            if (printOptions.filters.medicalPending && !person.MedicalExamDate) return true;
            if (printOptions.filters.medicalPassed && person.MedicalExamStatus === 'لائق') return true;
            if (printOptions.filters.medicalFailed && person.MedicalExamStatus === 'غير لائق') return true;
            if (printOptions.filters.fingerprintPending && person.MedicalExamStatus === 'لائق' && !person.FingerprintDate) return true;
            if (printOptions.filters.fingerprintDone && person.FingerprintDate) return true;
            if (printOptions.filters.visaPending && person.FingerprintDate && !person.VisaDate) return true;
            if (printOptions.filters.visaDone && person.VisaDate) return true;
            if (printOptions.filters.travelReady && person.VisaDate && !person.TravelDate) return true;
            if (printOptions.filters.traveled && person.TravelDate) return true;
            if (printOptions.filters.documentsNotDelivered && (!person.DocumentsDeliveryStatus || person.DocumentsDeliveryStatus === 'لم يسلم')) return true;
            if (printOptions.filters.documentsDelivered && person.DocumentsDeliveryStatus === 'سلم') return true;
            return false;
          });
        }
      
        // تطبيق فلاتر التواريخ (كل فلتر لوحده)
        const hasDateFilters = Object.values(printOptions.dateFilters).some(filter => filter.enabled);
        if (hasDateFilters) {
          // لو مفيش فلاتر حالات، ابدأ من كل البيانات
          if (!hasStatusFilters) {
            dataToExport = getFilteredFollowUps();
          }
          
          // طبق كل فلتر تاريخ لوحده
          Object.entries(printOptions.dateFilters).forEach(([filterKey, filterValue]) => {
            if (filterValue.enabled && (filterValue.startDate || filterValue.endDate)) {
              dataToExport = dataToExport.filter(person => {
                let personDate = null;
                
                switch (filterKey) {
                  case 'netDate':
                    personDate = person.NetWorkDate;
                    break;
                  case 'medicalDate':
                    personDate = person.MedicalExamDate;
                    break;
                  case 'fingerprintDate':
                    personDate = person.FingerprintDate;
                    break;
                  case 'visaDate':
                    personDate = person.VisaDate;
                    break;
              case 'travelDate':
                    personDate = person.TravelDate;
                    break;
                  case 'insuranceDate':
                    personDate = person.MedicalInsuranceDate;
                    break;
                  default:
                    return true;
                }
                
                // لو مفيش تاريخ للشخص، متعرضوش
                if (!personDate) return false;
                
                const date = new Date(personDate);
                
                // لو في تاريخ بداية بس
                if (filterValue.startDate && !filterValue.endDate) {
                  const startDate = new Date(filterValue.startDate);
                  return date >= startDate;
                }
                
                // لو في تاريخ نهاية بس
                if (!filterValue.startDate && filterValue.endDate) {
                  const endDate = new Date(filterValue.endDate);
                  return date <= endDate;
                }
                
                // لو في تاريخ بداية ونهاية
                if (filterValue.startDate && filterValue.endDate) {
                  const startDate = new Date(filterValue.startDate);
                  const endDate = new Date(filterValue.endDate);
                  return date >= startDate && date <= endDate;
                }
                
                return true;
              });
            }
          });
        }
        
        const printSummary = {
          total: dataToExport.length,
          medical: dataToExport.filter(p => p.MedicalExamStatus === 'لائق').length,
          fingerprint: dataToExport.filter(p => p.FingerprintDate).length,
          visa: dataToExport.filter(p => p.VisaDate).length,
          traveled: dataToExport.filter(p => p.TravelDate).length
        };
        
        const printWindow = window.open('', '_blank');
        
        // بناء headers حسب الخانات المختارة
        let headers = '';
        if (printOptions.includeFields.serialNumber) headers += '<th>م</th>';
        if (printOptions.includeFields.name) headers += '<th>الاسم</th>';
        if (printOptions.includeFields.company) headers += '<th>الشركة / رقم الملف</th>';
        if (printOptions.includeFields.profession) headers += '<th>المهنة</th>';
        if (printOptions.includeFields.passport) headers += '<th>الجواز</th>';
        if (printOptions.includeFields.phone) headers += '<th>الهاتف</th>';
        if (printOptions.includeFields.delegate) headers += '<th>المندوب</th>';
        if (printOptions.includeFields.military) headers += '<th>التجنيد</th>';
        if (printOptions.includeFields.listName) headers += '<th>القائمة</th>'; // ✅ جديد
        if (printOptions.includeFields.governorate) headers += '<th>المحافظة</th>';

        if (printOptions.includeFields.netDate) headers += '<th>تاريخ النت</th>';
        
        if (printOptions.includeFields.medical) headers += '<th>الكشف الطبي</th>';
        if (printOptions.includeFields.fingerprint) headers += '<th>البصمة</th>';
        if (printOptions.includeFields.consulate) headers += '<th>دخول القنصلية</th>';
        if (printOptions.includeFields.visa) headers += '<th>التأشيرة</th>';
        if (printOptions.includeFields.travel) headers += '<th>السفر</th>';
        if (printOptions.includeFields.documents) headers += '<th>الأوراق</th>';
        if (printOptions.includeFields.stage) headers += '<th>المرحلة</th>';
        if (printOptions.includeFields.notes) headers += '<th>ملاحظات</th>';
        
        // بناء البيانات حسب الخانات المختارة
        const tableRows = dataToExport.map((person, index) => {
          let row = '';
          
          if (printOptions.includeFields.serialNumber) row += `<td class="row-number">${index + 1}</td>`;
          if (printOptions.includeFields.name) row += `<td class="name-cell">${person.NameArabic}</td>`;
          if (printOptions.includeFields.company) row += `<td>
            <div style="font-weight:700; color:#111827;">${person.CompanyName || '-'}</div>
            <div style="font-size:11px; color:#1d4ed8; background:#eff6ff; border-radius:6px; padding:2px 6px; margin-top:4px; display:inline-block;">ID: ${getFollowUpFileNumber(person)}</div>
          </td>`;
          if (printOptions.includeFields.profession) row += `<td>${person.ProfessionName || '-'}</td>`;
          if (printOptions.includeFields.passport) row += `<td>${person.PassportNumber || '-'}</td>`;
        if (printOptions.includeFields.phone) row += `<td style="text-align: right; vertical-align: top; padding: 8px;">
    ${(() => {
        const phones = getAllPhoneNumbers(person.PhoneNumbers);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px 4px; background: #f0f0f0; border-radius: 3px; font-size: 12px; border: 1px solid #ddd;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic; font-size: 12px;">غير محدد</div>';
    })()}
</td>`;
          if (printOptions.includeFields.delegate) row += `<td>${person.DelegateName || '-'}</td>`;
          if (printOptions.includeFields.military) row += `<td>${person.FinalMilitaryStatus || person.MilitaryStatus || '-'}</td>`;
          if (printOptions.includeFields.netDate) row += `<td>${formatDate(person.NetWorkDate)}</td>`;
          if (printOptions.includeFields.listName) row += `<td>${person.ListName || '-'}</td>`; // ✅ جديد
  if (printOptions.includeFields.governorate) row += `<td>${person.Governorate || '-'}</td>`; // ✅ جديد

          if (printOptions.includeFields.medical) row += `<td>${person.MedicalExamDate ? formatDate(person.MedicalExamDate) : '-'}<br>${person.MedicalExamStatus || '-'}</td>`;
          if (printOptions.includeFields.fingerprint) row += `<td>${person.FingerprintDate ? formatDate(person.FingerprintDate) : '-'}</td>`;
          if (printOptions.includeFields.consulate) row += `<td>${person.ConsulateEntryDate ? formatDate(person.ConsulateEntryDate) : '-'}</td>`;
          if (printOptions.includeFields.visa) row += `<td>${person.VisaDate ? formatDate(person.VisaDate) : '-'}</td>`;
          if (printOptions.includeFields.travel) row += `<td>${person.TravelDate ? formatDate(person.TravelDate) : '-'}</td>`;
          if (printOptions.includeFields.documents) row += `<td>${person.DocumentsDeliveryStatus || 'لم يسلم'}</td>`;
          if (printOptions.includeFields.stage) row += `<td>${person.CurrentStage}</td>`;
          if (printOptions.includeFields.notes) row += `<td>${person.Notes || '-'}</td>`;
          
          return `<tr>${row}</tr>`;
        }).join('');
        
        // إضافة معلومات الفلاتر المطبقة في التقرير
        let appliedFilters = '';
        if (hasDateFilters || hasStatusFilters) {
          appliedFilters = '<div class="filters-info" style="margin-bottom: 15px; padding: 10px; background: #f0f0f0; border-radius: 8px;">';
          appliedFilters += '<div style="font-weight: bold; margin-bottom: 8px;">الفلاتر المطبقة:</div>';
          
          if (hasDateFilters) {
            Object.entries(printOptions.dateFilters).forEach(([key, filter]) => {
              if (filter.enabled && (filter.startDate || filter.endDate)) {
                const labels = {
                  netDate: 'تاريخ النت',
                  medicalDate: 'تاريخ الكشف الطبي',
                  fingerprintDate: 'تاريخ البصمة',
                  consulateDate: 'تاريخ دخول القنصلية',
                  visaDate: 'تاريخ التأشيرة',
                  travelDate: 'تاريخ السفر'
                };
                appliedFilters += `<div style="font-size: 12px;">• ${labels[key]}: `;
                if (filter.startDate && filter.endDate) {
                  appliedFilters += `من ${filter.startDate} إلى ${filter.endDate}`;
                } else if (filter.startDate) {
                  appliedFilters += `من ${filter.startDate}`;
                } else if (filter.endDate) {
                  appliedFilters += `حتى ${filter.endDate}`;
                }
                appliedFilters += '</div>';
              }
            });
          }
          
          appliedFilters += '</div>';
        }
        
        const htmlContent = `
          <!DOCTYPE html>
          <html dir="rtl" lang="ar">
          <head>
            <meta charset="UTF-8">
            <title>تقرير المتابعة الدورية</title>
<style>
@page { size: A4 landscape; margin: 8mm 5mm; }
* { margin: 0; padding: 0; box-sizing: border-box; }

body { 
  font-family: 'Segoe UI', sans-serif;
  direction: rtl;
  background: white;
  color: black;
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '18px';
    if (selectedColumns <= 10) return '16px';
    if (selectedColumns <= 14) return '14px';
    return '12px';
  })()};
}

.print-container { padding: 8px; }

.report-header {
  text-align: center;
  margin-bottom: 20px;
  border-bottom: 3px solid black;
  padding-bottom: 15px;
}

.company-name { 
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '36px';
    if (selectedColumns <= 10) return '32px';
    if (selectedColumns <= 14) return '28px';
    return '24px';
  })()};
  font-weight: bold; 
  margin-bottom: 8px; 
}

.report-title { 
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '28px';
    if (selectedColumns <= 10) return '24px';
    if (selectedColumns <= 14) return '22px';
    return '20px';
  })()};
  margin-bottom: 8px; 
}

.report-date { 
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '20px';
    if (selectedColumns <= 10) return '18px';
    if (selectedColumns <= 14) return '16px';
    return '14px';
  })()};
}

.summary-section {
  border: 2px solid black;
  border-radius: 8px;
  padding: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '20px';
    if (selectedColumns <= 10) return '18px';
    return '15px';
  })()};
  margin-bottom: 20px;
}

.summary-title {
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '26px';
    if (selectedColumns <= 10) return '22px';
    if (selectedColumns <= 14) return '20px';
    return '18px';
  })()};
  font-weight: bold;
  text-align: center;
  border-bottom: 1px solid black;
  padding-bottom: 8px;
  margin-bottom: 15px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '20px';
    if (selectedColumns <= 10) return '18px';
    return '15px';
  })()};
  text-align: center;
}

.summary-item {
  padding: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '15px';
    if (selectedColumns <= 10) return '12px';
    return '10px';
  })()};
  border: 1px solid black;
  border-radius: 6px;
}

.summary-label { 
  font-weight: bold; 
  margin-bottom: 6px; 
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '18px';
    if (selectedColumns <= 10) return '16px';
    if (selectedColumns <= 14) return '14px';
    return '12px';
  })()};
}

.summary-value { 
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '24px';
    if (selectedColumns <= 10) return '20px';
    if (selectedColumns <= 14) return '18px';
    return '16px';
  })()};
  font-weight: bold; 
}

.filters-info {
  margin-bottom: 15px;
  padding: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '15px';
    return '10px';
  })()};
  background: #f0f0f0;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '16px';
    if (selectedColumns <= 10) return '14px';
    return '12px';
  })()};
}

table { 
  width: 100%; 
  border-collapse: collapse;
  border: 2px solid black;
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 4) return '18px';
    if (selectedColumns <= 6) return '16px';
    if (selectedColumns <= 8) return '14px';
    if (selectedColumns <= 10) return '13px';
    if (selectedColumns <= 12) return '12px';
    if (selectedColumns <= 14) return '11px';
    return '10px';
  })()};
}

th, td { 
  padding: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 4) return '12px 8px';
    if (selectedColumns <= 6) return '10px 6px';
    if (selectedColumns <= 8) return '8px 6px';
    if (selectedColumns <= 10) return '7px 5px';
    if (selectedColumns <= 12) return '6px 4px';
    return '5px 3px';
  })()};
  text-align: center;
  border: 1px solid black;
  vertical-align: middle;
  line-height: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '1.6';
    if (selectedColumns <= 10) return '1.4';
    return '1.2';
  })()};
}

th { 
  background: #f0f0f0; 
  font-weight: bold; 
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 4) return '20px';
    if (selectedColumns <= 6) return '18px';
    if (selectedColumns <= 8) return '16px';
    if (selectedColumns <= 10) return '14px';
    if (selectedColumns <= 12) return '13px';
    if (selectedColumns <= 14) return '12px';
    return '11px';
  })()};
}

tr:nth-child(even) { background: #f8f8f8; }

.row-number { 
  font-weight: bold; 
  background: #e0e0e0; 
  width: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '50px';
    if (selectedColumns <= 10) return '40px';
    return '30px';
  })()};
}

.name-cell { 
  font-weight: bold; 
  text-align: right; 
  max-width: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 4) return '200px';
    if (selectedColumns <= 6) return '160px';
    if (selectedColumns <= 8) return '140px';
    if (selectedColumns <= 10) return '120px';
    return '100px';
  })()};
  min-width: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 4) return '150px';
    if (selectedColumns <= 6) return '120px';
    if (selectedColumns <= 8) return '100px';
    return '80px';
  })()};
}

.footer {
  margin-top: 20px;
  text-align: center;
  border-top: 1px solid black;
  padding-top: 10px;
  font-size: ${(() => {
    const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
    if (selectedColumns <= 6) return '16px';
    if (selectedColumns <= 10) return '14px';
    return '12px';
  })()};
}

/* تنسيق خاص للأعمدة القليلة */
${(() => {
  const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
  if (selectedColumns <= 4) {
    return `
      table { table-layout: fixed; }
      th, td { 
        word-wrap: break-word; 
        white-space: normal;
      }
      .name-cell {
        font-size: 20px;
        padding: 15px 10px;
      }
    `;
  }
  if (selectedColumns <= 6) {
    return `
      .name-cell {
        font-size: 18px;
        padding: 12px 8px;
      }
    `;
  }
  return '';
})()}

@media print {
  body { 
    font-size: ${(() => {
      const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
      if (selectedColumns <= 4) return '16px';
      if (selectedColumns <= 6) return '14px';
      if (selectedColumns <= 8) return '13px';
      if (selectedColumns <= 10) return '12px';
      if (selectedColumns <= 12) return '11px';
      return '10px';
    })()};
  }
  
  th, td { 
    padding: ${(() => {
      const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
      if (selectedColumns <= 4) return '10px 6px';
      if (selectedColumns <= 6) return '8px 5px';
      if (selectedColumns <= 8) return '6px 4px';
      if (selectedColumns <= 10) return '5px 3px';
      return '4px 2px';
    })()};
    font-size: ${(() => {
      const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
      if (selectedColumns <= 4) return '14px';
      if (selectedColumns <= 6) return '12px';
      if (selectedColumns <= 8) return '11px';
      if (selectedColumns <= 10) return '10px';
      if (selectedColumns <= 12) return '9px';
      return '8px';
    })()};
  }
  
  th {
    font-size: ${(() => {
      const selectedColumns = Object.values(printOptions.includeFields).filter(v => v).length;
      if (selectedColumns <= 4) return '16px';
      if (selectedColumns <= 6) return '14px';
      if (selectedColumns <= 8) return '12px';
      if (selectedColumns <= 10) return '11px';
      if (selectedColumns <= 12) return '10px';
      return '9px';
    })()};
  }
  
  @page { margin: 5mm 3mm; }
}
</style>
          </head>
          <body>
            <div class="print-container">
              <div class="report-header">
                <div class="company-name">المتابعة الدورية للعمالة</div>
                <div class="report-title">تقرير شامل - ${selectedList?.ListName || 'جميع القوائم'}</div>
                <div class="report-date">
                  تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')} | عدد العمالة: ${dataToExport.length}
                </div>
              </div>
      
              ${appliedFilters}
      
              <div class="summary-section">
                <div class="summary-title">الملخص الإحصائي</div>
                <div class="summary-grid">
                  <div class="summary-item">
                    <div class="summary-label">إجمالى الحضور</div>
                    <div class="summary-value">${printSummary.total}</div>
                  </div>
                  <div class="summary-item">
                    <div class="summary-label">كشف طبي لائق</div>
                    <div class="summary-value">${printSummary.medical}</div>
                  </div>
                  <div class="summary-item">
                    <div class="summary-label">تم البصمة</div>
                    <div class="summary-value">${printSummary.fingerprint}</div>
                  </div>
                  <div class="summary-item">
                    <div class="summary-label">صدرت التأشيرة</div>
                    <div class="summary-value">${printSummary.visa}</div>
                  </div>
                  <div class="summary-item">
                    <div class="summary-label">سافروا</div>
                    <div class="summary-value">${printSummary.traveled}</div>
                  </div>
                </div>
              </div>
      
              <table>
                <thead>
                  <tr>${headers}</tr>
                </thead>
                <tbody>
                  ${tableRows}
                </tbody>
              </table>
      
              <div class="footer">
                تم إنشاء هذا التقرير بواسطة نظام المتابعة الدورية | ${new Date().toLocaleString('ar-EG')}
              </div>
            </div>
          </body>
          </html>
        `;
        
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        };
      };
  
  // عدل exportToPDF عشان تستخدم نفس الفلترة
  const exportToPDF = async () => {
    printReport();
  };
  

  
  // ✅ مكون شريط الفلترة والبحث للقوائم
const ListsFilterBar = () => (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-6 border border-blue-200">
  {/* إحصائيات سريعة */}
        {canViewStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">{listsStats.TotalLists || 0}</div>
                    <div className="text-sm text-gray-600">إجمالي القوائم</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-green-600">{listsStats.VisibleLists || 0}</div>
                    <div className="text-sm text-gray-600">قوائم ظاهرة</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-gray-600">{listsStats.HiddenLists || 0}</div>
                    <div className="text-sm text-gray-600">قوائم مخفية</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600">{listsStats.VisiblePersons || 0}</div>
                    <div className="text-sm text-gray-600">أشخاص ظاهرين</div>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="text-2xl font-bold text-orange-600">{listsStats.HiddenPersons || 0}</div>
                    <div className="text-sm text-gray-600">أشخاص مخفيين</div>
                </div>
            </div>
        )}

        {/* أدوات الفلترة */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* البحث */}
            <div className="relative">
                <Search size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="🔍 ابحث في أسماء القوائم..."
                    value={listsFilter.search}
                    onChange={(e) => applyListsFilter({ search: e.target.value })}
                    className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

      {/* فلتر الرؤية - يظهر فقط لمن لديه صلاحية إخفاء وإظهار القوائم */}
{canToggleVisibility && (
<select
    value={listsFilter.showHidden.toString()}
    onChange={(e) => {
        const value = e.target.value;
        if (value === 'all') {
            applyListsFilter({ showHidden: 'all' });
        } else {
            applyListsFilter({ showHidden: value === 'true' });
        }
    }}
    className="border border-gray-300 rounded-xl px-4 py-3 bg-white focus:ring-2 focus:ring-blue-500"
>
    <option value="false">📋 القوائم الظاهرة ({listsStats.VisibleLists || 0})</option>
    <option value="true">🙈 القوائم المخفية ({listsStats.HiddenLists || 0})</option>
    <option value="all">📊 جميع القوائم ({listsStats.TotalLists || 0})</option>
</select>
)}

            {/* الترتيب */}
            <select
                value={listsFilter.sortBy}
                onChange={(e) => applyListsFilter({ sortBy: e.target.value })}
                className="border border-gray-300 rounded-xl px-4 py-3 bg-white focus:ring-2 focus:ring-blue-500"
            >
                <option value="recent">🕒 الأحدث</option>
                <option value="oldest">📅 الأقدم</option>
                <option value="name">🔤 الاسم</option>
                <option value="count">📊 العدد</option>
            </select>

            {/* عدد العناصر في الصفحة */}
            <select
                value={listsFilter.limit}
                onChange={(e) => applyListsFilter({ limit: parseInt(e.target.value) })}
                className="border border-gray-300 rounded-xl px-4 py-3 bg-white focus:ring-2 focus:ring-blue-500"
            >
                <option value="6">6 قوائم/صفحة</option>
                <option value="12">12 قائمة/صفحة</option>
                <option value="24">24 قائمة/صفحة</option>
                <option value="48">48 قائمة/صفحة</option>
            </select>
        </div>
    </div>
);

// ✅ بطاقة القائمة المحدثة
const ListCard = ({ list }) => (
    <div className={`relative bg-gradient-to-br from-white to-gray-50 border rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105 ${list.IsHidden ? 'opacity-60 border-gray-300' : 'border-gray-200'}`}>
        {/* زر الإخفاء/الإظهار */}
        {canToggleVisibility && (
        <button
            onClick={(e) => {
                e.stopPropagation();
                toggleListVisibility(list.ListID, list.IsHidden, list.ListName);
            }}
            className={`absolute top-3 left-3 p-2 rounded-xl transition-all duration-200 ${list.IsHidden ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title={list.IsHidden ? 'إظهار القائمة' : 'إخفاء القائمة'}
        >
            {list.IsHidden ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
        )}

        {/* شارة "مخفية" */}
        {list.IsHidden && (
            <div className="absolute top-3 right-3 bg-gray-500 text-white px-2 py-1 rounded-lg text-xs font-bold">
                مخفية
            </div>
        )}

        <div onClick={() => fetchListDetails(list.ListID)}>
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2 text-lg">
                <Star className="text-yellow-500" size={20} />
                {list.ListName}
            </h3>
            
<div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-6">
                {canViewRequiredCount && (
                    <div className="flex justify-between">
                        <span>العدد المطلوب:</span>
                        <span className="font-bold text-orange-600">{list.RequiredCount || 0}</span>
                    </div>
                )}
                
                {canViewTotalCount && (
                    <div className="flex justify-between">
                        <span>إجمالى الحضور:</span>
                        <span className="font-bold text-blue-600">{list.TotalCount}</span>
                    </div>
                )}
                
                {canViewRemainingCount && (
                    <div className="flex justify-between">
                        <span>العدد المتبقي:</span>
                        <span className={`font-bold ${
                            ((list.RequiredCount || 0) - list.TotalCount) > 0 ? 'text-orange-600' : 
                            ((list.RequiredCount || 0) - list.TotalCount) < 0 ? 'text-red-600' : 
                            'text-green-600'
                        }`}>
                            {(list.RequiredCount || 0) - list.TotalCount}
                        </span>
                    </div>
                )}
                
                {canViewMedicalPassed && (
                    <div className="flex justify-between">
                        <span>لائق:</span>
                        <span className="font-bold text-emerald-600">{list.MedicalPassedCount}</span>
                    </div>
                )}
                
                {canViewFingerprintCount && (
                    <div className="flex justify-between">
                        <span>البصمة:</span>
                        <span className="font-bold text-purple-600">{list.FingerprintDoneCount}</span>
                    </div>
                )}
                
                {canViewVisaCount && (
                    <div className="flex justify-between">
                        <span>التأشيرة:</span>
                        <span className="font-bold text-indigo-600">{list.VisaIssuedCount}</span>
                    </div>
                )}
                
                {canViewTraveledCount && (
                    <div className="flex justify-between col-span-2">
                        <span>سافروا:</span>
                        <span className="font-bold text-emerald-600">{list.TraveledCount}</span>
                    </div>
                )}
            </div>
            
            <div className="text-xs text-gray-500 mb-4">
                آخر تحديث: {formatDate(list.LastUpdated)}
            </div>
            
            <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-300">
                <Eye size={18} />
                عرض التفاصيل
            </button>
        </div>
    </div>
);

// ✅ مكون التنقل بين الصفحات المحسن
const PaginationComponent = () => {
    // تحقق من وجود البيانات
    if (!pagination || !pagination.totalPages || pagination.totalPages <= 1) {
        return null;
    }

    const pages = [];
    const maxVisiblePages = 5;
    const currentPage = pagination.currentPage || 1;
    const totalPages = pagination.totalPages || 1;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div className="flex justify-center items-center gap-2 mt-8">
            {/* الصفحة السابقة */}
            <button
                onClick={() => changePage(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            >
                السابق
            </button>

            {/* أرقام الصفحات */}
            {startPage > 1 && (
                <>
                    <button
                        onClick={() => changePage(1)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                    >
                        1
                    </button>
                    {startPage > 2 && <span className="px-2">...</span>}
                </>
            )}

            {pages.map(page => (
                <button
                    key={page}
                    onClick={() => changePage(page)}
                    className={`px-3 py-2 rounded-xl transition-colors ${
                        page === currentPage
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    {page}
                </button>
            ))}

            {endPage < totalPages && (
                <>
                    {endPage < totalPages - 1 && <span className="px-2">...</span>}
                    <button
                        onClick={() => changePage(totalPages)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                    >
                        {totalPages}
                    </button>
                </>
            )}

            {/* الصفحة التالية */}
            <button
                onClick={() => changePage(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            >
                التالي
            </button>

            {/* معلومات الصفحة */}
            <div className="mr-4 text-sm text-gray-600">
                صفحة {currentPage} من {totalPages} 
                ({pagination.totalCount || 0} قائمة)
            </div>
        </div>
    );
};



 // معالجة تحميل الصلاحيات
    if (permissionsLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">جاري تحميل الصلاحيات...</p>
                </div>
            </div>
        );
    }

    // التحقق من صلاحية الوصول للصفحة
    if (!canAccessPage) {
        return (
            <div className="container mx-auto p-4 text-right" dir="rtl">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <h2 className="text-xl font-bold text-red-700 mb-2">
                        عذراً، ليس لديك صلاحية للوصول
                    </h2>
                    <p className="text-red-600 mb-4">
                        ليس لديك صلاحية للوصول إلى صفحة المتابعة الدورية.
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                    >
                        العودة للرئيسية
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div className="modern-follow-up-container" dir="rtl">
            <style jsx>{`

            .enhanced-edit-display {
    min-height: 2rem;
    padding: 0.5rem;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    background: linear-gradient(45deg, rgb(var(--bg-slate-50, 248 250 252)), rgb(var(--bg-slate-200, 226 232 240)));
    position: relative;
}

.enhanced-edit-display.has-pending-changes {
    background: linear-gradient(45deg, #fef3cd, rgb(var(--bg-amber-400, 251 191 36)));
    border-color: rgb(var(--border-amber-500, 245 158 11));
}

.enhanced-edit-display:hover {
    background: linear-gradient(45deg, rgb(var(--bg-blue-100, 217 225 240)), rgb(var(--bg-blue-200, 180 195 224)));
    border-color: rgb(var(--border-blue-500, 61 90 150));
    transform: scale(1.02);
}

.pending-indicator {
    position: absolute;
    top: -4px;
    right: -4px;
    background: rgb(var(--bg-amber-500, 245 158 11));
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: pulse 2s infinite;
}

.enhanced-edit-input, .enhanced-edit-select {
    width: 100%;
    padding: 0.5rem;
    border: 2px solid rgb(var(--border-blue-500, 61 90 150));
    border-radius: 8px;
    font-size: 0.875rem;
    background: rgb(var(--bg-white, 255 255 255));
    box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.3);
}

.bulk-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.bulk-modal-content {
    background: rgb(var(--bg-white, 255 255 255));
    border-radius: 16px;
    padding: 2rem;
    max-width: 500px;
    width: 90%;
}

.pending-changes-bar {
    position: sticky;
    top: 0;
    z-index: 100;
    background: linear-gradient(135deg, rgb(var(--bg-amber-500, 245 158 11)), rgb(var(--bg-amber-600, 217 119 6)));
    color: white;
    padding: 1rem;
    border-radius: 12px;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.selection-controls {
    background: linear-gradient(135deg, rgb(var(--bg-blue-500, 61 90 150)), rgb(var(--bg-blue-700, 31 54 102)));
    color: white;
    padding: 1rem;
    border-radius: 12px;
    margin-bottom: 1rem;
}

/* 📱 Masar Android layout - mobile only, desktop untouched */
.pfu-mobile-only { display: none; }
.pfu-follow-card { background: rgb(var(--bg-white, 255 255 255)); border: 1px solid rgb(var(--border-gray-200, 229 231 235)); border-radius: 22px; box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08); overflow: hidden; }
.pfu-follow-card.has-mobile-pending { border-color: rgb(var(--border-amber-500, 245 158 11)); box-shadow: 0 14px 34px rgba(245, 158, 11, 0.16); }
.pfu-follow-card-header { background: linear-gradient(135deg, rgb(var(--bg-blue-900, 15 29 61)) 0%, rgb(var(--bg-indigo-600, 42 69 125)) 60%, rgb(var(--bg-blue-600, 42 69 125)) 100%); color: white; padding: 14px; }
.pfu-follow-card-body { padding: 12px; }
.pfu-mobile-field { background: rgb(var(--bg-slate-50, 248 250 252)); border: 1px solid #edf2f7; border-radius: 16px; padding: 10px; min-height: 70px; }
.pfu-mobile-field-label { color: rgb(var(--text-slate-500, 100 116 139)); font-size: 11px; font-weight: 700; margin-bottom: 6px; }
.pfu-mobile-field-content { color: rgb(var(--text-slate-900, 15 23 42)); font-size: 13px; font-weight: 700; word-break: break-word; }
.pfu-stage-row { background: rgb(var(--bg-white, 255 255 255)); border: 1px solid rgb(var(--border-gray-200, 229 231 235)); border-radius: 16px; padding: 10px; }
.pfu-stage-row-title { color: rgb(var(--text-slate-700, 51 65 85)); font-size: 12px; font-weight: 800; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.pfu-mobile-tools-scroll { display: flex; gap: 8px; overflow-x: auto; padding: 4px 0 8px; scrollbar-width: none; }
.pfu-mobile-tools-scroll::-webkit-scrollbar { display: none; }
.pfu-mobile-actions-sticky { position: sticky; bottom: 0; background: rgb(var(--bg-white, 255 255 255) / 0.96); backdrop-filter: blur(10px); border-top: 1px solid rgb(var(--border-gray-200, 229 231 235)); padding-top: 12px; }
.pfu-date-modal-panel { background: rgb(var(--bg-white, 255 255 255)); }
.pfu-date-modal-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.periodic-log-mobile-list { display: none; }
.periodic-log-desktop-table { display: table; }
.periodic-log-mobile-cleanup { display: none; }
@media (max-width: 768px) {
    .periodic-activity-log-overlay {
        padding: 0 !important;
        align-items: stretch !important;
        justify-content: stretch !important;
        background: rgba(15, 23, 42, 0.58) !important;
    }
    .periodic-activity-log-panel {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        height: 100dvh !important;
        max-width: 100vw !important;
        max-height: 100dvh !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        overflow: hidden !important;
    }
    .periodic-log-header {
        flex: 0 0 auto !important;
        padding: 12px 14px !important;
        min-height: 72px !important;
        gap: 10px !important;
    }
    .periodic-log-header h2 {
        font-size: 20px !important;
        line-height: 1.25 !important;
        gap: 6px !important;
    }
    .periodic-log-header p {
        font-size: 12px !important;
        line-height: 1.4 !important;
        margin-top: 3px !important;
        max-width: 260px !important;
    }
    .periodic-log-header button {
        min-width: 42px !important;
        min-height: 42px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-radius: 14px !important;
        background: rgb(var(--bg-white, 255 255 255) / 0.12) !important;
    }
    .periodic-log-filters {
        flex: 0 0 auto !important;
        padding: 10px 12px !important;
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 8px !important;
        background: rgb(var(--bg-slate-50, 248 250 252)) !important;
    }
    .periodic-log-filters input[type="text"] {
        grid-column: 1 / -1 !important;
        height: 42px !important;
        font-size: 13px !important;
        border-radius: 14px !important;
        min-width: 0 !important;
    }
    .periodic-log-filters select,
    .periodic-log-filters input[type="date"] {
        height: 42px !important;
        min-width: 0 !important;
        width: 100% !important;
        font-size: 13px !important;
        border-radius: 14px !important;
        padding: 0 10px !important;
        background-color: rgb(var(--bg-white, 255 255 255)) !important;
    }
    .periodic-log-filters > select:last-of-type {
        grid-column: 1 / -1 !important;
    }
    .periodic-log-filters > div {
        grid-column: 1 / -1 !important;
        display: grid !important;
        grid-template-columns: 1fr auto auto !important;
        align-items: center !important;
        gap: 8px !important;
    }
    .periodic-log-filters > div .text-sm {
        font-size: 12px !important;
        color: rgb(var(--text-slate-600, 71 85 105)) !important;
        white-space: nowrap !important;
    }
    .periodic-log-filters > div .flex {
        display: contents !important;
    }
    .periodic-log-filters button {
        min-height: 42px !important;
        border-radius: 14px !important;
        padding: 0 12px !important;
        font-size: 13px !important;
        justify-content: center !important;
        white-space: nowrap !important;
    }
    .periodic-log-cleanup-desktop {
        display: none !important;
    }
    .periodic-log-mobile-cleanup {
        display: block !important;
        flex: 0 0 auto !important;
        border-bottom: 1px solid rgb(var(--border-gray-200, 229 231 235)) !important;
        background: rgb(var(--bg-white, 255 255 255)) !important;
    }
    .periodic-log-mobile-cleanup summary {
        min-height: 44px !important;
        padding: 10px 14px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 8px !important;
        cursor: pointer !important;
        font-weight: 800 !important;
        color: rgb(var(--text-blue-900, 15 29 61)) !important;
        list-style: none !important;
    }
    .periodic-log-mobile-cleanup summary::-webkit-details-marker { display: none; }
    .periodic-log-mobile-cleanup summary small {
        font-size: 11px !important;
        font-weight: 700 !important;
        color: rgb(var(--text-slate-500, 100 116 139)) !important;
        background: rgb(var(--bg-slate-100, 241 245 249)) !important;
        padding: 4px 8px !important;
        border-radius: 999px !important;
    }
    .periodic-log-mobile-cleanup-body {
        max-height: 42dvh !important;
        overflow: auto !important;
        padding: 0 12px 12px !important;
        display: grid !important;
        gap: 10px !important;
        background: rgb(var(--bg-white, 255 255 255)) !important;
    }
    .periodic-log-cleanup-card {
        border-radius: 18px !important;
        padding: 12px !important;
        border: 1px solid rgb(var(--border-blue-100, 217 225 240)) !important;
        background: rgb(var(--bg-blue-50, 238 242 249)) !important;
        display: grid !important;
        gap: 8px !important;
    }
    .periodic-log-cleanup-card.danger {
        border-color: rgb(var(--border-red-200, 254 202 202)) !important;
        background: rgb(var(--bg-rose-50, 255 241 242)) !important;
    }
    .periodic-log-cleanup-title {
        font-size: 15px !important;
        font-weight: 900 !important;
        color: rgb(var(--text-blue-900, 15 29 61)) !important;
    }
    .periodic-log-cleanup-card.danger .periodic-log-cleanup-title { color: rgb(var(--text-red-800, 153 27 27)) !important; }
    .periodic-log-cleanup-subtitle {
        font-size: 11px !important;
        line-height: 1.5 !important;
        color: rgb(var(--text-slate-600, 71 85 105)) !important;
    }
    .periodic-log-mobile-select {
        height: 42px !important;
        border-radius: 14px !important;
        border: 1px solid rgb(var(--border-blue-200, 180 195 224)) !important;
        padding: 0 10px !important;
        background: rgb(var(--bg-white, 255 255 255)) !important;
        width: 100% !important;
    }
    .periodic-log-mobile-primary-btn,
    .periodic-log-mobile-danger-btn {
        height: 42px !important;
        border-radius: 14px !important;
        color: #fff !important;
        font-weight: 800 !important;
    }
    .periodic-log-mobile-primary-btn { background: rgb(var(--bg-blue-600, 42 69 125)) !important; }
    .periodic-log-mobile-danger-btn { background: rgb(var(--bg-red-600, 220 38 38)) !important; }
    .periodic-log-results {
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow: auto !important;
        background: rgb(var(--bg-slate-50, 248 250 252)) !important;
        -webkit-overflow-scrolling: touch !important;
    }
    .periodic-log-mobile-list {
        display: grid !important;
        grid-template-columns: 1fr !important;
        gap: 10px !important;
        padding: 10px 12px 18px !important;
    }
    .periodic-log-card {
        border-radius: 20px !important;
        padding: 12px !important;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06) !important;
        border: 1px solid rgb(var(--border-gray-200, 229 231 235)) !important;
    }
    .periodic-log-card .font-bold {
        font-size: 14px !important;
        line-height: 1.45 !important;
        max-width: 180px !important;
        word-break: break-word !important;
    }
    .periodic-log-card .inline-flex {
        flex-shrink: 0 !important;
        max-width: 140px !important;
        white-space: normal !important;
        text-align: center !important;
        line-height: 1.35 !important;
    }
    .periodic-log-card .grid {
        gap: 6px !important;
    }
    .periodic-log-desktop-table {
        display: none !important;
    }
}

@media (max-width: 768px) {
    html, body, #root { max-width: 100%; overflow-x: hidden; }
    .modern-follow-up-container { padding: 0 !important; background: rgb(var(--bg-gray-100, 243 244 246)) !important; overflow-x: hidden; }
    .modern-follow-up-container * { box-sizing: border-box; }
    .main-card { border-radius: 0 !important; min-height: 100vh; overflow: hidden !important; box-shadow: none !important; }
    .gradient-header { padding: 14px !important; border-radius: 0 0 24px 24px; }
    .gradient-header > div:first-child { align-items: flex-start !important; }
    .gradient-header h1 { font-size: 18px !important; line-height: 1.5; }
    .gradient-header .grid { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 6px !important; }
    .gradient-header .grid > div { background: rgb(var(--bg-white, 255 255 255) / 0.12); border-radius: 12px; padding: 8px 4px; }
    .pfu-desktop-table { display: none !important; }
    .pfu-mobile-only { display: block !important; }
    .pfu-mobile-list { display: grid; grid-template-columns: 1fr; gap: 12px; }
    .pfu-mobile-page-pad { padding: 12px !important; }
    .pending-changes-bar, .selection-controls { border-radius: 0 0 18px 18px !important; margin: 0 0 12px 0 !important; padding: 12px !important; }
    .pending-changes-bar > div, .selection-controls > div { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
    .pending-changes-bar .flex.gap-3, .selection-controls .flex.gap-3 { display: grid !important; grid-template-columns: repeat(2, minmax(0,1fr)) !important; gap: 8px !important; }
    .pending-changes-bar button, .selection-controls button { min-height: 44px; justify-content: center; }
    .enhanced-edit-display { min-height: 44px !important; padding: 10px !important; display: flex; align-items: center; justify-content: center; text-align: center; background: rgb(var(--bg-white, 255 255 255)) !important; border: 1px solid rgb(var(--border-slate-200, 226 232 240)) !important; border-radius: 13px !important; box-shadow: none !important; font-size: 13px; }
    .enhanced-edit-display:hover { transform: none !important; }
    .enhanced-edit-input, .enhanced-edit-select { min-height: 46px !important; font-size: 16px !important; border-radius: 13px !important; }
    .phone-manager-container { width: 100%; }
    .phone-display { background: rgb(var(--bg-white, 255 255 255)); border: 1px solid rgb(var(--border-slate-200, 226 232 240)); border-radius: 13px; min-height: 44px; display: flex; align-items: center; }
    .pfu-follow-card .military-status-badge { display: inline-flex; justify-content: center; width: 100%; padding: 8px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; }
    .modern-follow-up-container .fixed.inset-0 { align-items: flex-end !important; justify-content: center !important; padding: 0 !important; }
    .modern-follow-up-container .fixed.inset-0 > div.bg-white,
    .pfu-date-modal-panel,
    .bulk-modal-content { width: 100% !important; max-width: 100% !important; margin: 0 !important; border-radius: 24px 24px 0 0 !important; max-height: 92vh !important; overflow-y: auto !important; padding: 16px !important; }
    .bulk-modal-overlay { align-items: flex-end !important; padding: 0 !important; }
    .bulk-modal-content { max-width: 100% !important; }
    .pfu-date-modal-panel h2, .modern-follow-up-container .fixed.inset-0 h2 { font-size: 20px !important; line-height: 1.5; }
    .pfu-date-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
    .pfu-date-filter-card { padding: 12px !important; border-radius: 16px !important; }
    .pfu-date-modal-footer { position: sticky; bottom: 0; flex-direction: column !important; align-items: stretch !important; background: rgb(var(--bg-white, 255 255 255) / .97); padding-top: 12px; border-top: 1px solid rgb(var(--border-gray-200, 229 231 235)); }
    .pfu-date-modal-footer > div:last-child { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
    .pfu-date-modal-footer button { min-height: 46px; }
    input, select, textarea { max-width: 100%; }
    .modern-follow-up-container input[type="date"], .modern-follow-up-container input[type="datetime-local"], .modern-follow-up-container input[type="time"], .modern-follow-up-container select { min-height: 44px; font-size: 16px; }
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}
                /* CSS كما هو بالإضافة للتحسينات الجديدة */
                .modern-follow-up-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, rgb(var(--bg-blue-500, 61 90 150)) 0%, rgb(var(--bg-blue-800, 23 42 82)) 100%);
                    padding: 0.5rem;
                }
                @media (min-width: 640px) {
                    .modern-follow-up-container {
                        padding: 1.5rem;
                    }
                }

                .main-card {
                    background: rgb(var(--bg-white, 255 255 255) / 0.95);
                    backdrop-filter: blur(20px);
                    border-radius: 24px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    overflow: visible;
                }

                .gradient-header {
                    background: linear-gradient(135deg, rgb(var(--bg-indigo-600, 42 69 125)) 0%, rgb(var(--bg-blue-600, 42 69 125)) 100%);
                    color: white;
                    padding: 1rem;
                }
                @media (min-width: 640px) {
                    .gradient-header {
                        padding: 2rem;
                    }
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                    gap: 1rem;
                    margin-top: 1.5rem;
                    padding: 1.5rem;
                    background: rgb(var(--bg-white, 255 255 255) / 0.1);
                    border-radius: 16px;
                    backdrop-filter: blur(10px);
                }

                .stat-card {
                    text-align: center;
                    padding: 1rem;
                    background: rgb(var(--bg-white, 255 255 255) / 0.15);
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-2px);
                    background: rgb(var(--bg-white, 255 255 255) / 0.25);
                }

                .modern-table {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                    background: rgb(var(--bg-white, 255 255 255));
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
                }

                .modern-table thead {
                    background: linear-gradient(135deg, rgb(var(--bg-indigo-600, 42 69 125)) 0%, rgb(var(--bg-blue-600, 42 69 125)) 100%);
                    color: white;
                }

                .modern-table th {
                    padding: 1rem 0.75rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    text-align: right;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }

                .modern-table td {
                    padding: 0.75rem;
                    border-bottom: 1px solid rgb(var(--border-slate-100, 241 245 249));
                    vertical-align: middle;
                    font-size: 0.875rem;
                }

                .modern-table tbody tr {
                    transition: all 0.2s ease;
                }

                .modern-table tbody tr:hover {
                    background: linear-gradient(90deg, rgb(var(--bg-slate-50, 248 250 252)) 0%, rgb(var(--bg-slate-200, 226 232 240)) 100%);
                    transform: scale(1.01);
                    box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.1);
                }

                .inline-edit-display {
                    min-height: 2rem;
                    padding: 0.5rem;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 2px solid transparent;
                    background: linear-gradient(45deg, rgb(var(--bg-slate-50, 248 250 252)), rgb(var(--bg-slate-200, 226 232 240)));
                }

                .inline-edit-display:hover {
                    background: linear-gradient(45deg, rgb(var(--bg-blue-100, 217 225 240)), rgb(var(--bg-blue-200, 180 195 224)));
                    border-color: rgb(var(--border-blue-500, 61 90 150));
                    transform: scale(1.02);
                    box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.3);
                }

                .empty-value {
                    color: rgb(var(--text-slate-500, 100 116 139));
                    font-style: italic;
                    font-size: 0.8rem;
                }

                .inline-edit-container {
                    min-height: 2rem;
                    display: flex;
                    align-items: center;
                }

                .inline-edit-input, .inline-edit-select, .inline-edit-textarea {
                    width: 100%;
                    padding: 0.5rem;
                    border: 2px solid rgb(var(--border-blue-500, 61 90 150));
                    border-radius: 8px;
                    font-size: 0.875rem;
                    background: rgb(var(--bg-white, 255 255 255));
                    box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.3);
                    transition: all 0.2s ease;
                }

                .inline-edit-input:focus, .inline-edit-select:focus, .inline-edit-textarea:focus {
                    outline: none;
                    border-color: rgb(var(--border-blue-700, 31 54 102));
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .date-input {
                    min-width: 140px;
                }

                /* إضافات جديدة للتصدير والبحث المحلي */
                .export-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .export-content {
                    background: rgb(var(--bg-white, 255 255 255));
                    border-radius: 16px;
                    padding: 2rem;
                    max-width: 600px;
                    width: 90%;
                    max-height: 80vh;
                    overflow-y: auto;
                }

                .list-search-container {
                    position: relative;
                    margin-bottom: 1rem;
                }

                .list-search-input {
                    width: 100%;
                    padding: 0.75rem 3rem 0.75rem 1rem;
                    border: 2px solid rgb(var(--border-slate-200, 226 232 240));
                    border-radius: 12px;
                    font-size: 1rem;
                    background: rgb(var(--bg-white, 255 255 255));
                    transition: all 0.3s ease;
                }

                .list-search-input:focus {
                    outline: none;
                    border-color: rgb(var(--border-indigo-600, 42 69 125));
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
                }

                .filter-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin: 1rem 0;
                }

                .filter-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem;
                    background: rgb(var(--bg-slate-50, 248 250 252));
                    border-radius: 8px;
                }

                .alert-container {
                    position: fixed;
                    top: 1rem;
                    right: 1rem;
                    z-index: 1000;
                    space-y: 0.5rem;
                }

                .alert-item {
                    background: rgb(var(--bg-white, 255 255 255));
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.25);
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    max-width: 400px;
                    animation: slideIn 0.3s ease-out;
                    border-left: 4px solid;
                }

                .alert-success {
                    border-left-color: rgb(var(--border-emerald-500, 16 185 129));
                    background: linear-gradient(135deg, rgb(var(--bg-emerald-50, 236 253 245)) 0%, rgb(var(--bg-emerald-100, 209 250 229)) 100%);
                }

                .alert-error {
                    border-left-color: rgb(var(--border-red-500, 239 68 68));
                    background: linear-gradient(135deg, rgb(var(--bg-red-50, 254 242 242)) 0%, rgb(var(--bg-red-200, 254 202 202)) 100%);
                }

                .alert-info {
                    border-left-color: rgb(var(--border-blue-500, 61 90 150));
                    background: linear-gradient(135deg, rgb(var(--bg-blue-50, 238 242 249)) 0%, rgb(var(--bg-blue-100, 217 225 240)) 100%);
                }

                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }

                .tab-container {
                    display: flex;
                    background: rgba(0, 0, 0, 0.05);
                    padding: 0.5rem;
                    border-radius: 16px;
                    margin-top: 1.5rem;
                }

                .tab-button {
                    flex: 1;
                    padding: 0.5rem 0.4rem;
                    border-radius: 12px;
                    border: none;
                    background: transparent;
                    color: rgb(var(--text-slate-500, 100 116 139));
                    font-weight: 600;
                    font-size: 0.75rem;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.3rem;
                }
                @media (min-width: 640px) {
                    .tab-button {
                        padding: 1rem;
                        font-size: 1rem;
                        gap: 0.5rem;
                    }
                }

                .tab-button.active {
                    background: rgb(var(--bg-white, 255 255 255));
                    color: rgb(var(--text-indigo-600, 42 69 125));
                    box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.1);
                    transform: translateY(-1px);
                }

                .search-container {
                    position: relative;
                    margin-bottom: 2rem;
                }

                .search-input {
                    width: 100%;
                    padding: 1rem 3rem 1rem 1rem;
                    border: 2px solid rgb(var(--border-slate-200, 226 232 240));
                    border-radius: 16px;
                    font-size: 1.1rem;
                    background: rgb(var(--bg-white, 255 255 255));
                    transition: all 0.3s ease;
                }

                .search-input:focus {
                    outline: none;
                    border-color: rgb(var(--border-indigo-600, 42 69 125));
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
                }

                .military-status-badge {
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .list-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                    gap: 0.5rem;
                    margin: 1rem 0;
                    padding: 1rem;
                    background: rgb(var(--bg-white, 255 255 255) / 0.1);
                    border-radius: 12px;
                }

                .list-stat-card {
                    text-align: center;
                    padding: 0.5rem;
                    background: rgb(var(--bg-white, 255 255 255) / 0.15);
                    border-radius: 8px;
                }
                @media (max-width: 640px) {
                    .modern-table th, .modern-table td {
                        padding: 0.4rem 0.3rem;
                        font-size: 0.7rem;
                    }
                    .modern-table th {
                        font-size: 0.7rem;
                    }
                }
            `}</style>

            {/* 🚨 Enhanced Alert System */}
            <div className="alert-container">
                {alerts.map(alert => (
                    <div
                        key={alert.id}
                        className={`alert-item alert-${alert.type}`}
                    >
                        <div className="flex-shrink-0">
                            {alert.type === 'success' && <CheckCircle2 className="text-emerald-600" size={20} />}
                            {alert.type === 'error' && <AlertTriangle className="text-red-600" size={20} />}
                            {alert.type === 'info' && <RefreshCw className="text-blue-600" size={20} />}
                        </div>
                        <span className="font-medium text-gray-800 text-sm">
                            {alert.message}
                        </span>
                    </div>
                ))}
            </div>

            {/* Modal للتصدير المتقدم */}
            {showExportModal && (
                <div className="export-modal">
                    <div className="export-content">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">📊 تصدير متقدم - {selectedList?.ListName}</h2>
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>

                    {/* نوع التصدير */}
<div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
  <p className="text-sm font-semibold text-slate-700 mb-2">نوع التصدير</p>
  <div className="flex flex-wrap gap-4">
    {/* Excel */}
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        name="export-format"
        value="excel"
        checked={exportSettings.format === 'excel'}
        onChange={(e) =>
          setExportSettings(prev => ({ ...prev, format: e.target.value }))
        }
      />
      <FileDown className="text-emerald-600" size={18} />
      <span className="text-sm text-slate-700">Excel (.xlsx)</span>
    </label>

    {/* Image */}
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        name="export-format"
        value="image"
        checked={exportSettings.format === 'image'}
        onChange={(e) =>
          setExportSettings(prev => ({ ...prev, format: e.target.value }))
        }
      />
      <ImageIcon className="text-sky-500" size={18} />
      <span className="text-sm text-slate-700">صورة (PNG)</span>
    </label>
  </div>
</div>


                        {/* فلاتر البيانات */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3">🔍 فلاتر البيانات</h3>
                            <div className="filter-grid">
                                {[
                                    { key: 'medicalPending', label: '⏳ لم يعمل كشف طبي', count: listStatistics.MedicalPending },
                                    { key: 'medicalPassed', label: '✅ كشف طبي لائق', count: listStatistics.MedicalPassed },
                                    { key: 'medicalFailed', label: '❌ كشف طبي غير لائق', count: listStatistics.MedicalFailed },
                                    { key: 'fingerprintPending', label: '⏳ لم يعمل بصمة', count: listStatistics.FingerprintPending },
                                    { key: 'fingerprintDone', label: '👆 تم عمل البصمة', count: listStatistics.FingerprintDone },
                                    { key: 'visaPending', label: '⏳ لم تصدر التأشيرة', count: listStatistics.VisaPending },
                                    { key: 'visaDone', label: '📄 صدرت التأشيرة', count: listStatistics.VisaIssued },
                                    { key: 'travelReady', label: '✈️ جاهز للسفر', count: listStatistics.TravelReady },
                                    { key: 'traveled', label: '🌍 مسافر', count: listStatistics.Traveled },
                                    { key: 'documentsNotDelivered', label: '📦 لم يسلم أوراق', count: listStatistics.DocumentsPending },
                                    { key: 'documentsDelivered', label: '✅ سلم الأوراق', count: listStatistics.DocumentsDelivered }
                                ].map(filter => (
                                    <label key={filter.key} className="filter-item">
                                        <input
                                            type="checkbox"
                                            checked={exportSettings.filters[filter.key]}
                                            onChange={(e) => setExportSettings(prev => ({
                                                ...prev,
                                                filters: { ...prev.filters, [filter.key]: e.target.checked }
                                            }))}
                                        />
                                        <span>{filter.label}</span>
                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                            {filter.count || 0}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* اختيار الحقول */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3">📋 الحقول المراد تضمينها</h3>
                            <div className="filter-grid">
                                {[
                                    { key: 'personalInfo', label: '👤 البيانات الشخصية' },
                                    { key: 'medicalInfo', label: '🏥 بيانات الكشف الطبي' },
                                    { key: 'fingerprintInfo', label: '👆 بيانات البصمة' },
                                    { key: 'consulateInfo', label: '🏛️ دخول القنصلية' },
                                    { key: 'visaInfo', label: '📄 بيانات التأشيرة' },
                                    { key: 'travelInfo', label: '✈️ بيانات السفر' },
                                    { key: 'documentsInfo', label: '📦 حالة الأوراق' },
                                     { key: 'notes', label: '📝 الملاحظات' },
  { key: 'listName', label: '📋 اسم القائمة الأصلية' } // ✅ جديد
                                    
                                ].map(field => (
                                    <label key={field.key} className="filter-item">
                                        <input
                                            type="checkbox"
                                            checked={exportSettings.includeFields[field.key]}
                                            onChange={(e) => setExportSettings(prev => ({
                                                ...prev,
                                                includeFields: { ...prev.includeFields, [field.key]: e.target.checked }
                                            }))}
                                        />
                                        <span>{field.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* أزرار التحكم */}
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                            >
                                إلغاء
                            </button>
                            <button
                                onClick={exportData}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center gap-2"
                            >
                                <Download size={20} />
                                تصدير ({getFilteredFollowUps().length} سجل)
                            </button>
                        </div>
                    </div>
                </div>
            )}



            <PeriodicFollowUpActivityLogModal
                isOpen={showActivityLog}
                onClose={() => setShowActivityLog(false)}
                logs={activityLogEntries}
                users={activityLogUsers}
                filters={activityLogFilters}
                setFilters={setActivityLogFilters}
                onRefresh={() => fetchActivityLog(activityLogFilters)}
                isLoading={activityLogLoading}
                cleanupSettings={activityLogCleanupSettings}
                onSaveCleanupSettings={saveActivityLogCleanupSettings}
                onManualCleanup={manualCleanupActivityLog}
            />

            <div className="max-w-[1900px] mx-auto">

            {/* شريط التغييرات المعلقة */}
{Object.keys(pendingChanges).length > 0 && (
    <div className="pending-changes-bar">
        <div className="flex justify-between items-center">
            <div>
                <h3 className="font-bold text-lg">⏳ تغييرات معلقة</h3>
                <p>لديك {Object.keys(pendingChanges).length} تغيير غير محفوظ</p>
            </div>
            <div className="flex gap-3">
                <button
                    onClick={() => setPendingChanges({})}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                >
                    إلغاء الكل
                </button>
                <button
                    onClick={saveAllPendingChanges}
                    disabled={isSaving}
                    className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center gap-2"
                >
                    {isSaving ? (
                        <RefreshCw className="animate-spin" size={18} />
                    ) : (
                        <Save size={18} />
                    )}
                    حفظ جميع التغييرات
                </button>
            </div>
        </div>
    </div>
)}

{/* أدوات التحديد المتعدد */}
{bulkUpdateMode && selectedList && (
    <div className="selection-controls">
        <div className="flex justify-between items-center">
            <div>
                <h3 className="font-bold text-lg">✅ وضع التحديد المتعدد</h3>
                <p>محدد: {selectedPersons.length} شخص</p>
            </div>
            <div className="flex gap-3">
                <button
                    onClick={selectAllPersons}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
                >
                    تحديد الكل
                </button>
                <button
                    onClick={clearSelection}
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
                >
                    إلغاء التحديد
                </button>
                <button
                    onClick={() => setShowBulkModal(true)}
                    disabled={selectedPersons.length === 0}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50"
                >
                    تحديث جماعي
                </button>
                {canPrintSelectedApplicationBarcodes && (
                    <button
                        onClick={printSelectedApplicationBarcodes}
                        disabled={selectedPersons.length === 0}
                        className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                    >
                        <Printer size={16} />
                        طباعة باركود المحدد
                    </button>
                )}
                <button
                    onClick={() => setBulkUpdateMode(false)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                >
                    إنهاء التحديد
                </button>
            </div>
        </div>
    </div>
)}

                {/* 🎯 Modern Header */}
                <div className="main-card">
                  <div className="gradient-header">
    <div className="flex flex-wrap justify-between items-center gap-2">
        {/* العنوان المدمج */}
        <div className="flex items-center gap-2">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Activity size={20} />
            </div>
            <div>
                <h1 className="text-base sm:text-xl font-bold">⚡ المتابعة الدورية المتطورة</h1>
                <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">نظام متابعة ذكي وسريع لجميع مراحل العملاء</p>
            </div>
        </div>
        
        {/* أزرار التحكم */}
        <div className="flex items-center gap-2">
            {canViewActivityLog && (
                <button
                    onClick={() => setShowActivityLog(true)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-all duration-300 text-sm"
                    title="سجل حركات المتابعة الدورية"
                >
                    <Clock size={16} />
                    <span>السجل</span>
                </button>
            )}
            {canOpenApplicationBarcodeSettings && (
                <button
                    onClick={openGeneralApplicationBarcodeSettings}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-all duration-300 text-sm"
                    title="ضبط طباعة رقم الطلب والباركود"
                >
                    <Barcode size={16} />
                    <span>ضبط الباركود</span>
                </button>
            )}
            <button
                onClick={() => navigate('/')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-all duration-300 text-sm"
            >
                <ArrowLeft size={16} />
                <span>الرئيسية</span>
            </button>
        </div>
    </div>

    {/* الإحصائيات المدمجة - صف واحد فقط */}
    {!selectedList && statistics.TotalPersons > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-3 bg-white bg-opacity-10 rounded-xl p-2 backdrop-blur-sm">
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold">{statistics.TotalPersons}</div>
                <div className="text-xs opacity-90">إجمالي</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-emerald-300">{statistics.MedicalPassed}</div>
                <div className="text-xs opacity-90">لائق</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-red-300">{statistics.MedicalFailed}</div>
                <div className="text-xs opacity-90">غير لائق</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-amber-300">{statistics.MedicalPending}</div>
                <div className="text-xs opacity-90">منتظر</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-purple-300">{statistics.FingerprintDone}</div>
                <div className="text-xs opacity-90">البصمة</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-indigo-300">{statistics.VisaIssued}</div>
                <div className="text-xs opacity-90">التأشيرة</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-emerald-300">{statistics.Traveled}</div>
                <div className="text-xs opacity-90">سافروا</div>
            </div>
            <div className="text-center">
                <div className="text-sm sm:text-xl font-bold text-orange-300">{statistics.DocumentsDelivered}</div>
                <div className="text-xs opacity-90">الأوراق</div>
            </div>
        </div>
    )}

    {/* إحصائيات القائمة المحددة */}
    {selectedList && listStatistics.TotalPersons > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-3 bg-white bg-opacity-10 rounded-lg p-2 backdrop-blur-sm">
            <div className="text-center">
                <div className="text-base font-bold">{listStatistics.TotalPersons}</div>
                <div className="text-xs opacity-90">الإجمالي</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-emerald-300">{listStatistics.MedicalPassed}</div>
                <div className="text-xs opacity-90">لائق</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-red-300">{listStatistics.MedicalFailed}</div>
                <div className="text-xs opacity-90">غير لائق</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-amber-300">{listStatistics.MedicalPending}</div>
                <div className="text-xs opacity-90">منتظر</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-purple-300">{listStatistics.FingerprintDone}</div>
                <div className="text-xs opacity-90">البصمة</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-indigo-300">{listStatistics.VisaIssued}</div>
                <div className="text-xs opacity-90">التأشيرة</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-emerald-300">{listStatistics.Traveled}</div>
                <div className="text-xs opacity-90">مسافر</div>
            </div>
            <div className="text-center">
                <div className="text-base font-bold text-orange-300">{listStatistics.DocumentsDelivered}</div>
                <div className="text-xs opacity-90">أوراق</div>
            </div>
        </div>
    )}

    {/* التبويبات */}
    <div className="tab-container">
        <button
            onClick={() => {
                setActiveTab('lists');
                setSelectedList(null);
            }}
            className={`tab-button ${activeTab === 'lists' && !selectedList ? 'active' : ''}`}
        >
            <FileText size={18} />
            القوائم ({lists.length})
        </button>

        {canViewUnifiedListButton && (
            <button
                onClick={() => {
                    setActiveTab('lists');
                    fetchAllPersons();
                }}
                className={`tab-button ${selectedList?.IsUnified ? 'active' : ''}`}
                style={{
                    background: selectedList?.IsUnified 
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                        : 'transparent',
                    color: selectedList?.IsUnified ? 'white' : '#64748b',
                    fontWeight: 'bold',
                    border: '2px solid rgb(var(--border-amber-500, 245 158 11))'
                }}
            >
                <BarChart3 size={18} />
                📊 القائمة الموحدة
            </button>
        )}

        <button
            onClick={() => {
                setActiveTab('search');
                setSelectedList(null);
            }}
            className={`tab-button ${activeTab === 'search' ? 'active' : ''}`}
        >
            <Search size={18} />
            البحث الشامل
        </button>

           {canSendToDelegate && (
            <button
                onClick={() => {
                    setActiveTab('delegates');
                    if (selectedList) {
                        if (selectedList.IsUnified) {
                            fetchDelegatesDataForFiltered(followUps);
                        } else {
                            fetchDelegatesData();
                        }
                    }
                }}
                className={`tab-button ${activeTab === 'delegates' ? 'active' : ''}`}
            >
                <Users size={18} />
                إرسال للمناديب ({selectedList ? delegatesData.length : 0})
            </button>
        )}
    </div>
</div>



{/* 🌟 العرض الموحد الجديد */}
{activeTab === 'unified' && allPersonsView && (
    <div className="p-8">
        <div className="flex justify-between items-center mb-8">
            <div>
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <BarChart3 className="text-orange-600" size={32} />
                    📊 العرض الموحد لكل القوائم
                </h2>
                <p className="text-gray-600 mt-2">
                    جميع الأشخاص من كل القوائم الظاهرة • إجمالى الحضور: {allPersons.length} شخص
                </p>
            </div>
            <div className="flex gap-3">
                <button
                    onClick={fetchAllPersons}
                    disabled={loadingAllPersons}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 transition-all duration-300 shadow-lg"
                >
                    {loadingAllPersons ? (
                        <RefreshCw className="animate-spin" size={18} />
                    ) : (
                        <RefreshCw size={18} />
                    )}
                    تحديث البيانات
                </button>
            </div>
        </div>

        {/* 📊 إحصائيات مميزة للعرض الموحد */}
        {allPersonsStats.TotalPersons > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="text-3xl font-bold">{allPersonsStats.TotalPersons}</div>
                    <div className="text-blue-100 text-sm mt-1">إجمالي الأشخاص</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="text-3xl font-bold">{allPersonsStats.TotalLists}</div>
                    <div className="text-purple-100 text-sm mt-1">عدد القوائم</div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="text-3xl font-bold">{allPersonsStats.MedicalPassed}</div>
                    <div className="text-emerald-100 text-sm mt-1">لائق طبياً</div>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="text-3xl font-bold">{allPersonsStats.FingerprintDone}</div>
                    <div className="text-amber-100 text-sm mt-1">تم البصمة</div>
                </div>
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="text-3xl font-bold">{allPersonsStats.VisaIssued}</div>
                    <div className="text-indigo-100 text-sm mt-1">صدرت التأشيرة</div>
                </div>
                <div className="bg-gradient-to-br from-rose-500 to-rose-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="text-3xl font-bold">{allPersonsStats.Traveled}</div>
                    <div className="text-rose-100 text-sm mt-1">سافروا</div>
                </div>
            </div>
        )}

        {/* نفس الفلاتر والجدول الموجود في القائمة العادية ولكن للبيانات الموحدة */}
        {loadingAllPersons ? (
            <div className="text-center py-16">
                <RefreshCw className="animate-spin mx-auto h-16 w-16 text-orange-600 mb-6" />
                <h3 className="text-xl font-medium text-gray-900 mb-4">جاري تحميل البيانات من كل القوائم...</h3>
            </div>
        ) : allPersons.length > 0 ? (
            <div>
                <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-6 mb-6">
                    <h3 className="text-lg font-bold text-orange-900 mb-4">🎯 الفلاتر والبحث</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* بحث */}
                        <div className="relative">
                            <Search size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="🔍 ابحث في كل القوائم بالاسم، رقم الملف، الجواز..."
                                value={listSearchTerm}
                                onChange={(e) => setListSearchTerm(e.target.value)}
                                className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500"
                            />
                        </div>

                        {/* فلتر المرحلة */}
                        <select
                            value={stageFilter}
                            onChange={(e) => setStageFilter(e.target.value)}
                            className="border border-gray-300 rounded-xl px-4 py-3 bg-white focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="all">جميع المراحل</option>
                            
                            {/* 🚫 الفلاتر العكسية أولاً */}
                            <option value="exclude_medical_passed">🚫 الكل ماعدا اللائق طبياً</option>
                            <option value="exclude_fingerprint_done">🚫 الكل ماعدا المبصمين</option>
                            <option value="exclude_consulate_done">🚫 الكل ماعدا من دخل القنصلية</option>
                            <option value="exclude_visa_done">🚫 الكل ماعدا من صدرت تأشيرته</option>
                            <option value="exclude_traveled">🚫 الكل ماعدا المسافرين</option>
                            <option value="exclude_documents_delivered">🚫 الكل ماعدا من سلم أوراقه</option>
                            
                            {/* المراحل العادية */}
                            <option value="medical_pending">في انتظار الكشف الطبي</option>
                            <option value="medical_passed">طبي لائق</option>
                            <option value="medical_failed">طبي غير لائق</option>
                            <option value="fingerprint_pending">في انتظار البصمة</option>
                            <option value="fingerprint_done">تم البصمة</option>
                            <option value="consulate_done">دخل القنصلية 🏛️</option>
                            <option value="visa_pending">في انتظار التأشيرة</option>
                            <option value="visa_done">صدرت التأشيرة</option>
                            <option value="travel_ready">جاهز للسفر</option>
                            <option value="traveled">سافر</option>
                          <option value="documents_not_delivered">لم يسلم أوراق</option>
                            <option value="documents_delivered">سلم الأوراق</option>
                            <option value="insurance_done">تم التأمين الطبي ✅</option>
                            <option value="insurance_pending">لم يتم التأمين الطبي ❌</option>
                        </select>

                        {/* زر فلاتر التواريخ */}
                        <button
                            onClick={() => setShowDateFilters(true)}
                            className="border border-orange-300 rounded-xl px-4 py-3 bg-white hover:bg-orange-50 transition-all flex items-center gap-2"
                        >
                            <Calendar size={18} />
                            📅 فلاتر التواريخ
                        </button>
                    </div>
                </div>

                {/* استخدام نفس منطق الفلترة */}
                <FollowUpMobileCards persons={getFilteredFollowUps()} />
                <div className="pfu-desktop-table overflow-x-auto bg-white rounded-2xl shadow-xl">
                    <p className="text-center text-gray-500 p-4">
                        النتائج المفلترة: {
                            (() => {
                                // تطبيق نفس الفلاتر على allPersons
                                let filtered = allPersons;
                                
                                // فلتر البحث
                                if (listSearchTerm && listSearchTerm.trim().length >= 2) {
                                    const searchLower = listSearchTerm.toLowerCase();
                                    filtered = filtered.filter(person =>
                                        followUpMatchesFileNumber(person, listSearchTerm) ||
                                        person.NameArabic?.toLowerCase().includes(searchLower) ||
                                        person.PassportNumber?.toLowerCase().includes(searchLower) ||
                                        person.CompanyName?.toLowerCase().includes(searchLower) ||
                                        person.DelegateName?.toLowerCase().includes(searchLower) ||
                                        person.ListName?.toLowerCase().includes(searchLower)
                                    );
                                }
                                
                                // فلتر المرحلة
                                if (stageFilter !== 'all') {
                                    filtered = filtered.filter(person => {
                                        switch (stageFilter) {
                                            case 'medical_pending':
                                                return !person.MedicalExamDate;
                                            case 'medical_passed':
                                                return person.MedicalExamStatus === 'لائق';
                                            case 'medical_failed':
                                                return person.MedicalExamStatus === 'غير لائق';
                                            case 'fingerprint_pending':
                                                return person.MedicalExamStatus === 'لائق' && !person.FingerprintDate;
                                            case 'fingerprint_done':
                                                return person.FingerprintDate;
                                            case 'visa_pending':
                                                return person.FingerprintDate && !person.VisaDate;
                                            case 'visa_done':
                                                return person.VisaDate;
                                            case 'travel_ready':
                                                return person.VisaDate && !person.TravelDate;
                                            case 'traveled':
                                                return person.TravelDate;
                                            case 'documents_not_delivered':
                                                return !person.DocumentsDeliveryStatus || person.DocumentsDeliveryStatus === 'لم يسلم';
                                    case 'documents_delivered':
                                                return person.DocumentsDeliveryStatus === 'سلم';
                                            case 'insurance_done':
                                                return !!person.MedicalInsuranceDate;
                                            case 'insurance_pending':
                                                return !person.MedicalInsuranceDate;
                                            default:
                                                return true;
                                        }
                                    });
                                }
                                
                                return filtered.length;
                            })()
                        } شخص
                    </p>
                    
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>م</th>
                                <th>📋 القائمة</th>
                                <th>الاسم</th>
                                <th>الشركة</th>
                                <th>المندوب</th>
                                <th>الهاتف</th>
                                <th>الكشف الطبي</th>
                                <th>البصمة</th>
                                <th>التأشيرة</th>
                                <th>السفر</th>
                                <th>الأوراق</th>
                                <th>المرحلة</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                // تطبيق الفلاتر
                                let filtered = allPersons;
                                
                                if (listSearchTerm && listSearchTerm.trim().length >= 2) {
                                    const searchLower = listSearchTerm.toLowerCase();
                                    filtered = filtered.filter(person =>
                                        followUpMatchesFileNumber(person, listSearchTerm) ||
                                        person.NameArabic?.toLowerCase().includes(searchLower) ||
                                        person.PassportNumber?.toLowerCase().includes(searchLower) ||
                                        person.CompanyName?.toLowerCase().includes(searchLower) ||
                                        person.DelegateName?.toLowerCase().includes(searchLower) ||
                                        person.ListName?.toLowerCase().includes(searchLower)
                                    );
                                }
                                
                                if (stageFilter !== 'all') {
                                    filtered = filtered.filter(person => {
                                        switch (stageFilter) {
                                            case 'medical_pending': return !person.MedicalExamDate;
                                            case 'medical_passed': return person.MedicalExamStatus === 'لائق';
                                            case 'medical_failed': return person.MedicalExamStatus === 'غير لائق';
                                            case 'fingerprint_pending': return person.MedicalExamStatus === 'لائق' && !person.FingerprintDate;
                                            case 'fingerprint_done': return person.FingerprintDate;
                                            case 'visa_pending': return person.FingerprintDate && !person.VisaDate;
                                            case 'visa_done': return person.VisaDate;
                                            case 'travel_ready': return person.VisaDate && !person.TravelDate;
                                            case 'traveled': return person.TravelDate;
                                            case 'documents_not_delivered': return !person.DocumentsDeliveryStatus || person.DocumentsDeliveryStatus === 'لم يسلم';
                                   case 'documents_delivered': return person.DocumentsDeliveryStatus === 'سلم';
                                            case 'insurance_done': return !!person.MedicalInsuranceDate;
                                            case 'insurance_pending': return !person.MedicalInsuranceDate;
                                            default: return true;
                                        }
                                    });
                                }
                                
                                return filtered.map((person, index) => (
                                    <tr key={person.FollowUpID}>
                                        <td>
                                            <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                                {index + 1}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-bold">
                                                📋 {person.ListName}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="font-semibold text-gray-900 text-sm">
                                                {person.NameArabic}
                                            </div>
                                        </td>
                                        <td className="text-sm">{person.CompanyName}</td>
                                        <td className="text-sm">{person.DelegateName || '-'}</td>
                                        <td className="text-sm">{getPhoneNumber(person.PhoneNumbers)}</td>
                                        <td className="text-sm">{person.MedicalExamStatus || '-'}</td>
                                        <td className="text-sm">{person.FingerprintDate ? formatDate(person.FingerprintDate) : '-'}</td>
                                        <td className="text-sm">{person.VisaDate ? formatDate(person.VisaDate) : '-'}</td>
                                        <td className="text-sm">{person.TravelDate ? formatDate(person.TravelDate) : '-'}</td>
                                        <td className="text-sm">{person.DocumentsDeliveryStatus || 'لم يسلم'}</td>
                                        <td>
                                            <div className="flex items-center gap-2 text-sm">
                                                {getStageIcon(person.CurrentStage)}
                                                <span>{person.CurrentStage}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ));
                            })()}
                        </tbody>
                    </table>
                </div>
            </div>
        ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
                <FileText className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                <h3 className="text-xl font-medium text-gray-900 mb-4">لا توجد بيانات</h3>
                <p className="text-gray-600">لا توجد قوائم ظاهرة حالياً</p>
            </div>
        )}
    </div>
)}

{activeTab === 'lists' && !selectedList && (
    <div className="p-3 sm:p-4">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText size={20} className="text-blue-600" />
                📋 إدارة قوائم المتابعة الدورية
            </h2>
            <button
                onClick={() => {
                    fetchLists();
                    fetchListsStats();
                }}
                disabled={isLoading}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 text-sm"
            >
                {isLoading ? (
                    <RefreshCw className="animate-spin" size={16} />
                ) : (
                    <RefreshCw size={16} />
                )}
                تحديث
            </button>
        </div>

        {/* شريط الفلترة والإحصائيات - نسخة مدمجة */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3 mb-4 border border-blue-200">
            {/* الإحصائيات السريعة في صف واحد */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <div className="text-lg font-bold text-blue-600">{listsStats.TotalLists || 0}</div>
                    <div className="text-xs text-gray-600">إجمالي القوائم</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <div className="text-lg font-bold text-green-600">{listsStats.VisibleLists || 0}</div>
                    <div className="text-xs text-gray-600">قوائم ظاهرة</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <div className="text-lg font-bold text-gray-600">{listsStats.HiddenLists || 0}</div>
                    <div className="text-xs text-gray-600">قوائم مخفية</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <div className="text-lg font-bold text-emerald-600">{listsStats.VisiblePersons || 0}</div>
                    <div className="text-xs text-gray-600">أشخاص ظاهرين</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <div className="text-lg font-bold text-orange-600">{listsStats.HiddenPersons || 0}</div>
                    <div className="text-xs text-gray-600">أشخاص مخفيين</div>
                </div>
            </div>

            {/* أدوات الفلترة في صف واحد */}
            <div className="flex flex-wrap gap-2">
                {/* البحث */}
                <div className="relative">
                    <Search size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="🔍 ابحث في أسماء القوائم..."
                        value={listsFilter.search}
                        onChange={(e) => applyListsFilter({ search: e.target.value })}
                        className="w-full pl-3 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                </div>

                {/* فلتر الرؤية - يظهر فقط لمن لديه صلاحية */}
                {canToggleVisibility && (
                <select
                    value={listsFilter.showHidden.toString()}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'all') {
                            applyListsFilter({ showHidden: 'all' });
                        } else {
                            applyListsFilter({ showHidden: value === 'true' });
                        }
                    }}
                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                >
                    <option value="false">📋 الظاهرة ({listsStats.VisibleLists || 0})</option>
                    <option value="true">🙈 المخفية ({listsStats.HiddenLists || 0})</option>
                    <option value="all">📊 الكل ({listsStats.TotalLists || 0})</option>
                </select>
                )}

                {/* الترتيب */}
                <select
                    value={listsFilter.sortBy}
                    onChange={(e) => applyListsFilter({ sortBy: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                >
                    <option value="recent">🕒 الأحدث</option>
                    <option value="oldest">📅 الأقدم</option>
                    <option value="name">🔤 الاسم</option>
                    <option value="count">📊 العدد</option>
                </select>

                {/* عدد العناصر */}
                <select
                    value={listsFilter.limit}
                    onChange={(e) => applyListsFilter({ limit: parseInt(e.target.value) })}
                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                >
                    <option value="6">6 قوائم/صفحة</option>
                    <option value="12">12 قائمة/صفحة</option>
                    <option value="24">24 قائمة/صفحة</option>
                    <option value="48">48 قائمة/صفحة</option>
                </select>
            </div>
        </div>

{/* 🌟 القائمة الموحدة - نسخة مصغرة جداً */}
{canViewUnifiedListBanner && (
<button
    onClick={() => fetchAllPersons()}
    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg p-2.5 mb-4 hover:shadow-lg transition-all duration-300 border border-orange-600"
>
    <div className="flex justify-between items-center text-white">
        <div className="flex items-center gap-2">
            <BarChart3 size={18} />
            <span className="font-bold text-sm">📊 القائمة الموحدة (القوائم الظاهرة) ⭐</span>
        </div>
        
    <div className="flex flex-wrap items-center gap-2 text-xs mt-1 sm:mt-0">
    <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
        {listsStats.VisiblePersons || 0} شخص • {listsStats.VisibleLists || 0} قائمة
    </span>
    <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
        {visibleUnifiedStats.MedicalPassed || 0} لائق
    </span>
    <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
        {visibleUnifiedStats.FingerprintDone || 0} بصمة
    </span>
    <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
        {visibleUnifiedStats.VisaIssued || 0} تأشيرة
    </span>
    <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
        {visibleUnifiedStats.Traveled || 0} سافروا
    </span>
</div>

    </div>
</button>
)}

        {/* المحتوى الرئيسي */}
        {isLoading ? (
            <div className="text-center py-16">
                <RefreshCw className="animate-spin mx-auto h-16 w-16 text-blue-600 mb-6" />
                <h3 className="text-xl font-medium text-gray-900 mb-4">جاري تحميل القوائم...</h3>
            </div>
        ) : lists.length > 0 ? (
            <>
                {/* عرض نتائج البحث */}
      {/* عرض نتائج البحث */}
{listsFilter.search && (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Search size={20} className="text-blue-600" />
                <span className="text-blue-800 font-medium">
                    نتائج البحث: {pagination?.totalCount || 0} قائمة
                </span>
            </div>
            <button
                onClick={() => applyListsFilter({ search: '' })}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
                مسح البحث
            </button>
        </div>
    </div>
)}

                {/* بطاقات القوائم */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {lists.map(list => (
                        <ListCard key={list.ListID} list={list} />
                    ))}
                </div>

                {/* التنقل بين الصفحات */}
                <PaginationComponent />
                
             {/* معلومات إضافية */}
<div className="mt-8 text-center text-sm text-gray-500">
    عرض {lists.length} من أصل {pagination?.totalCount || 0} قائمة
    {listsFilter.showHidden && ' (القوائم المخفية)'}
</div>
            </>
        ) : (
            <div className="text-center py-16">
                {listsFilter.search ? (
                    <>
                        <Search className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 mb-4">
                            لم يتم العثور على قوائم تطابق "{listsFilter.search}"
                        </h3>
                        <p className="text-gray-600 mb-6">
                            جرب تغيير مصطلح البحث أو مسح الفلاتر
                        </p>
                        <button
                            onClick={() => applyListsFilter({ search: '' })}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
                        >
                            مسح البحث وعرض جميع القوائم
                        </button>
                    </>
                ) : listsFilter.showHidden ? (
                    <>
                        <EyeOff className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 mb-4">لا توجد قوائم مخفية</h3>
                        <p className="text-gray-600 mb-6">
                            جميع القوائم ظاهرة حالياً. يمكنك إخفاء القوائم المكتملة لتنظيم أفضل.
                        </p>
                        <button
                            onClick={() => applyListsFilter({ showHidden: false })}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl transition-colors"
                        >
                            عرض القوائم الظاهرة
                        </button>
                    </>
                ) : (
                    <>
                        <FileText className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 mb-4">لا توجد قوائم متابعة</h3>
                        <p className="text-gray-600 mb-6">
                            القوائم ستظهر هنا عند تأكيد أشخاص من صفحة الاختيار المطور
                        </p>
                        <div className="mt-6">
                            <button
                                onClick={() => navigate('/')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors mr-3"
                            >
                                الذهاب للاختيار المطور
                            </button>
                            <button
                                onClick={() => {
                                    fetchLists();
                                    fetchListsStats();
                                }}
                                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-xl transition-colors"
                            >
                                تحديث
                            </button>
                        </div>
                    </>
                )}
            </div>
        )}
    </div>
)}
{activeTab === 'lists' && selectedList && (
    <div className="p-3 sm:p-6">
        <div className="flex flex-wrap justify-between items-start mb-4 gap-2">
            <div>
               <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
    {selectedList.IsUnified ? (
        <>
            <BarChart3 className="text-orange-600" size={32} />
            <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                {selectedList.ListName}
            </span>
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                قائمة مميزة ⭐
            </span>
        </>
    ) : (
        <>
            <Target className="text-blue-600" size={28} />
            {selectedList.ListName}
        </>
    )}
</h2>
                <p className="text-gray-600 mt-2">إجمالي العملاء: {followUps.length} - ⚡ اضغط على أي خانة للتعديل الفوري</p>
            </div>
         <div className="flex flex-wrap gap-2 mt-3 sm:mt-0">
    {/* ✅ البحث داخل القائمة */}
    <div className="list-search-container">
        <Search size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
            type="text"
            placeholder="🔍 بحث في القائمة بالاسم، الجواز، رقم الملف، الشركة..."
            value={listSearchTerm}
            onChange={(e) => setListSearchTerm(e.target.value)}
            className="list-search-input"
        />
    </div>

<select
    value={stageFilter}
    onChange={(e) => setStageFilter(e.target.value)}
    className="border border-gray-300 rounded-xl px-2 py-2 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 text-sm"
>
    <option value="all">جميع المراحل</option>
    
    {/* 🚫 الفلاتر العكسية أولاً */}
    <option value="exclude_medical_passed">🚫 الكل ماعدا اللائق طبياً</option>
    <option value="exclude_fingerprint_done">🚫 الكل ماعدا المبصمين</option>
    <option value="exclude_consulate_done">🚫 الكل ماعدا من دخل القنصلية</option>
    <option value="exclude_visa_done">🚫 الكل ماعدا من صدرت تأشيرته</option>
    <option value="exclude_traveled">🚫 الكل ماعدا المسافرين</option>
    <option value="exclude_documents_delivered">🚫 الكل ماعدا من سلم أوراقه</option>
    
    {/* المراحل العادية */}
    <option value="medical_pending">في انتظار الكشف الطبي</option>
    <option value="medical_passed">طبي لائق</option>
    <option value="medical_failed">طبي غير لائق</option>
    <option value="fingerprint_pending">في انتظار البصمة</option>
    <option value="fingerprint_done">تم البصمة</option>
    <option value="consulate_done">دخل القنصلية 🏛️</option>
    <option value="visa_pending">في انتظار التأشيرة</option>
    <option value="visa_done">صدرت التأشيرة</option>
    <option value="travel_ready">جاهز للسفر</option>
    <option value="traveled">سافر</option>
<option value="documents_not_delivered">لم يسلم أوراق</option>
    <option value="documents_partial">📄 سلم البعض وينقصه شيء</option>
    <option value="documents_delivered">سلم الأوراق</option>
    <option value="insurance_done">تم التأمين الطبي ✅</option>
    <option value="insurance_pending">لم يتم التأمين الطبي ❌</option>
</select>

    {/* ✅ زر فلاتر التواريخ الجديد */}
    <button
        onClick={() => setShowDateFilters(true)}
        className={`border border-gray-300 rounded-xl px-2 py-2 bg-white shadow-sm hover:bg-gray-50 transition-all duration-300 flex items-center gap-1 text-sm ${
            Object.values(dateFilters).some(filter => filter.enabled) ? 'bg-blue-50 border-blue-300 text-blue-700' : ''
        }`}
    >
        <Calendar size={18} />
        📅 فلاتر التواريخ
        {Object.values(dateFilters).some(filter => filter.enabled) && (
            <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                {Object.values(dateFilters).filter(filter => filter.enabled).length}
            </span>
        )}
    </button>

    {/* 🔃 ترتيب البيانات */}
    <div className="flex items-center gap-0 border border-gray-300 rounded-xl bg-white shadow-sm text-sm" style={{overflow: 'visible'}}>
        <select
            value={listSortBy}
            onChange={(e) => setListSortBy(e.target.value)}
            className="px-2 py-2 bg-white focus:ring-0 focus:outline-none text-xs rounded-l-xl border-0"
            title="ترتيب حسب"
        >
            <option value="addedDate">📅 تاريخ الإضافة</option>
            <option value="name">🔤 الاسم أبجدياً</option>
        </select>
        <div className="w-px h-8 bg-gray-300" />
        <button
            onClick={() => setListSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-2 hover:bg-gray-100 transition-colors rounded-r-xl text-blue-600 font-bold text-sm flex items-center justify-center min-w-[36px]"
            title={listSortDir === 'asc' ? 'تصاعدي — اضغط للتنازلي' : 'تنازلي — اضغط للتصاعدي'}
        >
            {listSortDir === 'asc' ? '▲' : '▼'}
        </button>
    </div>

    {/* ✅ زر التصدير المتقدم */}
    <button
        onClick={() => setShowExportModal(true)}
        className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-3 py-2 rounded-xl flex items-center gap-1 transition-all duration-300 text-sm"
    >
        <Download size={16} />
        <span className="hidden sm:inline">تصدير </span>({getFilteredFollowUps().length})
    </button>

                <button
                    onClick={() => setShowPrintModal(true)}
                    className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-3 py-2 rounded-xl flex items-center gap-1 transition-all duration-300 text-sm"
                >
                    <Printer size={16} />
                    <span className="hidden sm:inline">طباعة</span>
                    <span className="sm:hidden">🖨</span>
                </button>

<button
  onClick={() => setShowImageExportModal(true)}
  className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-3 py-2 rounded-xl flex items-center gap-1 shadow-sm transition-all duration-300 text-sm"
>
  <ImageIcon size={16} />
  <span className="hidden sm:inline">تصدير كصورة</span>
  <span className="sm:hidden">📸</span>
</button>






          

                      {canBulkUpdate && (
                    <button
                        onClick={() => setBulkUpdateMode(!bulkUpdateMode)}
                        className={`${bulkUpdateMode ? 'bg-gradient-to-r from-red-600 to-red-700' : 'bg-gradient-to-r from-green-600 to-green-700'} hover:opacity-80 text-white px-3 py-2 rounded-xl flex items-center gap-1 transition-all duration-300 text-sm`}
                    >
                        <CheckSquare size={16} />
                        <span className="hidden sm:inline">{bulkUpdateMode ? 'إنهاء التحديد' : 'تحديث جماعي'}</span>
                        <span className="sm:hidden">{bulkUpdateMode ? 'إنهاء' : 'تحديث'}</span>
                    </button>
                )}

              <button
    onClick={() => {
        setSelectedList(null);
        setActiveTab('lists');
    }}
    className={`${selectedList?.IsUnified 
        ? 'bg-gradient-to-r from-orange-600 to-amber-700 hover:from-orange-700 hover:to-amber-800' 
        : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
    } text-white px-3 py-2 rounded-xl flex items-center gap-1 transition-all duration-300 text-sm`}
>
                    <ArrowLeft size={16} />
                    <span className="hidden sm:inline">العودة للقوائم</span>
                    <span className="sm:hidden">رجوع</span>
                </button>
            </div>
        </div>



        {getFilteredFollowUps().length > 0 ? (
            <>
            <FollowUpMobileCards persons={getFilteredFollowUps()} />
                <div className="pfu-desktop-table overflow-x-auto bg-white rounded-2xl shadow-xl">
                <table className="modern-table" style={{minWidth: "920px"}}>
                 <thead>
    <tr>
       {bulkUpdateMode && <th>✅</th>}
        <th>م</th>
        {selectedList?.IsUnified && <th>📋 القائمة</th>}
        <th className="sticky-name bg-[#22407a]">الاسم</th>
                            <th>الشركة</th>
                            <th>المهنة</th>
                            <th>الجواز</th>
                            {canShowColAppNumber && <th>⚡ رقم الطلب</th>}
                            {canShowColPhone && <th>الهاتف</th>}
                            <th>المندوب</th>
                            <th>التجنيد</th>
                            {canShowColNetDate && <th>تاريخ النت</th>}
                         {canShowColMedical && <th>⚡ الكشف الطبي</th>}
                        {canShowColFingerprint && <th>⚡ البصمة</th>}
                           {canShowColInsurance && <th>🏥 التأمين الطبي</th>}
                           {canShowColDocuments && <th>⚡ تسليم الأوراق</th>}
                            {canShowColNotes && <th>⚡ ملاحظات</th>}
                            {canShowColConsulate && <th>⚡ دخول القنصلية 🏛️</th>}
                            {canShowColVisa && <th>⚡ التأشيرة</th>}
                         {canShowColTravel && <th>⚡ السفر</th>}
                         {canShowColBorderNumber && <th>⚡ رقم الحدود</th>}
                            <th>المرحلة</th>
                          {canShowColAppNumber && canPrintApplicationBarcodeExecute && <th>طباعة</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {getFilteredFollowUps().map((person, index) => (
                            <tr key={person.FollowUpID}>
                                {/* الرقم التسلسلي */}
                            {bulkUpdateMode && (
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedPersons.includes(person.FollowUpID)}
                                        onChange={() => togglePersonSelection(person.FollowUpID)}
                                        className="w-5 h-5"
                                    />
                                </td>
                            )}
                            <td>
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                    {index + 1}
                                </div>
                            </td>
                            {selectedList?.IsUnified && (
    <td>
        <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-bold">
            📋 {person.ListName}
        </span>
    </td>
)}
                                
                                {/* الاسم */}
                                <td className="sticky-name bg-white min-w-[8.5rem]">
                                    <div className="font-semibold text-gray-900 text-sm">
                                        {person.NameArabic}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        منذ {person.DaysSinceNet} يوم
                                    </div>
                                </td>
                                
                                {/* الشركة */}
                                <td>
                                    <div className="text-sm font-medium text-gray-900">
                                        {person.CompanyName}
                                    </div>
                                    <div className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded-full inline-block mt-1">
                                        ID: {getFollowUpFileNumber(person)}
                                    </div>
                                </td>
                                
                                {/* المهنة */}
                                <td>
                                    <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">
                                        {person.ProfessionName || 'غير محدد'}
                                    </span>
                                </td>
                                
                                {/* رقم الجواز */}
                                <td>
                                    <span className="font-mono text-sm bg-gray-100 px-3 py-1 rounded-lg">
                                        {person.PassportNumber || 'غير محدد'}
                                    </span>
                                </td>
                                

                            {/* ⚡ رقم الطلب - مع تنظيف */}
                            {canShowColAppNumber && (
                            <td>
                                <EnhancedEditCell
                                    person={person}
                                    field="ApplicationNumber"
                                    currentValue={cleanApplicationNumber(person.CleanApplicationNumber || person.ApplicationNumber) || ''}
                                    type="text"
                                />
                            </td>
                            )}
                            {/* ⚡ أرقام الهواتف - إدارة متقدمة */}
                            {canShowColPhone && (
                            <td className="min-w-40">
                                <PhoneNumbersManager 
                                    person={person} 
                                    onUpdate={ultraFastUpdateWithoutRefresh}
                                    readOnly={!canEnterPhone}
                                />
                            </td>
                            )}
                                
                               {/* المندوب */}
                                                    <td>
                                                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                                            {person.DelegateName || 'غير محدد'}
                                                        </span>
                                                    </td>
                                
                                {/* الموقف من التجنيد */}
                                <td>
                                    <span className={`military-status-badge ${getMilitaryStatusColor(person.FinalMilitaryStatus || person.MilitaryStatus)}`}>
                                        {person.FinalMilitaryStatus || person.MilitaryStatus || 'غير محدد'}
                                    </span>
                                </td>
                                
                                {/* تاريخ النت */}
                                {canShowColNetDate && (
                                <td>
                                    <div className="text-sm font-medium text-emerald-600">
                                        {formatDate(person.NetWorkDate)}
                                    </div>
                                </td>
                                )}
                                
                                {/* ⚡ الكشف الطبي - تعديل فوري */}
                                {canShowColMedical && (
                                <td>
                                    <EnhancedEditCell
                                        person={person}
                                        field="MedicalExamDate"
                                        currentValue={person.MedicalExamDate ? person.MedicalExamDate.split('T')[0] : ''}
                                        type="date"
                                    />
                                    <div className="mt-1">
                                        <EnhancedEditCell
                                            person={person}
                                            field="MedicalExamStatus"
                                            currentValue={person.MedicalExamStatus || 'قيد التحميل'}
                                            type="select"
                                        />
                                    </div>
                                </td>
                                )}
                                
                                {/* ⚡ البصمة - تعديل فوري */}
                                {canShowColFingerprint && (
                                <td>
                                    <EnhancedEditCell
                                        person={person}
                                        field="FingerprintDate"
                                        currentValue={person.FingerprintDate ? person.FingerprintDate.split('T')[0] : ''}
                                        type="date"
                                    />
                                </td>
                                )}
                             {/* 🏥 التأمين الطبي - تعديل فوري */}
                                {canShowColInsurance && (
                                <td>
                                    <EnhancedEditCell
                                        person={person}
                                        field="MedicalInsuranceDate"
                                        currentValue={person.MedicalInsuranceDate ? person.MedicalInsuranceDate.split('T')[0] : ''}
                                        type="date"
                                    />
                                </td>
                                )}
                                {/* ⚡ تسليم الأوراق - تعديل فوري */}
                                {canShowColDocuments && (
                                <td>
                                    <EnhancedEditCell
                                        person={person}
                                        field="DocumentsDeliveryStatus"
                                        currentValue={person.DocumentsDeliveryStatus || 'لم يسلم'}
                                        type="select"
                                    />
                                </td>
                                )}
                                {/* ⚡ الملاحظات - تعديل فوري */}
                                {canShowColNotes && (
                                <td className="max-w-32">
                                    <EnhancedEditCell
                                        person={person}
                                        field="Notes"
                                        currentValue={person.Notes || ''}
                                        type="textarea"
                                    />
                                </td>
                                )}
                                
                                {/* ⚡ دخول القنصلية - تعديل فوري */}
                                {canShowColConsulate && (
                                <td>
                                    <EnhancedEditCell
                                        person={person}
                                        field="ConsulateEntryDate"
                                        currentValue={person.ConsulateEntryDate ? person.ConsulateEntryDate.split('T')[0] : ''}
                                        type="date"
                                    />
                                </td>
                                )}
                                
                                {/* ⚡ التأشيرة - تعديل فوري + رفع ملف التأشيرة */}
                                {canShowColVisa && (
                                <td>
                                    <div className="flex flex-col items-center gap-2">
                                        <EnhancedEditCell
                                            person={person}
                                            field="VisaDate"
                                            currentValue={person.VisaDate ? person.VisaDate.split('T')[0] : ''}
                                            type="date"
                                        />
                                        <VisaFileUploadCell person={person} />
                                    </div>
                                </td>
                                )}
                                {/* ⚡ السفر - تعديل فوري */}
                                {canShowColTravel && (
                                <td>
                                 <EnhancedEditCell
    person={person}
    field="TravelDate"
    currentValue={person.TravelDate ? person.TravelDate.slice(0, 16) : ''}
    type="datetime-local"
/>

                                </td>
                                )}
                                {/* ⚡ رقم الحدود */}
                                {canShowColBorderNumber && (
                                <td>
                                    <EnhancedEditCell
                                        person={person}
                                        field="BorderNumber"
                                        currentValue={person.BorderNumber || ''}
                                        type="text"
                                    />
                                </td>
                                )}
                                {/* المرحلة الحالية */}
                                <td>
                                    <div className="flex items-center gap-2">
                                        {getStageIcon(person.CurrentStage)}
                                        <span className="text-sm font-medium">
                                            {person.CurrentStage}
                                        </span>
                                    </div>
                                </td>
                                {canShowColAppNumber && canPrintApplicationBarcodeExecute && (
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => printApplicationBarcodeDirect(person)}
                                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-black shadow-sm"
                                        title="تنفيذ طباعة رقم الطلب بالإعدادات المحفوظة"
                                    >
                                        <Printer size={15} />
                                        تنفيذ
                                    </button>
                                </td>
                                )}
                                
        
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </>
        ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
                <Users className="mx-auto h-16 w-16 text-gray-400 mb-6" />
                <h3 className="text-xl font-medium text-gray-900 mb-4">لا توجد نتائج</h3>
                <p className="text-gray-600">جرب تغيير فلتر المرحلة أو مصطلح البحث أو إعدادات فلاتر التواريخ</p>
                <div className="mt-4 flex justify-center gap-3">
                    <button
                        onClick={() => {
                            setStageFilter('all');
                            setListSearchTerm('');
                      setDateFilters({
                                netDate: { enabled: false, startDate: '', endDate: '' },
                                medicalDate: { enabled: false, startDate: '', endDate: '' },
                                fingerprintDate: { enabled: false, startDate: '', endDate: '' },
                                consulateDate: { enabled: false, startDate: '', endDate: '' },
                                visaDate: { enabled: false, startDate: '', endDate: '' },
                                travelDate: { enabled: false, startDate: '', endDate: '' },
                                insuranceDate: { enabled: false, startDate: '', endDate: '' }
                            });
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
                    >
                        إعادة تعيين جميع الفلاتر
                    </button>
                </div>
            </div>
        )}
    </div>
)}

                    {/* 🔍 Enhanced Search Tab */}
                    {activeTab === 'search' && (
                        <div className="p-8">
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-2xl font-bold text-gray-900">🔍 البحث الشامل المتطور</h2>
                            </div>

                            {/* شريط البحث المحسن */}
                            <div className="search-container">
                                <div className="relative">
                                    <Search size={24} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="🔍 ابحث بالاسم، رقم الملف، رقم الجواز، القائمة، الشركة، المندوب، أو الهاتف..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            if (e.target.value.length >= 2) {
                                                searchAllLists(e.target.value);
                                            } else {
                                                setSearchResults([]);
                                            }
                                        }}
                                        className="search-input"
                                    />
                                </div>
                                {searchTerm.length > 0 && searchTerm.length < 2 && (
                                    <p className="text-sm text-gray-500 mt-3 mr-4">💡 أدخل على الأقل حرفين للبحث</p>
                                )}
                            </div>

                            {/* نتائج البحث المحسنة */}
                            {searchResults.length > 0 ? (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-xl font-semibold text-gray-800">
                                            📊 نتائج البحث ({searchResults.length}) - ⚡ تحديث فوري متاح
                                        </h3>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6">
                                        {searchResults.map((person) => (
                                            <div key={person.FollowUpID} className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
                                                <div className="flex justify-between items-start mb-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-4 mb-3">
                                                            <h4 className="text-lg font-bold text-gray-900">{person.NameArabic}</h4>
                                                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                                                {person.ListName}
                                                            </span>
                                                            <div className="flex items-center gap-2">
                                                                {getStageIcon(person.CurrentStage)}
                                                                <span className="text-sm text-gray-600 font-medium">{person.CurrentStage}</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm text-gray-600 mb-4">
                                                            <div><strong>الجواز:</strong> {person.PassportNumber}</div>
                                                            <div><strong>الشركة:</strong> {person.CompanyName}</div>
                                                            <div><strong>المندوب:</strong> {person.DelegateName}</div>
                                                            <div>
                                                                <strong>التجنيد:</strong> 
                                                                <span className={`military-status-badge ${getMilitaryStatusColor(person.FinalMilitaryStatus || person.MilitaryStatus)} ml-1`}>
                                                                    {person.FinalMilitaryStatus || person.MilitaryStatus || 'غير محدد'}
                                                                </span>
                                                            </div>
                                                            <div><strong>الهاتف:</strong> {getPhoneNumber(person.PhoneNumbers)}</div>
                                                        </div>
                                                        
                                                        {/* ⚡ شبكة التحديث السريع للبحث */}
                                                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 p-4 bg-gray-50 rounded-xl">
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">🏥 الطبي:</label>
                                                                <EnhancedEditCell
                                                                    person={person}
                                                                    field="MedicalExamDate"
                                                                    currentValue={person.MedicalExamDate ? person.MedicalExamDate.split('T')[0] : ''}
                                                                    type="date"
                                                                />
                                                                <div className="mt-1">
                                                                    <EnhancedEditCell
                                                                        person={person}
                                                                        field="MedicalExamStatus"
                                                                        currentValue={person.MedicalExamStatus || 'قيد التحميل'}
                                                                        type="select"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">👆 البصمة:</label>
                                                                <EnhancedEditCell
                                                                    person={person}
                                                                    field="FingerprintDate"
                                                                    currentValue={person.FingerprintDate ? person.FingerprintDate.split('T')[0] : ''}
                                                                    type="date"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">📄 التأشيرة:</label>
                                                                <EnhancedEditCell
                                                                    person={person}
                                                                    field="VisaDate"
                                                                    currentValue={person.VisaDate ? person.VisaDate.split('T')[0] : ''}
                                                                    type="date"
                                                                />
                                                                <div className="mt-2">
                                                                    <VisaFileUploadCell person={person} />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">✈️ السفر:</label>
                                                           <EnhancedEditCell
    person={person}
    field="TravelDate"
    currentValue={person.TravelDate ? person.TravelDate.slice(0, 16) : ''}
    type="datetime-local"
/>

                                                            </div>


<div>
    <label className="text-xs font-semibold text-gray-600 block mb-1">📋 رقم الطلب:</label>
    <EnhancedEditCell
        person={person}
        field="ApplicationNumber"
        currentValue={person.ApplicationNumber || ''}
        type="text"
    />
</div>
<div>
    <label className="text-xs font-semibold text-gray-600 block mb-1">📱 أرقام الهواتف:</label>
    <PhoneNumbersManager 
        person={person} 
        onUpdate={ultraFastUpdateWithoutRefresh}
    />
</div>


                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">📦 الأوراق:</label>
                                                                <EnhancedEditCell
                                                                    person={person}
                                                                    field="DocumentsDeliveryStatus"
                                                                    currentValue={person.DocumentsDeliveryStatus || 'لم يسلم'}
                                                                    type="select"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">📝 ملاحظات:</label>
                                                                <EnhancedEditCell
                                                                    person={person}
                                                                    field="Notes"
                                                                    currentValue={person.Notes || ''}
                                                                    type="textarea"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 block mb-1">🏛️ دخول القنصلية:</label>
                                                                <EnhancedEditCell
                                                                    person={person}
                                                                    field="ConsulateEntryDate"
                                                                    currentValue={person.ConsulateEntryDate ? person.ConsulateEntryDate.split('T')[0] : ''}
                                                                    type="date"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : searchTerm.length >= 2 ? (
                                <div className="text-center py-16 bg-white rounded-2xl">
                                    <Search className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                                    <h3 className="text-xl font-medium text-gray-900 mb-4">لا توجد نتائج</h3>
                                    <p className="text-gray-600">لم يتم العثور على أي نتائج تطابق "{searchTerm}"</p>
                                </div>
                            ) : (
                                <div className="text-center py-16 bg-white rounded-2xl">
                                    <Search className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                                    <h3 className="text-xl font-medium text-gray-900 mb-4">البحث الذكي المتطور</h3>
                                    <p className="text-gray-600 mb-6">ابحث عن أي شخص في جميع قوائم المتابعة بسرعة البرق</p>
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 max-w-2xl mx-auto border border-blue-200">
                                        <h4 className="font-bold text-blue-900 mb-4 text-lg">🎯 يمكنك البحث بـ:</h4>
                                        <div className="grid grid-cols-2 gap-4 text-blue-800 text-sm">
                                            <div className="flex items-center gap-2">
                                                <User size={16} />
                                                الاسم العربي للشخص
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FileText size={16} />
                                                رقم الجواز
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Activity size={16} />
                                                اسم القائمة
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Package size={16} />
                                                اسم الشركة
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Users size={16} />
                                                اسم المندوب
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Phone size={16} />
                                                رقم الهاتف
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}


{activeTab === 'delegates' && selectedList && (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
    <h2 className="text-2xl font-bold text-gray-900">📤 إرسال كشوف للمناديب</h2>
    <div className="flex items-center gap-4">
        {/* زر تطبيق البيانات المفلترة الجديد */}
        <button
            onClick={() => {
                const filteredData = getFilteredFollowUps();
                fetchDelegatesDataForFiltered(filteredData);
                showAlert(`🔄 تم تطبيق الفلاتر - ${filteredData.length} شخص`, 'success');
            }}
            className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-4 py-2 rounded-xl flex items-center gap-2 transition-all duration-300"
        >
            <Filter size={18} />
            تطبيق البيانات المفلترة ({getFilteredFollowUps().length})
        </button>
        
        {/* الأزرار الموجودة */}
        <button
            onClick={printAllDelegates}
            disabled={loadingDelegatesData || delegatesData.length === 0}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-2 rounded-xl flex items-center gap-2 transition-all duration-300 disabled:opacity-50"
        >
            <Printer size={18} />
            طباعة كل المناديب ({delegatesData.length})
        </button>
        
        <button
            onClick={downloadAllDelegateImages}
            disabled={loadingDelegatesData || delegatesData.length === 0}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-2 rounded-xl flex items-center gap-2 transition-all duration-300 disabled:opacity-50"
        >
            <Download size={18} />
            تنزيل كل الصور ({delegatesData.length})
        </button>
        
        <div className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg">
            📊 البيانات المفلترة: {getFilteredFollowUps().length} شخص من أصل {followUps.length}
        </div>
    </div>
</div>

    
<div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-8 border border-blue-200">
    <h3 className="text-lg font-semibold mb-4 text-gray-800">📝 إعداد الرسالة والموعد</h3>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* إعدادات الموعد */}
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">📅 نوع الموعد:</label>
                <select
                    value={delegateMessage.appointmentType}
                    onChange={(e) => setDelegateMessage(prev => ({ 
                        ...prev, 
                        appointmentType: e.target.value,
                        customMessage: messageTemplates[e.target.value] || messageTemplates.medical
                    }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                    <option value="medical">🏥 الكشف الطبي</option>
                    <option value="documents">📦 تسليم الأوراق</option>
                    <option value="fingerprint">👆 البصمة</option>
                    <option value="visa">📄 التأشيرة</option>
                    <option value="travel">✈️ السفر</option>
                </select>
            </div>

            {/* ✅ خيار إظهار التجنيد للتأشيرة والسفر */}
            {(delegateMessage.appointmentType === 'visa' || delegateMessage.appointmentType === 'travel') && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <label className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            checked={delegateMessage.showMilitary}
                            onChange={(e) => setDelegateMessage(prev => ({ 
                                ...prev, 
                                showMilitary: e.target.checked 
                            }))}
                            className="w-5 h-5"
                        />
                        <span className="text-sm font-medium text-yellow-800">
                            🎖️ إظهار عمود التجنيد في الكشف
                        </span>
                    </label>
                    <p className="text-xs text-yellow-700 mt-1">
                        سيظهر عمود التجنيد بين رقم الهاتف والمحافظة
                    </p>
                </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">📅 تاريخ الموعد:</label>
                    <input
                        type="date"
                        value={delegateMessage.appointmentDate}
                        onChange={(e) => setDelegateMessage(prev => ({ ...prev, appointmentDate: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">🕘 وقت الحضور (اختياري):</label>
                    <input
                        type="time"
                        value={delegateMessage.appointmentTime}
                        onChange={(e) => setDelegateMessage(prev => ({ ...prev, appointmentTime: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
        </div>
        
        {/* الرسالة المخصصة */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">📝 ملاحظات إضافية للمندوب (اختياري):</label>
            <textarea
                value={delegateMessage.customMessage}
                onChange={(e) => setDelegateMessage(prev => ({ ...prev, customMessage: e.target.value }))}
                rows={6}
                placeholder="مثال: يرجى التأكد من حضور العمال في الانتظار لتأكيد حضورهم"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
            />
        </div>
    </div>
</div>

        {/* عرض المناديب والعملاء */}
        {loadingDelegatesData ? (
            <div className="text-center py-16">
                <RefreshCw className="animate-spin mx-auto h-16 w-16 text-blue-600 mb-6" />
                <h3 className="text-xl font-medium text-gray-900 mb-4">جاري تحميل بيانات المناديب للبيانات المفلترة...</h3>
            </div>
        ) : delegatesData.length > 0 ? (
            <div className="space-y-6">
                {delegatesData.map((delegateGroup, index) => {
                    const allClients = delegateGroup.clients || [];
                    
                    if (allClients.length === 0) return null;
                    
                    return (
                        <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                            {/* معلومات المندوب */}
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h4 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                                        👤 {delegateGroup.delegateInfo?.DelegateName || 'مندوب غير معروف'}
                                    </h4>
                                    <p className="text-gray-600 mt-1">
                                        📊 إجمالي العملاء: {allClients.length} شخص
                                    </p>
                                    <div className="mt-2 text-sm text-blue-600">
                                        📋 {selectedList?.ListName} • 📅 {delegateMessage.appointmentDate ? 
                                            new Date(delegateMessage.appointmentDate).toLocaleDateString('ar-EG') : 'لم يحدد تاريخ'}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-3xl font-bold text-blue-600">{allClients.length}</div>
                                    <div className="text-sm text-gray-500">عميل</div>
                                </div>
                            </div>
                            
<div className="bg-gray-50 rounded-xl p-4 mb-6">
    <h5 className="font-medium text-gray-700 mb-3">👥 معاينة العملاء:</h5>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
        {allClients.slice(0, 9).map((client, idx) => (
            <div key={idx} className="flex justify-between items-center bg-white p-2 rounded-lg text-sm">
                <div className="flex flex-col">
                    <span className="font-medium">{client.NameArabic}</span>
                    <span className="text-blue-600 text-xs">🗺️ {client.Governorate || 'غير محدد'}</span>
                </div>
                <span className="text-gray-600 text-xs">{client.CompanyName}</span>
            </div>
        ))}
        {allClients.length > 9 && (
            <div className="col-span-full text-center text-gray-500 text-sm bg-white p-2 rounded-lg">
                و {allClients.length - 9} عميل آخر...
            </div>
        )}
    </div>
</div>
                            
                            {/* أزرار الإجراءات */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <button
                                    onClick={() => printDelegateReport(delegateGroup.delegateInfo, allClients)}
                                    className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
                                >
                                    <Printer size={18} />
                                    طباعة الكشف
                                </button>
                                
                                <button
                                    onClick={() => generateDelegateImageForWhatsApp(delegateGroup.delegateInfo, allClients)}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
                                >
                                    <Camera size={18} />
                                    صورة واتساب
                                </button>
                                
<button
    onClick={() => {
        const appointmentText = delegateMessage.appointmentDate ? 
            `📅 موعد الحضور: ${new Date(delegateMessage.appointmentDate).toLocaleDateString('ar-EG')} الساعة ${delegateMessage.appointmentTime}` : '';
        
        const message = `📋 *كشف المندوب*\n\n👤 *المندوب:* ${delegateGroup.delegateInfo?.DelegateName}\n📋 *النوع:* ${
            delegateMessage.appointmentType === 'medical' ? '🏥 الكشف الطبي' : 
            delegateMessage.appointmentType === 'fingerprint' ? '👆 البصمة' :
            delegateMessage.appointmentType === 'visa' ? '📄 التأشيرة' :
            delegateMessage.appointmentType === 'travel' ? '✈️ السفر' :
            delegateMessage.appointmentType === 'documents' ? '📦 استلام الأوراق' : '📋 عام'
        }\n👥 *العدد:* ${allClients.length} شخص\n${appointmentText}\n\n${delegateMessage.customMessage ? `📝 ${delegateMessage.customMessage}\n\n` : ''}${allClients.slice(0, 8).map((c, i) => {
    const phones = getAllPhoneNumbers(c.PhoneNumbers);
    const phoneText = phones.length > 0 ? phones.join(' | ') : 'لا يوجد هاتف';
    return `${i+1}. ${c.NameArabic} - ${c.Governorate || 'غير محدد'} - ${c.CompanyName} - ${phoneText}${c.Notes ? ` - 📝 ${c.Notes}` : ''}`;
}).join('\n')}${allClients.length > 8 ? `\n\nو ${allClients.length - 8} آخرين...` : ''}`;
        
        navigator.clipboard.writeText(message);
        showAlert('✅ تم نسخ الرسالة للحافظة', 'success');
    }}
    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
>
    <FileText size={18} />
    نسخ كنص
</button>
                                
                                <button
                                    onClick={() => {
                                        console.log('عرض تفاصيل المندوب:', delegateGroup);
                                        showAlert(`📊 ${delegateGroup.delegateInfo?.DelegateName}: ${allClients.length} عميل`, 'info');
                                    }}
                                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
                                >
                                    <Eye size={18} />
                                    التفاصيل
                                </button>
                            </div>
                        </div>
                    );
                }).filter(Boolean)}
            </div>
        ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
                <Users className="mx-auto h-20 w-20 text-gray-400 mb-6" />
                <h3 className="text-xl font-medium text-gray-900 mb-4">لا توجد بيانات مناديب</h3>
                <p className="text-gray-600">تأكد من وجود مناديب في هذه القائمة أو قم بتطبيق فلاتر أخرى</p>
                <button
                    onClick={() => {
                        const filteredData = getFilteredFollowUps();
                        fetchDelegatesDataForFiltered(filteredData);
                    }}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
                >
                    إعادة تحميل البيانات
                </button>
            </div>
        )}
    </div>
)}


                </div>
            </div>


{/* Modal خاص بتصدير الصورة */}
{/* Modal خاص بتصدير الصورة */}
{/* Modal خاص بتصدير الصورة */}
{showImageExportModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white w-full max-w-5xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          🖼 خيارات تصدير كشف المتابعة الدورية كصورة
        </h2>
        <button
          onClick={() => setShowImageExportModal(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>
      </div>

      {/* ─── إعدادات الصورة المتقدمة (مضغوطة) ─── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
        <h3 className="text-sm font-bold text-slate-700 mb-3">⚙️ إعدادات الصورة</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">

          {/* إظهار الجزء العلوي */}
          <label className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 cursor-pointer hover:border-blue-400 transition-colors">
            <input
              type="checkbox"
              className="w-4 h-4 accent-blue-600"
              checked={imageExportAdvanced.showHeader}
              onChange={(e) => setImageExportAdvanced(prev => ({ ...prev, showHeader: e.target.checked }))}
            />
            <div>
              <div className="text-xs font-semibold text-gray-700">الجزء العلوي</div>
              <div className="text-xs text-gray-400">إجمالي + الصفحة</div>
            </div>
          </label>

          {/* إظهار الجزء السفلي */}
          <label className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 cursor-pointer hover:border-blue-400 transition-colors">
            <input
              type="checkbox"
              className="w-4 h-4 accent-blue-600"
              checked={imageExportAdvanced.showFooter}
              onChange={(e) => setImageExportAdvanced(prev => ({ ...prev, showFooter: e.target.checked }))}
            />
            <div>
              <div className="text-xs font-semibold text-gray-700">الجزء السفلي</div>
              <div className="text-xs text-gray-400">نظام المتابعة</div>
            </div>
          </label>

          {/* عدد الصفوف */}
          <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
            <div className="text-xs font-semibold text-gray-700 mb-1">🔢 صفوف في كل صورة</div>
            <input
              type="number"
              min="5"
              max="200"
              value={imageExportAdvanced.maxRowsPerImage}
              onChange={(e) => setImageExportAdvanced(prev => ({ ...prev, maxRowsPerImage: parseInt(e.target.value) || 25 }))}
              className="w-full border border-gray-200 rounded px-2 py-1 text-center text-sm font-bold focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* العنوان المخصص */}
          <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
            <div className="text-xs font-semibold text-gray-700 mb-1">✏️ عنوان مخصص</div>
            <input
              type="text"
              placeholder='مثال: كشف رقم 5'
              value={imageExportAdvanced.customTitle}
              onChange={(e) => setImageExportAdvanced(prev => ({ ...prev, customTitle: e.target.value }))}
              className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
              dir="rtl"
            />
          </div>

        </div>

        {/* اختيار اللون — شبكة مضغوطة */}
        <div>
          <div className="text-xs font-semibold text-gray-600 mb-2">🎨 لون الصورة</div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {Object.entries(colorThemes).map(([key, thm]) => (
              <button
                key={key}
                onClick={() => setImageExportAdvanced(prev => ({ ...prev, colorTheme: key }))}
                title={thm.label}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                  imageExportAdvanced.colorTheme === key
                    ? 'border-gray-800 shadow-md scale-105'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                style={{
                  background: imageExportAdvanced.colorTheme === key ? thm.cardBg : 'white',
                }}
              >
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0 border border-white shadow-sm"
                  style={{ background: thm.dot }}
                />
                <span className="text-gray-700 truncate">{thm.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* نفس تفريده الأعمدة الموجودة في نافذة الطباعة تمامًا */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* الأعمدة الأساسية */}
        <div>
          <h3 className="text-lg font-semibold mb-3">📋 الأعمدة الأساسية</h3>
          <div className="space-y-2">
            {[
              { key: 'serialNumber', label: '#️⃣ م' },
              { key: 'name', label: '👤 الاسم' },
              { key: 'company', label: '🏢 الشركة + رقم الملف' },
              { key: 'profession', label: '💼 المهنة' },
              { key: 'passport', label: '📘 الجواز' },
              { key: 'phone', label: '📱 الهاتف' },
              { key: 'delegate', label: '👨‍💼 المندوب' },
              { key: 'military', label: '🎖️ التجنيد' },
              { key: 'netDate', label: '📅 تاريخ النت' },
              { key: 'listName', label: '📋 اسم القائمة الأصلية' },
               { key: 'governorate', label: '📍 المحافظة' },
            ].map(field => (
              <label
                key={field.key}
                className="flex items-center gap-3 p-2 bg-gray-50 rounded hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={!!printOptions.includeFields[field.key]}
                  onChange={(e) =>
                    setPrintOptions(prev => ({
                      ...prev,
                      includeFields: {
                        ...prev.includeFields,
                        [field.key]: e.target.checked
                      }
                    }))
                  }
                />
                <span className="text-sm">{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* الأعمدة الإجرائية */}
        <div>
          <h3 className="text-lg font-semibold mb-3">⚙️ الأعمدة الإجرائية</h3>
          <div className="space-y-2">
            {[
              { key: 'medical', label: '🏥 الكشف الطبي' },
              { key: 'fingerprint', label: '👆 البصمة' },
              { key: 'consulate', label: '🏛️ دخول القنصلية' },
              { key: 'visa', label: '📄 التأشيرة' },
              { key: 'travel', label: '✈️ السفر' },
              { key: 'documents', label: '📦 الأوراق' },
              { key: 'stage', label: '📊 المرحلة' },
              { key: 'notes', label: '📝 ملاحظات' }
            ].map(field => (
              <label
                key={field.key}
                className="flex items-center gap-3 p-2 bg-gray-50 rounded hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={!!printOptions.includeFields[field.key]}
                  onChange={(e) =>
                    setPrintOptions(prev => ({
                      ...prev,
                      includeFields: {
                        ...prev.includeFields,
                        [field.key]: e.target.checked
                      }
                    }))
                  }
                />
                <span className="text-sm">{field.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* إعدادات سريعة – نفس منطق نافذة الطباعة */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h4 className="font-semibold mb-3">⚡ إعدادات سريعة:</h4>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() =>
              setPrintOptions(prev => ({
                ...prev,
                includeFields: {
                  // نفس الافتراضى المستخدم للطباعة
                  serialNumber: true,
                  name: true,
                  company: true,
                  profession: true,
                  passport: false,
                  phone: true,
                  delegate: true,
                  military: true,
                  netDate: false,
                  listName: true,
                  medical: true,
                  fingerprint: false,
                  visa: false,
                  travel: true,
                  documents: true,
                  stage: false,
                  notes: true
                }
              }))
            }
            className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
          >
            الافتراضي المطلوب
          </button>

          <button
            onClick={() =>
              setPrintOptions(prev => ({
                ...prev,
                includeFields: Object.fromEntries(
                  Object.keys(prev.includeFields).map(key => [key, true])
                )
              }))
            }
            className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
          >
            تحديد الكل
          </button>

          <button
            onClick={() =>
              setPrintOptions(prev => ({
                ...prev,
                includeFields: Object.fromEntries(
                  Object.keys(prev.includeFields).map(key => [key, false])
                )
              }))
            }
            className="px-3 py-1 bg-red-100 text-red-800 rounded text-sm hover:bg-red-200"
          >
            إلغاء الكل
          </button>
        </div>
      </div>

      {/* أزرار التحكم */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => setShowImageExportModal(false)}
          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
        >
          إلغاء
        </button>
        <button
          onClick={async () => {
            await savePeriodicReportAsImages();
            setShowImageExportModal(false);
          }}
          className="px-6 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-xl flex items-center gap-2"
        >
          {/* لو فوق مستوردها كـ ImageIcon استخدم <ImageIcon size={18} /> */}
          <ImageIcon size={18} />
          تصدير كصورة
        </button>
      </div>
    </div>
  </div>
)}




{/* نافذة فلترة الطباعة والـ PDF */}
{showPrintModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white w-full max-w-5xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">🖨️ خيارات الطباعة والـ PDF</h2>
        <button
          onClick={() => setShowPrintModal(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
      {/* الأعمدة الأساسية */}
        <div>
          <h3 className="text-lg font-semibold mb-3">📋 الأعمدة الأساسية</h3>
          <div className="space-y-2">
            {[
              { key: 'serialNumber', label: '#️⃣ م' },
              { key: 'name', label: '👤 الاسم' },
              { key: 'company', label: '🏢 الشركة + رقم الملف' },
              { key: 'profession', label: '💼 المهنة' },
              { key: 'passport', label: '📘 الجواز' },
              { key: 'phone', label: '📱 الهاتف' },
              { key: 'delegate', label: '👨‍💼 المندوب' },
              { key: 'military', label: '🎖️ التجنيد' },
                { key: 'netDate', label: '📅 تاريخ النت' },
  { key: 'listName', label: '📋 اسم القائمة الأصلية' } ,
              
  { key: 'governorate', label: '📍 المحافظة' },
            ].map(field => (
              <label key={field.key} className="flex items-center gap-3 p-2 bg-gray-50 rounded hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={printOptions.includeFields[field.key]}
                  onChange={(e) => setPrintOptions(prev => ({
                    ...prev,
                    includeFields: { ...prev.includeFields, [field.key]: e.target.checked }
                  }))}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* الأعمدة الإجرائية */}
        <div>
          <h3 className="text-lg font-semibold mb-3">⚙️ الأعمدة الإجرائية</h3>
          <div className="space-y-2">
            {[
              { key: 'medical', label: '🏥 الكشف الطبي' },
              { key: 'fingerprint', label: '👆 البصمة' },
              { key: 'consulate', label: '🏛️ دخول القنصلية' },
              { key: 'visa', label: '📄 التأشيرة' },
              { key: 'travel', label: '✈️ السفر' },
              { key: 'documents', label: '📦 الأوراق' },
              { key: 'stage', label: '📊 المرحلة' },
              { key: 'notes', label: '📝 ملاحظات' }
            ].map(field => (
              <label key={field.key} className="flex items-center gap-3 p-2 bg-gray-50 rounded hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={printOptions.includeFields[field.key]}
                  onChange={(e) => setPrintOptions(prev => ({
                    ...prev,
                    includeFields: { ...prev.includeFields, [field.key]: e.target.checked }
                  }))}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* فلاتر التواريخ */}
        <div>
          <h3 className="text-lg font-semibold mb-3">📅 فلتر التواريخ</h3>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {[
              { key: 'netDate', label: '📅 تاريخ النت' },
              { key: 'medicalDate', label: '🏥 تاريخ الكشف الطبي' },
              { key: 'fingerprintDate', label: '👆 تاريخ البصمة' },
              { key: 'consulateDate', label: '🏛️ تاريخ دخول القنصلية' },
              { key: 'visaDate', label: '📄 تاريخ التأشيرة' },
              { key: 'travelDate', label: '✈️ تاريخ السفر' }
            ].map(dateFilter => (
              <div key={dateFilter.key} className="bg-gray-50 p-3 rounded">
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={printOptions.dateFilters[dateFilter.key].enabled}
                    onChange={(e) => setPrintOptions(prev => ({
                      ...prev,
                      dateFilters: {
                        ...prev.dateFilters,
                        [dateFilter.key]: {
                          ...prev.dateFilters[dateFilter.key],
                          enabled: e.target.checked
                        }
                      }
                    }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">{dateFilter.label}</span>
                </label>
                
                {printOptions.dateFilters[dateFilter.key].enabled && (
                  <div className="space-y-2 mt-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">من تاريخ:</label>
                      <input
                        type="date"
                        value={printOptions.dateFilters[dateFilter.key].startDate}
                        onChange={(e) => setPrintOptions(prev => ({
                          ...prev,
                          dateFilters: {
                            ...prev.dateFilters,
                            [dateFilter.key]: {
                              ...prev.dateFilters[dateFilter.key],
                              startDate: e.target.value
                            }
                          }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">إلى تاريخ:</label>
                      <input
                        type="date"
                        value={printOptions.dateFilters[dateFilter.key].endDate}
                        onChange={(e) => setPrintOptions(prev => ({
                          ...prev,
                          dateFilters: {
                            ...prev.dateFilters,
                            [dateFilter.key]: {
                              ...prev.dateFilters[dateFilter.key],
                              endDate: e.target.value
                            }
                          }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>


        {/* فلاتر البيانات */}
        <div>
          <h3 className="text-lg font-semibold mb-3">🔍 فلتر البيانات</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {[
              { key: 'medicalPending', label: '⏳ لم يعمل كشف طبي', count: listStatistics.MedicalPending },
              { key: 'medicalPassed', label: '✅ كشف طبي لائق', count: listStatistics.MedicalPassed },
              { key: 'medicalFailed', label: '❌ كشف طبي غير لائق', count: listStatistics.MedicalFailed },
              { key: 'fingerprintPending', label: '⏳ لم يعمل بصمة', count: listStatistics.FingerprintPending },
              { key: 'fingerprintDone', label: '👆 تم عمل البصمة', count: listStatistics.FingerprintDone },
              { key: 'visaPending', label: '⏳ لم تصدر التأشيرة', count: listStatistics.VisaPending },
              { key: 'visaDone', label: '📄 صدرت التأشيرة', count: listStatistics.VisaIssued },
              { key: 'travelReady', label: '✈️ جاهز للسفر', count: listStatistics.TravelReady },
              { key: 'traveled', label: '🌍 مسافر', count: listStatistics.Traveled },
              { key: 'documentsNotDelivered', label: '📦 لم يسلم أوراق', count: listStatistics.DocumentsPending },
              { key: 'documentsDelivered', label: '✅ سلم الأوراق', count: listStatistics.DocumentsDelivered }
            ].map(filter => (
              <label key={filter.key} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                <input
                  type="checkbox"
                  checked={printOptions.filters[filter.key]}
                  onChange={(e) => setPrintOptions(prev => ({
                    ...prev,
                    filters: { ...prev.filters, [filter.key]: e.target.checked }
                  }))}
                  className="w-3 h-3"
                />
                <span className="flex-1">{filter.label}</span>
                <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">
                  {filter.count || 0}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* أزرار سريعة */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h4 className="font-semibold mb-3">⚡ إعدادات سريعة:</h4>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setPrintOptions(prev => ({
              ...prev,
              includeFields: {
                serialNumber: true,
                name: true,
                company: true,
                profession: true,
                passport: false,
                phone: true,
                delegate: true,
                military: false,
                netDate: false,
                medical: false,
                fingerprint: false,
                visa: false,
                travel: false,
                documents: false,
                stage: false,
                notes: false
              }
            }))}
            className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
          >
            الافتراضي المطلوب
          </button>
          <button
            onClick={() => setPrintOptions(prev => ({
              ...prev,
              includeFields: Object.fromEntries(Object.keys(prev.includeFields).map(key => [key, true]))
            }))}
            className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
          >
            تحديد الكل
          </button>
          <button
            onClick={() => setPrintOptions(prev => ({
              ...prev,
              includeFields: Object.fromEntries(Object.keys(prev.includeFields).map(key => [key, false]))
            }))}
            className="px-3 py-1 bg-red-100 text-red-800 rounded text-sm hover:bg-red-200"
          >
            إلغاء الكل
          </button>

          <button
            onClick={() => setPrintOptions(prev => ({
              ...prev,
              dateFilters: Object.fromEntries(Object.keys(prev.dateFilters).map(key => [key, {
                enabled: false,
                startDate: '',
                endDate: ''
              }]))
            }))}
            className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm hover:bg-yellow-200"
          >
            مسح فلاتر التواريخ
          </button>


        </div>
      </div>

      {/* أزرار التحكم */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => setShowPrintModal(false)}
          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
        >
          إلغاء
        </button>
        <button
          onClick={() => {
            printReport();
            setShowPrintModal(false);
          }}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2"
        >
          <Printer size={20} />
          طباعة
        </button>
        <button
          onClick={() => {
            exportToPDF();
            setShowPrintModal(false);
          }}
          className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 flex items-center gap-2"
        >
          <FileText size={20} />
          PDF
        </button>
      </div>
    </div>
  </div>
)}

{showBulkModal && <BulkUpdateModal />}


{/* ✅ نافذة فلاتر التواريخ المنبثقة */}
{showDateFilters && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 pfu-modal-overlay">
        <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl p-6 m-4 max-h-[80vh] overflow-y-auto pfu-date-modal-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    📅 فلاتر التواريخ
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                        المطبقة: {Object.values(dateFilters).filter(filter => filter.enabled).length}
                    </span>
                </h2>
                <button
                    onClick={() => setShowDateFilters(false)}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <X size={24} />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pfu-date-grid">
                {[
                    { key: 'netDate', label: '📅 تاريخ النت', color: 'blue' },
                    { key: 'medicalDate', label: '🏥 تاريخ الكشف الطبي', color: 'green' },
                    { key: 'fingerprintDate', label: '👆 تاريخ البصمة', color: 'purple' },
                    { key: 'consulateDate', label: '🏛️ تاريخ دخول القنصلية', color: 'teal' },
                    { key: 'visaDate', label: '📄 تاريخ التأشيرة', color: 'indigo' },
                 { key: 'travelDate', label: '✈️ تاريخ السفر', color: 'emerald' },
                    { key: 'insuranceDate', label: '🏥 تاريخ التأمين الطبي', color: 'green' }
                ].map(dateFilter => (
                    <div key={dateFilter.key} className={`pfu-date-filter-card bg-gray-50 p-4 rounded-lg border-2 ${
                        dateFilters[dateFilter.key].enabled ? `border-${dateFilter.color}-400 bg-${dateFilter.color}-50` : 'border-gray-200'
                    }`}>
                        <label className="flex items-center gap-3 mb-3">
                            <input
                                type="checkbox"
                                checked={dateFilters[dateFilter.key].enabled}
                                onChange={(e) => setDateFilters(prev => ({
                                    ...prev,
                                    [dateFilter.key]: {
                                        ...prev[dateFilter.key],
                                        enabled: e.target.checked
                                    }
                                }))}
                                className="w-5 h-5"
                            />
                            <span className="text-lg font-medium">{dateFilter.label}</span>
                        </label>
                        
                        {dateFilters[dateFilter.key].enabled && (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1 font-medium">من تاريخ:</label>
                                    <input
                                        type="date"
                                        value={dateFilters[dateFilter.key].startDate}
                                        onChange={(e) => setDateFilters(prev => ({
                                            ...prev,
                                            [dateFilter.key]: {
                                                ...prev[dateFilter.key],
                                                startDate: e.target.value
                                            }
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1 font-medium">إلى تاريخ:</label>
                                    <input
                                        type="date"
                                        value={dateFilters[dateFilter.key].endDate}
                                        onChange={(e) => setDateFilters(prev => ({
                                            ...prev,
                                            [dateFilter.key]: {
                                                ...prev[dateFilter.key],
                                                endDate: e.target.value
                                            }
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>



                        )}
                                {/* ⏰ اختيار رحلة (وقت السفر) خاص بفلتر تاريخ السفر فقط */}
        {dateFilter.key === 'travelDate' && dateFilters.travelDate.enabled && (
            <div className="mt-3">
                <label className="block text-sm text-gray-700 mb-1 font-medium">
                    اختيار رحلة (وقت السفر فى هذا اليوم):
                </label>
                <select
                    value={selectedTravelTime}
                    onChange={(e) => setSelectedTravelTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                >
                    <option value="all">كل الرحلات فى اليوم المختار</option>
                    {getAvailableTravelTimesForSelectedDate().map(time => (
                        <option key={time} value={time}>
                            {time}
                        </option>
                    ))}
                </select>
            </div>
        )}

                    </div>
                ))}
            </div>

            {/* عداد النتائج وأزرار التحكم */}
            <div className="mt-6 flex justify-between items-center pfu-date-modal-footer">
                <div className="text-center">
                    <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-lg font-medium">
                        📊 النتائج المفلترة: {getFilteredFollowUps().length} شخص
                    </span>
                </div>
                
                <div className="flex gap-3">
                    <button
                    onClick={() => setDateFilters({
                            netDate: { enabled: false, startDate: '', endDate: '' },
                            medicalDate: { enabled: false, startDate: '', endDate: '' },
                            fingerprintDate: { enabled: false, startDate: '', endDate: '' },
                            consulateDate: { enabled: false, startDate: '', endDate: '' },
                            visaDate: { enabled: false, startDate: '', endDate: '' },
                            travelDate: { enabled: false, startDate: '', endDate: '' },
                            insuranceDate: { enabled: false, startDate: '', endDate: '' }
                        })}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                    >
                        مسح جميع الفلاتر
                    </button>
                    
                    <button
                        onClick={() => setShowDateFilters(false)}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        تطبيق الفلاتر
                    </button>
                </div>
            </div>
        </div>
    </div>
)}


<PeriodicApplicationBarcodeModal
    isOpen={showApplicationBarcodeModal}
    onClose={() => setShowApplicationBarcodeModal(false)}
    person={applicationBarcodePerson}
    showAlert={showAlert}
    canSaveNamedProfile={canSaveNamedApplicationBarcodeProfile}
    canDeleteNamedProfile={canDeleteApplicationBarcodeProfile}
    canSaveGlobalSettings={canSaveApplicationBarcodeSettingsGlobally}
/>


{/* نافذة معاينة الصورة للواتساب */}
{showImagePreview && previewImageData && (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-green-600 text-white p-4 flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold">📱 معاينة صورة الواتساب</h3>
                    <p className="text-green-100">المندوب: {previewImageData.delegateName} - {previewImageData.clientCount} عميل</p>
                </div>
                <button
                    onClick={() => {
                        setShowImagePreview(false);
                        URL.revokeObjectURL(previewImageData.url);
                        setPreviewImageData(null);
                    }}
                    className="text-white hover:bg-green-700 p-2 rounded-full"
                >
                    <X size={24} />
                </button>
            </div>
            
            <div className="p-4">
                <div className="mb-4 max-h-96 overflow-auto border border-gray-300 rounded-lg">
                    <img 
                        src={previewImageData.url} 
                        alt="معاينة قائمة العملاء"
                        className="w-full h-auto"
                    />
                </div>
                
                <div className="flex gap-3">
                    <button
                        onClick={async () => {
                            try {
                                if (navigator.clipboard && navigator.clipboard.write) {
                                    const item = new ClipboardItem({ 'image/png': previewImageData.blob });
                                    await navigator.clipboard.write([item]);
                                    showAlert('✅ تم نسخ الصورة! يمكنك الآن لصقها في الواتساب', 'success');
                                } else {
                                    throw new Error('المتصفح لا يدعم نسخ الصور');
                                }
                            } catch (error) {
                                console.error('خطأ في نسخ الصورة:', error);
                                showAlert('تعذر نسخ الصورة تلقائياً. جرب النسخ اليدوي بالضغط طويل على الصورة', 'warning');
                            }
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 font-medium"
                    >
                        📋 نسخ للواتساب
                    </button>
                    
                    <button
                        onClick={() => {
                            const link = document.createElement('a');
                            link.href = previewImageData.url;
                            link.download = `قائمة_${previewImageData.delegateName}_${new Date().toISOString().split('T')[0]}.png`;
                            link.click();
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 font-medium"
                    >
                        💾 تحميل الصورة
                    </button>
                    
                    <button
                        onClick={() => {
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(`
                                <html>
                                    <head><title>طباعة قائمة ${previewImageData.delegateName}</title></head>
                                    <body style="margin: 0; padding: 20px;">
                                        <img src="${previewImageData.url}" style="width: 100%; height: auto;" />
                                    </body>
                                </html>
                            `);
                            printWindow.document.close();
                            printWindow.onload = () => {
                                printWindow.print();
                                printWindow.close();
                            };
                        }}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                        <Printer size={18} />
                    </button>
                </div>
                
                <div className="mt-3 p-3 bg-green-50 rounded-lg text-sm text-green-800">
                    💡 <strong>نصيحة:</strong> اضغط "نسخ للواتساب" ثم اذهب لجروب الواتساب والصق الصورة مباشرة (Ctrl+V)
                </div>
            </div>
        </div>
    </div>
)}


        </div>
    );
};

export default PeriodicFollowUp;